import {
  DOOM_CLOCK_CHAIN_BONUS_SEC,
  DOOM_CLOCK_DRAIN_PER_SEC,
  DOOM_CLOCK_GROOVE_CHARGE_BASELINE_SEC,
  DOOM_CLOCK_MATCH_REWARD_SEC,
  DOOM_CLOCK_POWERUP_SWEEP_BONUS_SEC,
  KEY_EARN_STEP,
  MAX_LEVEL,
  START_ROTATION_KEYS
} from "./constants";
import {
  createBoardForLevel,
  injectObstaclesForLevel,
  rotateBoard,
  swapCells,
  canSwap,
  deserializeBoard,
  serializeBoard
} from "./board";
import { advanceBeat, createBeatState, setRotationInterval } from "./beatClock";
import { doomClockCapForLevel, getLevelConfig, versusScoreTargetForLevel } from "./levelConfig";
import { cloneBoard } from "./grid";
import { hasAnyMatch } from "./match";
import {
  DEAD_BOARD_RESHUFFLE_STEP_SEC,
  findHintCells,
  guaranteedReshuffle,
  hasValidMove,
  planDeadBoardReshuffle
} from "./noMoves";
import { Rng, seedFromTime } from "./rng";
import { getSettingsPageCount } from "./settingsPages";
import {
  resolveCascades,
  resolveBombDetonationInPlace,
  resolvePostRotationSettle,
  resolvePowerupColCut,
  resolvePowerupRowRip,
  resolvePowerupTransmute,
  resolvePowerupZigzag,
  resolveSwapByPriorityStages,
  resolveWipeDetonationInPlace
} from "./resolve";
import {
  LEVEL20_BOUNTY_POOL,
  createLevel20BountyState,
  createObjectiveStateForLevel,
  emptyObjectiveTelemetry,
  normalizeObjectiveState,
  objectiveLabel,
  objectiveProgressFromTelemetry,
  objectiveRewardForLevel,
  objectiveRewardSummary,
  level20BountyDefinition
} from "./progression";
import {
  countOccupiedCells,
  createColorHuntStageGoal,
  createSoloStageGoal,
  emptySoloStageTelemetry,
  hasSoloStageTelemetryValue,
  isSoloModeUnlocked,
  levelConfigForSoloMode,
  mergeSoloStageTelemetry,
  normalizeSoloStageGoal,
  soloModeAnimationDurationMultiplier,
  soloModeBoardSize,
  soloModeDoomPressureMultiplier,
  soloModeGrooveChargeMultiplier,
  soloModeLabel,
  soloModeTutorialEligible,
  soloModeUnlockLevel,
  soloModeUsesClassicProgression,
  SOLO_SELECT_MODES,
  SOLO_MODES
} from "./soloModes";
import { activationFromSwap, isBomb, isWipe } from "./specials";
import { AudioManager, type MusicVisualFrame } from "./audio";
import type { SfxName } from "./sfx";
import { GROOVE_MODE_DURATION_SEC, GROOVE_MODE_MULTIPLIER } from "./scoring";
import { POWERUP_COSTS } from "./powerupConfig";
import { createPowerupRadialItems, createTrapRadialItems, resolveHighlightedIndex } from "./radialMenu";
import { fetchLeaderboard, fetchPersonalBests, fetchScoreRank, submitScore } from "../leaderboard/supabase";
import type { PersonalBests } from "../leaderboard/supabase";
import { clearRunState, hasTutorialBeenSeen, loadRunStateContext, loadSoloContinueAvailability, markTutorialSeen, saveRunState, resetOnboardingSeen, markOnboardingSeen } from "../persistence/save";
import { saveRunStateCloud, loadRunStateCloud, clearRunStateCloud } from "../persistence/cloudSave";
import type { AuthManager, AuthState } from "../auth/authManager";
import { createTutorialState, isTutorialComplete, notifyTutorialSwap, notifyTutorialAction, notifyTutorialRadialHold, updateTutorial, TUTORIAL_BOARD, type TutorialState } from "./tutorial";
import {
  type OnboardingState,
  createOnboardingState,
  getCurrentStep,
  advanceOnboardingStep,
  updateCallShake
} from "./onboarding";
import { clamp01, easeInOutSine } from "../shared/easing";
import { FX_TILE_RGB, STATE_MACHINE_TUNING } from "./tuning";
import {
  BG_BLOOM_OPTIONS,
  BG_GLOW_OPTIONS,
  BG_MIX_OPTIONS,
  BG_MOTION_OPTIONS,
  BG_SATURATION_OPTIONS,
  BG_STYLE_OPTIONS,
  DEBUG_DIAGNOSTICS_OPTIONS,
  DEBUG_VERBOSITY_OPTIONS,
  DEFAULT_PERFORMANCE_SETTINGS,
  FPS_CAP_OPTIONS,
  OPPONENT_RENDER_OPTIONS,
  PARTICLE_OPTIONS,
  PERFORMANCE_PRESET_ORDER,
  SIDE_FX_DENSITY_OPTIONS,
  SIDE_FX_OPTIONS,
  applyPerformancePreset,
  clonePerformanceSettings,
  cycleOption,
  normalizePerformanceSettings
} from "./performanceSettings";
import { resolveVersusFeedbackPolicy, type VersusFeedbackMode } from "./versusFeedbackPolicy";
import type { DragState, GameInput, UiAction } from "./input";
import { prefersReducedMotion } from "./platform";
import { CHARACTER_CATALOG, getDailyShopSkins, getCharacterById, getSkinBgColor, type CharacterDef } from "../economy/characters";
import { getCachedOwnedIds } from "../economy/inventoryManager";
import type {
  VersusLobbyRenderData,
  VersusCountdownData,
  VersusMatchRenderData,
  VersusResultData
} from "../versus/types";
import type { BotDecisionDiagnostics } from "../versus/botInputAdapter";
import {
  loadLeaderboardTabPreference,
  saveLeaderboardTabPreference,
  type LeaderboardTab,
  type RankedLeaderboardEntry,
  type RankedRankContext
} from "../versus/ranked";
import { featureUnlockLevel, isFeatureUnlocked, levelFromTotalXp, prestigeStarsForLevel, type UnlockableFeature } from "../economy/levelSystem";
import type { DailyLoginBonusStatus } from "../economy/dailyLoginRewards";
import type { AvatarDisplay } from "../wallet/avatarResolver";
import { hapticManager, type HapticEvent } from "./haptics";
import { networkManager } from "./network";
import { tokenShopService } from "../wallet/tokenShopService";
import { staminaService } from "../stamina/staminaService";
import type { PendingGameplayIntent, PremiumSource } from "../stamina/staminaTypes";
import { buildGameplayDebugSnapshot } from "./debugSnapshot";
import { enforceDebugAccess, normalizeDebugSettings, type DebugSettings } from "./debugSettings";
import { computeProfileTotalPages, computeProfileVisibleItemCount } from "./profilePagination";
import type { AdminDebugAccess } from "../auth/adminDebugAccess";
import {
  cashForScoreEvent,
  clampDoomClockSecForLevel,
  collapseDurationForStep,
  completionCashForPowerup,
  hasObjectiveTelemetryValue,
  interruptedLinearPowerupCompletion,
  mergeObjectiveTelemetry,
  mergeUniquePositions,
  normalizeGrooveState,
  normalizeLevel20BountyCompletions,
  normalizeLevelAdvanceBaseScore,
  normalizeObjectiveStateForLevel,
  normalizePowerupInventory,
  phaseDuration,
  resolveBatchPoints,
  strongerBomb,
  strongestBombSeedHapticType,
  toBombHapticType,
  toBombHapticTypeFromSpecial,
  type PendingScoreBatch,
  type QueuedScoreBatchContext,
} from "./stateMachineHelpers";
import type {
  BoardState,
  CollapseAnimState,
  CollapseAnimStep,
  GamePhase,
  IapPackRenderData,
  IapPurchaseState,
  LeaderboardEntry,
  LevelObjectiveKind,
  LevelObjectiveState,
  LevelConfig,
  MoveAttempt,
  ObjectiveReward,
  ObjectiveTelemetry,
  PerformancePreset,
  PerformanceSettings,
  PowerupKind,
  Position,
  ResolveResult,
  ResolveScoreEvent,
  RotationAnimState,
  RotationDir,
  RunState,
  SoloMode,
  SoloStageGoal,
  SoloStageTelemetry,
  SwapAnimState
} from "./types";

const HALF_PI = Math.PI / 2;
const GROOVE_IDLE_DECAY_PER_SEC = 0.025;
const GROOVE_READY_WINDOW_SEC = 3;
const GROOVE_CHARGE_DIVISOR = 4.8;
const GROOVE_DEBUG_ENABLED = typeof import.meta !== "undefined" && import.meta.env.DEV;
const LEVEL20_REWARD_SCORE_STEP = 25000;
const COLOR_HUNT_LEVEL20_SERIES_BONUS = 10000;
const ROW_RUSH_ROUND_TIMER_SEC = 10;
const ROW_RUSH_INTERSTITIAL_SEC = 2;
const ROW_RUSH_PERFECT_BONUS = 10000;
const COMBO_EXTENSION_WINDOW_SEC = 1.0;
const DOUBLE_TAP_WINDOW_SEC = 0.3;
const HINT_CAST_DURATION_SEC = 3;
const CLEAROUT_ROTATION_CAP = 5;
const POWERUP_INJECTION_SPECIALS: Array<"BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE"> = [
  "BOMB_3",
  "BOMB_4",
  "BOMB_5",
  "WIPE"
];
const CLASSIC_LEVEL20_REWARD_BUNDLE: ReadonlyArray<{ special: "BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE"; count: number }> = [
  { special: "BOMB_3", count: 1 },
];
const DEFAULT_LEVEL20_REWARD_BUNDLE: ReadonlyArray<{ special: "BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE"; count: number }> = [
  { special: "BOMB_3", count: 3 },
  { special: "BOMB_4", count: 2 },
  { special: "BOMB_5", count: 1 },
  { special: "WIPE", count: 1 }
];

type BombSpecial = "BOMB_3" | "BOMB_4" | "BOMB_5";

function semitoneMultiplier(semitones: number): number {
  return 2 ** (semitones / 12);
}

function bombSfxForSpecial(special: BombSpecial): SfxName {
  if (special === "BOMB_5") {
    return "bomb_5";
  }
  if (special === "BOMB_4") {
    return "bomb_4";
  }
  return "bomb_explode";
}

function strongestBombSfxForSeeds(seeds: Array<{ special: BombSpecial }>): SfxName | null {
  if (seeds.some((seed) => seed.special === "BOMB_5")) {
    return "bomb_5";
  }
  if (seeds.some((seed) => seed.special === "BOMB_4")) {
    return "bomb_4";
  }
  if (seeds.some((seed) => seed.special === "BOMB_3")) {
    return "bomb_explode";
  }
  return null;
}

type SessionMode = "singleplayer" | "versus";
export type SwapBufferMode = "live_latest" | "replay_fifo";

export interface GameControllerOptions {
  sessionMode?: SessionMode;
  allowMusicIntentChanges?: boolean;
  swapBufferMode?: SwapBufferMode;
}

export class GameController {
  phase: GamePhase = "AUTH_GATE";
  run: RunState | null = null;

  private rng: Rng | null = null;
  private audio: AudioManager;
  private authManager: AuthManager;
  private readonly sessionMode: SessionMode;
  private readonly allowMusicIntentChanges: boolean;
  private readonly swapBufferMode: SwapBufferMode;
  private authState: AuthState = { user: null, profile: null, loading: true };
  private leaderboard: LeaderboardEntry[] = [];
  private leaderboardMode: SoloMode | null = null;
  private leaderboardLoading = false;
  private leaderboardRequestSeq = 0;
  private leaderboardTab: LeaderboardTab = loadLeaderboardTabPreference();
  private soloCarouselIndex = 0;
  private selectedSoloMode: SoloMode = "classic";
  private selectedSoloLeaderboardMode: SoloMode = "classic";
  private soloModeContinueAvailability: Record<SoloMode, boolean> = {
    classic: false,
    "row-rush": false,
    clearout: false,
    "color-hunt": false,
    turbomaxxing: false
  };
  private versusLeaderboard: RankedLeaderboardEntry[] = [];
  private versusRankContext: RankedRankContext | null = null;
  private versusLeaderboardLoading = false;
  private hasContinue = false;
  private cloudSaveTimer = 0;
  private readonly CLOUD_SAVE_DEBOUNCE_SEC = 2;
  private paused = false;
  private pendingRotation: RotationDir | null = null;
  private warningActive = false;
  private levelUpTimer = 0;
  private newColorIndex: number | null = null;
  private message = "";
  private messageTimer = 0;
  private flashAlpha = 0;
  private shakeMagnitude = 0;

  private rotationAnimState: RotationAnimState | null = null;
  private collapseAnimState: CollapseAnimState | null = null;
  private swapAnimState: SwapAnimState | null = null;
  private deferredCollapseSteps: CollapseAnimStep[] = [];
  private tutorialState: TutorialState | null = null;
  private lastScorePopup: { points: number; row: number; col: number; multiplier: number } | null = null;
  private currentChainDepth = 0;
  private idleFramesSinceChainUpdate = 0;
  private pendingComboPoints = 0;
  private pendingComboCount = 0;
  private comboTimerRemainingSec = 0;
  private comboPausedUntilSettle = false;
  private flashColor = "255,255,255";
  private hypeText = "";
  private hypeTimer = 0;
  private displayScore = 0;
  private lastMilestoneScore = 0;
  private freezeTimer = 0;
  private noClearElapsedSec = 0;
  private hintActive = false;
  private hintCells: Position[] = [];
  private hintCastCells: Position[] = [];
  private hintCastRemainingSec = 0;
  private dragState: DragState | null = null;
  private hoverCell: Position | null = null;
  private hoveredPowerup: PowerupKind | null = null;
  private wasDragging = false;
  private pendingScoreBatches: PendingScoreBatch[] = [];
  private pendingObjectiveTelemetry: ObjectiveTelemetry | null = null;
  private pendingSoloStageTelemetry: SoloStageTelemetry | null = null;
  private queuedSwaps: MoveAttempt[] = [];
  private deferredResolveSwap: MoveAttempt | null = null;
  private pendingPostResolveChecks = false;
  private rowRushRoundAdvancePending = false;
  private rowRushPendingGameOver = false;
  private pendingClearoutRotationSettle = false;
  private skipNoMoveCheckOnce = false;
  private skipNoMoveCheckAfterReshuffleOnce = false;
  private gameOverElapsedSec = 0;
  private gameOverStep = 0;
  private gameOverStepElapsed = 0;
  private versusResultStep = 0;
  private versusResultStepElapsed = 0;
  private versusAdHeartRestored = false;
  private leaderboardExpanded = false;
  private levelUpTriggered = false;
  private newLevel = 1;
  private xpBeforeRun = 0;
  private newlyUnlockedModes: SoloMode[] = [];
  private _isBitbeastOwner = false;
  private doomClockTimePopup: { amount: number; beatSync: boolean; anchor: Position } | null = null;
  private armedPowerup: PowerupKind | null = null;
  private hintFirstPick: Position | null = null;
  private powerupExecuting = false;
  private powerupExecution: { kind: PowerupKind; remainingSec: number } | null = null;
  private doomFrozenByPowerupAnim = false;
  private grooveCyclesRemaining = 0;
  private grooveAnchorLevel: number | null = null;
  private grooveFallbackRemainingSec = 0;
  private grooveLastAudioRemainingSec: number | null = null;
  private grooveReadyCuePending = false;
  private grooveEndByAudioCount = 0;
  private grooveEndByFallbackCount = 0;
  private lastBoardTap: { row: number; col: number; atSec: number } | null = null;
  private lastGestureBoardTap: { row: number; col: number; atSec: number } | null = null;
  private nextLevel20RewardScore = LEVEL20_REWARD_SCORE_STEP;
  private cashPopupAmount = 0;
  private cashDenyFlashSec = 0;
  private forceTutorial = false;
  private currentRunIsTutorial = false;
  private soloStaminaSpentForCurrentRun = false;
  pendingGameplayIntent: PendingGameplayIntent | null = null;
  private authError = "";
  private authErrorTimer = 0;
  private performanceSettings: PerformanceSettings = clonePerformanceSettings(DEFAULT_PERFORMANCE_SETTINGS);
  private lastHypeCueAtSec = Number.NEGATIVE_INFINITY;
  private lastHypeCuePriority = 0;
  private lastClutchCueAtSec = Number.NEGATIVE_INFINITY;
  private lastPayoffCueAtSec = Number.NEGATIVE_INFINITY;
  private settingsPage = 0;
  private pauseShowSettings = false;
  private preSettingsPhase: GamePhase | null = null;
  private preAddFriendPhase: GamePhase | null = null;
  private debugAccess: AdminDebugAccess = {
    canUseDebugSettings: false,
    reason: "NO_USER",
    source: "none"
  };
  private debugSettings: DebugSettings = normalizeDebugSettings(null);
  private botDecisionDiagnostics: BotDecisionDiagnostics | null = null;
  onVersusAction: ((action: UiAction) => void) | null = null;
  onShopAction: ((action: UiAction) => void) | null = null;
  onProfileAction: ((action: UiAction) => void) | null = null;
  onDebugModeTransition: ((payload: { enabled: boolean; source: string }) => void) | null = null;

  /** Fires when a game event occurs (swap, rotation, powerup, groove). Used by versus mode. */
  onGameEvent: ((type: string, payload: Record<string, unknown>) => void) | null = null;
  private latestRunName = "PLAYER";
  private latestRunScore = 0;
  private latestRunLevel = 1;
  private latestRunSoloMode: SoloMode = "classic";
  private latestRunRank: number | null = null;
  private latestRunRankResolved = false;
  private scoreSubmitFailed = false;

  // Economy
  private walletCoins = 0;
  private walletGems = 0;
  private gemBurstPending = false;
  private gemBurstPackIdx = -1;
  private dailyLoginModalOpen = false;
  private dailyRewardAnimPhase: "entrance" | "waiting" | "shaking" | "burst" | "reveal" | "done" = "entrance";
  private dailyRewardAnimStartMs = 0;
  private dailyRewardClaimedReward: { coins: number; gems: number } | null = null;
  private coinsEarnedThisRun = 0;
  private equippedChar: { icon: string; color: string; name: string; imagePath: string; imageSmallPath: string } = { icon: "1", color: "#FF8E53", name: "Skin #1", imagePath: "/assets/characters/1.png", imageSmallPath: "/assets/characters/1.png" };
  private profilePage = 0;
  private profileFilter: "all" | "wallet" = "all";
  private profileViewMode: "browse" | "grid" = "browse";
  private profileBrowseIndex = 0;
  private profileVinylFlipped = false;
  private profileVinylFlipMs = 0;
  private profileNftAvatarCount = 0;
  private personalBests: PersonalBests = {};
  private dailyShopSkins: CharacterDef[] = [];
  private shopViewIndex = 0;
  private phoneLastSeenCount = 0;
  private phoneTotalCount = 0;
  private phoneTab: "messages" | "friends" | "highscores" | "settings" | "wallet" = "messages";
  private phoneMessageId: string | null = null;
  private phoneFriendId: string | null = null;
  private phoneAddFriendMode = false;
  private phoneEditUsernameMode = false;
  private phoneReferMode = false;
  private incomingMessageAlert: {
    sender: string;
    preview: string;
    messageId: string;
    senderSkin: string;
  } | null = null;
  private phoneShakeTimer = 0;
  private overrideUnreadPhoneCount: number | null = null;
  private onboardingSkin: CharacterDef | null = null;
  private onboardingState: OnboardingState | null = null;
  /** Whether the onboarding world tour is currently active */
  get isOnboardingActive(): boolean { return this.onboardingState?.active === true; }
  private friendsOnlineCount = 0;
  private appearOffline = false;
  private versusError = "";
  private versusBusy = false;
  setPhoneTotalCount(count: number): void { this.phoneTotalCount = count; }
  setUnreadPhoneCount(count: number): void { this.overrideUnreadPhoneCount = count; }
  setPhoneMessageId(id: string | null): void { this.phoneMessageId = id; }
  getPhoneMessageId(): string | null { return this.phoneMessageId; }
  setPhoneFriendId(id: string | null): void { this.phoneFriendId = id; }
  getPhoneFriendId(): string | null { return this.phoneFriendId; }
  getPhoneAddFriendMode(): boolean { return this.phoneAddFriendMode; }
  getPhoneEditUsernameMode(): boolean { return this.phoneEditUsernameMode; }
  clearPhoneEditUsernameMode(): void { this.phoneEditUsernameMode = false; }
  getPhoneReferMode(): boolean { return this.phoneReferMode; }
  setIncomingMessageAlert(alert: { sender: string; preview: string; messageId: string; senderSkin: string }): void {
    this.incomingMessageAlert = { ...alert };
    this.phoneShakeTimer = 1.5;
  }
  onOnboardingConfirm: ((skinId: string) => void) | null = null;
  onOnboardingTourComplete: (() => void) | null = null;
  onWalletAction: ((action: "connect" | "unlink" | "sync" | "avatarSelect" | "solConnect" | "solDisconnect", nftIndex?: number) => void) | null = null;
  onMessageAction: ((action: "primary" | "secondary", messageId: string) => void) | null = null;
  onAdGateWatchAd: (() => void) | null = null;
  onAdGateConnectWallet: (() => void) | null = null;
  onQuickAdForStamina: (() => void) | null = null;
  onShareReferral: (() => void) | null = null;
  onInstallApp: (() => void) | null = null;
  onTogglePushNotifications: (() => void) | null = null;

  constructor(audio: AudioManager, authManager: AuthManager, options: GameControllerOptions = {}) {
    this.audio = audio;
    this.authManager = authManager;
    this.sessionMode = options.sessionMode ?? "singleplayer";
    this.allowMusicIntentChanges = options.allowMusicIntentChanges ?? true;
    this.swapBufferMode = options.swapBufferMode ?? "live_latest";
    this.authState = authManager.getState();
    this.refreshSoloContinueAvailability();
    if (this.isSingleplayerSession()) {
      void this.refreshLeaderboard();
    }

    authManager.subscribe((state) => {
      this.authState = state;
      if (state.user == null) {
        this.versusRankContext = null;
      }
      if (state.user != null && this.phase === "AUTH_GATE") {
        if (state.profile?.username == null) {
          this.prepareOnboardingSkin();
          this.phase = "USERNAME_PICK";
        } else {
          this.phase = "TITLE";
        }
      }
      if (state.user == null && this.phase !== "AUTH_GATE") {
        this.phase = "AUTH_GATE";
        this.dailyLoginModalOpen = false;
        this.run = null;
        this.pendingGameplayIntent = null;
        this.tutorialState = null;
        this.currentRunIsTutorial = false;
        this.soloStaminaSpentForCurrentRun = false;
        this.setPausedState(false);
      }
    });

    try { this.appearOffline = localStorage.getItem("bitbeast.appearOffline") === "1"; } catch { /* ignore */ }

    if (this.authState.user != null) {
      if (this.authState.profile?.username == null) {
        this.prepareOnboardingSkin();
        this.phase = "USERNAME_PICK";
      } else {
        this.phase = "TITLE";
      }
    }
  }

  private sfxAndHaptic(sfx: SfxName, haptic: HapticEvent): void {
    void this.audio.playSfx(sfx);
    hapticManager.fire(haptic);
  }

  async refreshLeaderboard(limit = 100, mode: SoloMode = this.selectedSoloLeaderboardMode): Promise<void> {
    const requestSeq = ++this.leaderboardRequestSeq;
    if (this.leaderboardMode !== mode) {
      this.leaderboard = [];
      this.leaderboardMode = null;
    }
    this.leaderboardLoading = true;
    try {
      const entries = networkManager.isOnline()
        ? await fetchLeaderboard(limit, mode)
        : [];
      if (requestSeq !== this.leaderboardRequestSeq) {
        return;
      }
      this.leaderboard = entries;
      this.leaderboardMode = mode;
    } finally {
      if (requestSeq === this.leaderboardRequestSeq) {
        this.leaderboardLoading = false;
      }
    }
  }

  private prepareLeaderboardDisplay(mode: SoloMode): void {
    this.leaderboardRequestSeq += 1;
    if (this.leaderboardMode !== mode) {
      this.leaderboard = [];
      this.leaderboardMode = null;
    }
    this.leaderboardLoading = true;
  }

  private leaderboardDisplayMode(): SoloMode {
    return this.phase === "RUN_GAMEOVER"
      ? this.latestRunSoloMode
      : this.selectedSoloLeaderboardMode;
  }

  getLeaderboardTab(): LeaderboardTab {
    return this.leaderboardTab;
  }

  getPhoneTab(): "messages" | "friends" | "highscores" | "settings" | "wallet" {
    return this.phoneTab;
  }

  private initialRotationKeysForMode(mode: SoloMode): number {
    if (mode === "row-rush") {
      return 0;
    }
    return mode === "clearout" ? CLEAROUT_ROTATION_CAP : START_ROTATION_KEYS;
  }

  private clampRotationKeysForMode(rotationKeys: number, mode: SoloMode): number {
    const normalized = Number.isFinite(rotationKeys)
      ? Math.max(0, Math.floor(rotationKeys))
      : 0;
    if (mode === "row-rush") {
      return 0;
    }
    if (mode === "clearout") {
      return Math.min(CLEAROUT_ROTATION_CAP, normalized);
    }
    return normalized;
  }

  private clampRunRotationKeysForCurrentMode(): void {
    if (this.run == null) {
      return;
    }
    this.run.rotationKeys = this.clampRotationKeysForMode(
      this.run.rotationKeys,
      this.run.soloMode ?? this.selectedSoloMode
    );
  }

  private addRotationKeys(amount: number, mode: SoloMode = this.runSoloMode()): boolean {
    if (this.run == null || amount <= 0 || mode === "row-rush") {
      return false;
    }
    const before = this.clampRotationKeysForMode(this.run.rotationKeys, mode);
    const after = this.clampRotationKeysForMode(before + amount, mode);
    this.run.rotationKeys = after;
    return after > before;
  }

  isPhoneHighscoresVisible(): boolean {
    return this.phase === "PHONE" && this.phoneTab === "highscores";
  }

  setLeaderboardTab(tab: LeaderboardTab): void {
    if (this.leaderboardTab === tab) {
      return;
    }
    this.leaderboardTab = tab;
    saveLeaderboardTabPreference(tab);
  }

  setVersusLeaderboardData(entries: RankedLeaderboardEntry[], context: RankedRankContext | null): void {
    this.versusLeaderboard = entries;
    this.versusRankContext = context;
  }

  setVersusRankContext(context: RankedRankContext | null): void {
    this.versusRankContext = context;
  }

  setVersusLeaderboardLoading(loading: boolean): void {
    this.versusLeaderboardLoading = loading;
  }

  setPerformanceSettings(settings: Partial<PerformanceSettings> | null | undefined): void {
    this.performanceSettings = normalizePerformanceSettings(settings);
  }

  getPerformanceSettings(): PerformanceSettings {
    return clonePerformanceSettings(this.performanceSettings);
  }

  setDebugOverlayEnabled(enabled: boolean): void {
    this.applyDebugSettings(
      {
        ...this.debugSettings,
        enabled
      },
      "legacy_toggle"
    );
  }

  setDebugSettings(settings: Partial<DebugSettings> | null | undefined, source = "runtime_sync"): void {
    this.applyDebugSettings(settings, source);
  }

  getDebugSettings(): DebugSettings {
    return { ...this.debugSettings };
  }

  setDebugAccess(access: AdminDebugAccess): void {
    this.debugAccess = access;
    this.applyDebugSettings(this.debugSettings, "auth_access_update");
  }

  setBotDecisionDiagnostics(diagnostics: BotDecisionDiagnostics | null): void {
    this.botDecisionDiagnostics = diagnostics;
  }

  private applyDebugSettings(next: Partial<DebugSettings> | null | undefined, source: string): void {
    const prevEnabled = this.debugSettings.enabled;
    this.debugSettings = enforceDebugAccess(next, this.debugAccess.canUseDebugSettings);
    if (this.debugSettings.enabled !== prevEnabled) {
      this.onDebugModeTransition?.({ enabled: this.debugSettings.enabled, source });
    }
  }

  private setPerformancePreset(preset: Exclude<PerformancePreset, "CUSTOM">): void {
    this.performanceSettings = applyPerformancePreset(preset);
  }

  private applyManualPerformanceChange(next: Partial<Omit<PerformanceSettings, "preset">>): void {
    this.performanceSettings = normalizePerformanceSettings({
      ...this.performanceSettings,
      ...next,
      preset: "CUSTOM"
    });
  }

  private isSingleplayerSession(): boolean {
    return this.sessionMode === "singleplayer";
  }

  private setPausedState(paused: boolean): void {
    if (this.paused === paused) {
      return;
    }
    this.paused = paused;
    void this.audio.setPaused(paused);
    if (paused) {
      this.setMessage("PAUSED", STATE_MACHINE_TUNING.continueMessageDurationSec);
    }
  }

  private pauseEligible(): boolean {
    if (this.phase === "VERSUS_MATCH") return true;
    if (!this.isSingleplayerSession()) return false;
    return this.phase === "RUN_PLAYABLE" || this.phase === "RUN_RESOLVING" || this.phase === "RUN_LEVELUP";
  }

  private setMessage(text: string, seconds: number = STATE_MACHINE_TUNING.messageDurationSec): void {
    this.message = text;
    this.messageTimer = seconds;
  }

  private hasPersistentProgressAccount(): boolean {
    const userId = this.authState.user?.id;
    return userId != null && userId !== "guest-local";
  }

  private currentAccountLevel(): number {
    return Math.max(1, this.authState.profile?.level ?? 1);
  }

  private featureLockState(feature: UnlockableFeature): {
    unlocked: boolean;
    requiredLevel: number;
    signInRequired: boolean;
  } {
    // During onboarding, all features appear unlocked so buildings show normally
    if (this.onboardingState?.active) {
      return { unlocked: true, requiredLevel: 0, signInRequired: false };
    }
    const requiredLevel = featureUnlockLevel(feature);
    const unlocked = isFeatureUnlocked(this.currentAccountLevel(), feature);
    return {
      unlocked,
      requiredLevel,
      signInRequired: !this.hasPersistentProgressAccount() && !unlocked,
    };
  }

  private soloModeLockState(mode: SoloMode): {
    unlocked: boolean;
    requiredLevel: number;
    signInRequired: boolean;
  } {
    const requiredLevel = soloModeUnlockLevel(mode);
    const unlocked = this._isBitbeastOwner || isSoloModeUnlocked(this.currentAccountLevel(), mode);
    return {
      unlocked,
      requiredLevel,
      signInRequired: !this.hasPersistentProgressAccount() && !unlocked,
    };
  }

  private progressionLockMessage(lockState: {
    requiredLevel: number;
    signInRequired: boolean;
  }): string {
    if (lockState.signInRequired) {
      return `SIGN IN AND REACH LV ${lockState.requiredLevel}`;
    }
    return `UNLOCKS AT LV ${lockState.requiredLevel}`;
  }

  private featureLockedMessage(feature: UnlockableFeature): string {
    return this.progressionLockMessage(this.featureLockState(feature));
  }

  private soloModeLockedMessage(mode: SoloMode): string {
    return this.progressionLockMessage(this.soloModeLockState(mode));
  }

  private denyLockedFeature(feature: UnlockableFeature): void {
    this.sfxAndHaptic("button_click", "button_tap");
    this.setMessage(this.featureLockedMessage(feature), Math.max(2.2, STATE_MACHINE_TUNING.messageDurationSec));
  }

  private focusSoloSelectorMode(mode: SoloMode): void {
    const nextIndex = SOLO_SELECT_MODES.indexOf(mode);
    this.soloCarouselIndex = nextIndex >= 0 ? nextIndex : 0;
    this.setSelectedSoloMode(mode);
    this.refreshSoloContinueAvailability();
  }

  private denyLockedSoloMode(mode: SoloMode): void {
    this.focusSoloSelectorMode(mode);
    this.phase = "SOLO_SELECT";
    this.setMessage(this.soloModeLockedMessage(mode), Math.max(2.2, STATE_MACHINE_TUNING.messageDurationSec));
  }

  private lockedFeatureForShopAction(action: UiAction): UnlockableFeature | null {
    if (action === "SHOP_BUY" || action === "SHOP_EQUIP") {
      return "shop";
    }
    if (
      action === "SHOP_EXCHANGE"
      || action === "SHOP_BUY_GEMS"
      || action === "SHOP_BUY_SOL"
      || action === "SHOP_BUY_IAP"
      || action === "SHOP_RESTORE_IAP"
      || action === "SHOP_IAP_CONFIRM"
    ) {
      return "gemShop";
    }
    if (
      action === "TOKEN_SHOP_SELECT"
      || action === "TOKEN_SHOP_CONFIRM"
      || action === "TOKEN_SHOP_RECHECK"
    ) {
      return "tokenShop";
    }
    return null;
  }

  private pulseFx(
    flash: number = STATE_MACHINE_TUNING.pulseFx.genericFlash,
    shake: number = STATE_MACHINE_TUNING.pulseFx.genericShake
  ): void {
    this.flashAlpha = Math.max(this.flashAlpha, flash);
    this.shakeMagnitude = Math.max(this.shakeMagnitude, shake);
  }

  private isReducedMotionPreferred(): boolean {
    return prefersReducedMotion();
  }

  private resolveFeedbackMode(): VersusFeedbackMode {
    return resolveVersusFeedbackPolicy({
      performance: this.performanceSettings,
      reducedMotionPreferred: this.isReducedMotionPreferred()
    }).mode;
  }

  private hypeCooldownForMode(mode: VersusFeedbackMode): number {
    if (mode === "full") {
      return STATE_MACHINE_TUNING.versusFeedback.hypeCooldownSec.full;
    }
    if (mode === "reduced") {
      return STATE_MACHINE_TUNING.versusFeedback.hypeCooldownSec.reduced;
    }
    return STATE_MACHINE_TUNING.versusFeedback.hypeCooldownSec.off;
  }

  private scoreSignificanceForMode(mode: VersusFeedbackMode): number {
    if (mode === "full") {
      return STATE_MACHINE_TUNING.versusFeedback.significanceScore.full;
    }
    if (mode === "reduced") {
      return STATE_MACHINE_TUNING.versusFeedback.significanceScore.reduced;
    }
    return STATE_MACHINE_TUNING.versusFeedback.significanceScore.off;
  }

  private maybeSetCadencedHypeCue(
    text: string,
    durationSec: number,
    priority: number,
    nowSec: number,
    mode: VersusFeedbackMode,
    force = false
  ): boolean {
    const cooldownSec = this.hypeCooldownForMode(mode);
    const elapsedSec = nowSec - this.lastHypeCueAtSec;
    const canReplace = priority > this.lastHypeCuePriority;
    if (!force && elapsedSec < cooldownSec && !canReplace) {
      return false;
    }
    this.hypeText = text;
    this.hypeTimer = durationSec;
    this.lastHypeCueAtSec = nowSec;
    this.lastHypeCuePriority = priority;
    return true;
  }

  private resetTransientAnimations(): void {
    this.rotationAnimState = null;
    this.collapseAnimState = null;
    this.swapAnimState = null;
    this.deferredCollapseSteps.length = 0;
    this.pendingScoreBatches.length = 0;
    this.pendingObjectiveTelemetry = null;
    this.pendingSoloStageTelemetry = null;
    this.clearBufferedBoardInteractions();
    this.idleFramesSinceChainUpdate = 0;
    this.pendingPostResolveChecks = false;
    this.skipNoMoveCheckOnce = false;
    this.skipNoMoveCheckAfterReshuffleOnce = false;
    this.armedPowerup = null;
    this.hintFirstPick = null;
    this.powerupExecuting = false;
    this.powerupExecution = null;
    this.doomFrozenByPowerupAnim = false;
    this.hintCastCells = [];
    this.hintCastRemainingSec = 0;
    this.cashPopupAmount = 0;
    this.cashDenyFlashSec = 0;
    this.lastHypeCueAtSec = Number.NEGATIVE_INFINITY;
    this.lastHypeCuePriority = 0;
    this.lastClutchCueAtSec = Number.NEGATIVE_INFINITY;
    this.lastPayoffCueAtSec = Number.NEGATIVE_INFINITY;
    this.resetComboState();
  }

  /** Flush all gameplay visual state so nothing leaks onto menu screens. */
  private clearGameplayVisualState(): void {
    this.resetTransientAnimations();
    this.flashAlpha = 0;
    this.shakeMagnitude = 0;
    this.flashColor = "255,255,255";
    this.hypeText = "";
    this.hypeTimer = 0;
    this.messageTimer = 0;
    this.message = "";
    this.levelUpTimer = 0;
    this.warningActive = false;
    this.displayScore = 0;
    this.currentChainDepth = 0;
    this.rowRushRoundAdvancePending = false;
    this.rowRushPendingGameOver = false;
  }

  private resetComboState(): void {
    this.pendingComboPoints = 0;
    this.pendingComboCount = 0;
    this.comboTimerRemainingSec = 0;
    this.comboPausedUntilSettle = false;
  }

  private saveIfRunning(): void {
    if (!this.isSingleplayerSession()) return;
    if (this.run == null || this.rng == null) return;
    this.run.rngState = this.rng.getState();
    this.run.board = serializeBoard(this.run.board);
    saveRunState(this.run, { tutorialRun: this.currentRunIsTutorial });
    this.refreshSoloContinueAvailability();
    this.cloudSaveTimer = this.CLOUD_SAVE_DEBOUNCE_SEC;
  }

  private resetGrooveRuntimeState(): void {
    this.grooveCyclesRemaining = 0;
    this.grooveAnchorLevel = null;
    this.grooveFallbackRemainingSec = 0;
    this.grooveLastAudioRemainingSec = null;
    this.grooveReadyCuePending = false;
  }

  private resetGrooveTelemetry(): void {
    this.grooveEndByAudioCount = 0;
    this.grooveEndByFallbackCount = 0;
  }

  private resetGrooveStateToIdle(): void {
    if (this.run == null) {
      return;
    }
    this.run.groove.active = false;
    this.run.groove.armed = false;
    this.run.groove.readyWindowSec = 0;
    this.run.groove.activeRemainingSec = 0;
    this.run.groove.multiplier = 1;
    this.run.groove.freeRotationsRemaining = 0;
    this.run.groove.groove = 0;
    this.resetGrooveRuntimeState();
  }

  private flushCloudSave(): void {
    if (!this.isSingleplayerSession()) return;
    if (this.run == null) return;
    const userId = this.authState.user?.id;
    if (userId == null) return;
    if (networkManager.isOnline()) {
      void saveRunStateCloud(userId, this.run, this.runSoloMode());
    }
  }

  private createClearoutBoard(levelCfg: LevelConfig): BoardState {
    if (this.rng == null) {
      throw new Error("Clearout board generation requires an active RNG");
    }
    let board = createBoardForLevel(levelCfg, this.rng, soloModeBoardSize("clearout"));
    let attempts = 0;
    while ((!hasValidMove(board) || hasAnyMatch(board)) && attempts < 40) {
      board = createBoardForLevel(levelCfg, this.rng, soloModeBoardSize("clearout"));
      attempts += 1;
    }
    if (!hasValidMove(board) || hasAnyMatch(board)) {
      guaranteedReshuffle(board, levelCfg, this.rng);
    }
    if (!hasValidMove(board) || hasAnyMatch(board)) {
      forceKnownGoodBoard(board);
    }
    board.crateGravity = true;
    return board;
  }

  private reseedEndlessClearoutLevel20(): boolean {
    if (
      this.run == null
      || this.rng == null
      || !this.isSingleplayerSession()
      || this.runSoloMode() !== "clearout"
      || this.run.level !== MAX_LEVEL
      || countOccupiedCells(this.run.board) !== 0
    ) {
      return false;
    }
    this.run.board = this.createClearoutBoard(this.levelConfig());
    this.syncProgressionStateForCurrentLevel();
    return true;
  }

  private startNewRun(mode: SoloMode = this.selectedSoloMode): void {
    const seed = seedFromTime();
    this.rng = new Rng(seed);
    const levelCfg = levelConfigForSoloMode(mode, getLevelConfig(1));
    const startDoomClockSec = mode === "row-rush" ? ROW_RUSH_ROUND_TIMER_SEC : doomClockCapForLevel(1);
    let board = createBoardForLevel(levelCfg, this.rng, soloModeBoardSize(mode));
    if (mode === "clearout") {
      board = this.createClearoutBoard(levelCfg);
    } else {
      let attempts = 0;
      while ((!hasValidMove(board) || hasAnyMatch(board)) && attempts < 40) {
        board = createBoardForLevel(levelCfg, this.rng, soloModeBoardSize(mode));
        attempts += 1;
      }
      if (!hasValidMove(board) || hasAnyMatch(board)) {
        guaranteedReshuffle(board, levelCfg, this.rng);
      }
      if (!hasValidMove(board) || hasAnyMatch(board)) {
        forceKnownGoodBoard(board);
      }
    }

    this.run = {
      level: 1,
      levelScore: 0,
      levelAdvanceBaseScore: 0,
      totalScore: 0,
      lives: Math.ceil(startDoomClockSec / 20),
      doomClockSec: startDoomClockSec,
      nextExtraLifeScore: Infinity,
      nextKeyScore: mode === "row-rush" ? Number.POSITIVE_INFINITY : KEY_EARN_STEP,
      rotationKeys: this.initialRotationKeysForMode(mode),
      board,
      groove: {
        groove: 0,
        multiplier: 1,
        armed: false,
        readyWindowSec: 0,
        active: false,
        activeRemainingSec: 0,
        freeRotationsRemaining: 0
      },
      beat: createBeatState(levelCfg.rotationEveryBeats),
      rngSeed: seed,
      rngState: this.rng.getState(),
      cashBalance: 0,
      cashTilesClearedTotal: 0,
      soloMode: mode,
      soloStageGoal: null,
      rowRushRound: mode === "row-rush" ? this.createRowRushRoundState(0) : null,
      powerupInventory: {
        HINT: 0,
        ROWRIP: 0,
        COLCUT: 0,
        TRANSMUTE: 0,
        ZIGZAGBOLT: 0
      },
      level20BountyCompletions: 0,
      currentObjective: null,
      currentBounty: null
    };
    this.setSelectedSoloMode(mode);
    this.syncProgressionStateForCurrentLevel();
    this.setPausedState(false);
    this.phase = "RUN_PLAYABLE";
    this.pendingRotation = mode === "row-rush" ? null : "LEFT";
    this.warningActive = false;
    this.levelUpTimer = 0;
    this.newColorIndex = null;
    this.message = "";
    this.messageTimer = 0;
    this.resetTransientAnimations();
    this.displayScore = 0;
    this.currentChainDepth = 0;
    this.idleFramesSinceChainUpdate = 0;
    this.hypeText = "";
    this.hypeTimer = 0;
    this.freezeTimer = 0;
    this.noClearElapsedSec = 0;
    this.hintActive = false;
    this.lastMilestoneScore = 0;
    this.hintCells = [];
    this.tutorialState = soloModeTutorialEligible(mode) && (!this.forceTutorial && hasTutorialBeenSeen())
      ? null
      : soloModeTutorialEligible(mode)
        ? createTutorialState()
        : null;
    this.currentRunIsTutorial = this.tutorialState != null;
    this.soloStaminaSpentForCurrentRun = false;
    this.pendingGameplayIntent = null;
    this.forceTutorial = false;
    // Apply fixed tutorial board so the guided swap is always valid
    if (this.tutorialState != null && this.run != null) {
      const board = this.run.board;
      for (let r = 0; r < board.size; r++) {
        for (let c = 0; c < board.size; c++) {
          const cell = board.cells[r][c];
          if (cell.tile != null) {
            cell.tile.color = TUTORIAL_BOARD[r][c];
            cell.tile.special = "NONE";
            cell.tile.lockHp = 0;
          }
        }
      }
    }
    this.pendingScoreBatches.length = 0;
    this.pendingObjectiveTelemetry = null;
    this.pendingSoloStageTelemetry = null;
    this.clearQueuedSwapBuffer();
    this.pendingPostResolveChecks = false;
    this.rowRushRoundAdvancePending = false;
    this.rowRushPendingGameOver = false;
    this.skipNoMoveCheckOnce = false;
    this.skipNoMoveCheckAfterReshuffleOnce = false;
    this.resetGrooveRuntimeState();
    this.resetGrooveTelemetry();
    clearRunState(mode);
    this.normalizeRowRushContinuationState();
    this.clearRowRushRoundSummary();
    this.saveIfRunning();
    if (this.allowMusicIntentChanges) {
      this.audio.setMusicIntent(1, "VERSE");
    }
    void this.audio.preloadLevel(1);
  }

  resetVersusResultStepTimer(): void {
    if (this.phase === "VERSUS_RESULT") {
      this.versusResultStepElapsed = 0;
      this.versusAdHeartRestored = true;
    }
  }

  openStaminaGate(intent: PendingGameplayIntent): void {
    this.pendingGameplayIntent = intent;
    this.setPausedState(false);
    this.phase = "AD_GATE";
  }

  fallbackPendingStaminaIntentToGate(): void {
    const intent = this.pendingGameplayIntent;
    if (intent != null) {
      this.openStaminaGate(intent);
      return;
    }
    this.setPausedState(false);
    this.phase = "AD_GATE";
  }

  async restoreStaminaFromAd(): Promise<void> {
    await staminaService.restoreFromAd();
    this.resumeAfterStaminaGateResolved();
  }

  resumeAfterStaminaGateResolved(): void {
    if (!staminaService.canPlay()) {
      return;
    }
    const intent = this.pendingGameplayIntent;
    this.pendingGameplayIntent = null;
    this.setPausedState(false);
    if (intent == null) {
      this.phase = "TITLE";
      return;
    }
    if (intent.kind === "solo_start") {
      this.startNewRun(intent.mode);
      return;
    }
    if (intent.kind === "solo_continue") {
      if (!this.continueRun(intent.mode)) {
        this.phase = "TITLE";
      }
      return;
    }
    if (intent.kind === "versus_quickmatch") {
      this.phase = "TITLE";
      this.onVersusAction?.("VERSUS_QUICKMATCH");
      return;
    }
    this.phase = intent.returnPhase;
  }

  /**
   * Initialize a new run from a deterministic seed (used by versus mode).
   * Does NOT save, does NOT trigger tutorial, does NOT set phase.
   * Caller is responsible for setting the phase after this call.
   */
  startNewRunFromSeed(
    seed: number,
    options?: { startLevel?: number; doomClockStartSec?: number }
  ): void {
    this.rng = new Rng(seed);
    const startLevel = Math.max(1, Math.min(MAX_LEVEL, Math.floor(options?.startLevel ?? 1)));
    const startDoomClockSec = doomClockCapForLevel(startLevel);
    const levelCfg = getLevelConfig(startLevel);
    let board = createBoardForLevel(levelCfg, this.rng);
    let attempts = 0;
    while ((!hasValidMove(board) || hasAnyMatch(board)) && attempts < 40) {
      board = createBoardForLevel(levelCfg, this.rng);
      attempts += 1;
    }
    if (!hasValidMove(board) || hasAnyMatch(board)) {
      guaranteedReshuffle(board, levelCfg, this.rng);
    }

    this.run = {
      level: startLevel,
      levelScore: 0,
      levelAdvanceBaseScore: 0,
      totalScore: 0,
      lives: Math.ceil(startDoomClockSec / 20),
      doomClockSec: startDoomClockSec,
      nextExtraLifeScore: Infinity,
      nextKeyScore: KEY_EARN_STEP,
      rotationKeys: this.initialRotationKeysForMode("classic"),
      board,
      groove: {
        groove: 0,
        multiplier: 1,
        armed: false,
        readyWindowSec: 0,
        active: false,
        activeRemainingSec: 0,
        freeRotationsRemaining: 0
      },
      beat: createBeatState(levelCfg.rotationEveryBeats),
      rngSeed: seed,
      rngState: this.rng.getState(),
      cashBalance: 0,
      cashTilesClearedTotal: 0,
      soloMode: "classic",
      soloStageGoal: null,
      powerupInventory: {
        HINT: 0,
        ROWRIP: 0,
        COLCUT: 0,
        TRANSMUTE: 0,
        ZIGZAGBOLT: 0
      },
      level20BountyCompletions: 0,
      currentObjective: null,
      currentBounty: null
    };
    this.syncProgressionStateForCurrentLevel();
    this.initializeLevel20RewardProgress();
    this.setPausedState(false);
    this.pendingRotation = "LEFT";
    this.warningActive = false;
    this.levelUpTimer = 0;
    this.newColorIndex = null;
    this.message = "";
    this.messageTimer = 0;
    this.resetTransientAnimations();
    this.displayScore = 0;
    this.currentChainDepth = 0;
    this.idleFramesSinceChainUpdate = 0;
    this.hypeText = "";
    this.hypeTimer = 0;
    this.freezeTimer = 0;
    this.noClearElapsedSec = 0;
    this.hintActive = false;
    this.lastMilestoneScore = 0;
    this.hintCells = [];
    this.tutorialState = null;
    this.currentRunIsTutorial = false;
    this.soloStaminaSpentForCurrentRun = false;
    this.pendingGameplayIntent = null;
    this.pendingScoreBatches.length = 0;
    this.pendingObjectiveTelemetry = null;
    this.pendingSoloStageTelemetry = null;
    this.clearQueuedSwapBuffer();
    this.pendingPostResolveChecks = false;
    this.skipNoMoveCheckOnce = false;
    this.skipNoMoveCheckAfterReshuffleOnce = false;
    this.resetGrooveRuntimeState();
    this.resetGrooveTelemetry();
  }

  private continueRun(mode: SoloMode = this.selectedSoloMode): boolean {
    const savedContext = loadRunStateContext(mode);
    const saved = savedContext?.run ?? null;
    if (saved == null) {
      return false;
    }
    // Background: also check cloud for a newer save
    const userId = this.authState.user?.id;
    if (userId != null) {
      if (networkManager.isOnline()) {
        void loadRunStateCloud(userId, mode);
      }
    }
    const nextKeyScore =
      (Math.floor(Math.max(0, saved.totalScore) / KEY_EARN_STEP) + 1) * KEY_EARN_STEP;
    const savedMode = saved.soloMode ?? mode;
    const doomClockCap = savedMode === "row-rush" ? ROW_RUSH_ROUND_TIMER_SEC : doomClockCapForLevel(saved.level);
    const doomClockSec = saved.doomClockSec != null && saved.doomClockSec > 0
      ? Math.max(0, Math.min(doomClockCap, saved.doomClockSec))
      : doomClockCap;
    this.run = {
      ...saved,
      doomClockSec,
      lives: Math.ceil(doomClockSec / 20),
      nextExtraLifeScore: Infinity,
      nextKeyScore: savedMode === "row-rush" ? Number.POSITIVE_INFINITY : nextKeyScore,
      rotationKeys: this.clampRotationKeysForMode(saved.rotationKeys, savedMode),
      levelAdvanceBaseScore: normalizeLevelAdvanceBaseScore(saved.levelScore, saved.levelAdvanceBaseScore),
      board: deserializeBoard(saved.board),
      groove: normalizeGrooveState(saved.groove),
      cashBalance: Math.max(0, Math.floor(saved.cashBalance ?? 0)),
      cashTilesClearedTotal: Math.max(0, Math.floor(saved.cashTilesClearedTotal ?? 0)),
      powerupInventory: normalizePowerupInventory(saved.powerupInventory),
      soloMode: savedMode,
      level20BountyCompletions: normalizeLevel20BountyCompletions(saved.level20BountyCompletions)
    };
    this.setSelectedSoloMode(this.run.soloMode ?? mode);
    this.syncProgressionStateForCurrentLevel({ preserveExisting: true });
    this.initializeLevel20RewardProgress();
    this.grooveReadyCuePending = false;
    this.rng = new Rng(saved.rngSeed);
    this.rng.setState(saved.rngState);
    this.phase = "RUN_PLAYABLE";
    this.setPausedState(false);
    this.pendingRotation = this.run.soloMode === "row-rush"
      ? this.run.rowRushRound?.lockedRotation ?? null
      : "LEFT";
    this.warningActive = false;
    this.levelUpTimer = 0;
    this.newColorIndex = null;
    this.message = "CONTINUED RUN";
    this.messageTimer = STATE_MACHINE_TUNING.continueMessageDurationSec;
    this.resetTransientAnimations();
    this.displayScore = this.run.totalScore;
    this.currentChainDepth = 0;
    this.idleFramesSinceChainUpdate = 0;
    this.noClearElapsedSec = 0;
    this.hintActive = false;
    this.hintCells = [];
    this.tutorialState = null;
    this.currentRunIsTutorial = (savedContext?.tutorialRun === true) && this.run.soloMode === "classic";
    this.soloStaminaSpentForCurrentRun = false;
    this.pendingGameplayIntent = null;
    this.pendingScoreBatches.length = 0;
    this.pendingObjectiveTelemetry = null;
    this.pendingSoloStageTelemetry = null;
    this.clearQueuedSwapBuffer();
    this.pendingPostResolveChecks = false;
    this.rowRushRoundAdvancePending = false;
    this.rowRushPendingGameOver = false;
    this.skipNoMoveCheckOnce = false;
    this.skipNoMoveCheckAfterReshuffleOnce = false;
    this.resetGrooveTelemetry();
    if (this.run.groove.active) {
      this.grooveCyclesRemaining = 1;
      this.grooveAnchorLevel = this.run.level;
      this.grooveFallbackRemainingSec = Math.max(0, this.run.groove.activeRemainingSec);
      this.grooveLastAudioRemainingSec = null;
      this.run.groove.activeRemainingSec = this.grooveFallbackRemainingSec;
    } else {
      this.resetGrooveRuntimeState();
    }
    this.syncMusicIntentForRun();
    this.syncGrooveRemainingFromMusicBoundary();
    this.normalizeRowRushContinuationState();
    void this.audio.preloadLevel(this.run.level);
    return true;
  }

  private levelConfig() {
    const level = this.run?.level ?? 1;
    const base = getLevelConfig(level);
    if (!this.isSingleplayerSession()) {
      return {
        ...base,
        scoreTarget: versusScoreTargetForLevel(level)
      };
    }
    return levelConfigForSoloMode(this.run?.soloMode ?? this.selectedSoloMode, base);
  }

  private currentClassicLevelAdvanceBaseScore(): number {
    if (this.run == null) {
      return 0;
    }
    return normalizeLevelAdvanceBaseScore(this.run.levelScore, this.run.levelAdvanceBaseScore);
  }

  private currentClassicLevelScoreProgress(): number {
    if (this.run == null) {
      return 0;
    }
    return Math.max(0, this.run.levelScore - this.currentClassicLevelAdvanceBaseScore());
  }

  private currentActiveObjective(): LevelObjectiveState | null {
    if (this.run == null || !this.isSingleplayerSession()) {
      return null;
    }
    return this.run.level >= MAX_LEVEL
      ? this.run.currentBounty ?? null
      : this.run.currentObjective ?? null;
  }

  private rollLevel20Bounty(previousKind: LevelObjectiveKind | null = null): LevelObjectiveState {
    const filtered = LEVEL20_BOUNTY_POOL.filter((kind) => kind !== previousKind);
    const candidates = filtered.length > 0 ? filtered : [...LEVEL20_BOUNTY_POOL];
    const picked = candidates[this.rng?.nextInt(candidates.length) ?? 0] ?? LEVEL20_BOUNTY_POOL[0];
    return createLevel20BountyState(
      picked,
      normalizeLevel20BountyCompletions(this.run?.level20BountyCompletions)
    );
  }

  private syncProgressionStateForCurrentLevel(options?: {
    preserveExisting?: boolean;
    previousRowRushTargetRows?: number[];
    previousColorHuntTargetColors?: number[];
  }): void {
    if (this.run == null) {
      return;
    }
    if (!this.isSingleplayerSession()) {
      this.run.soloStageGoal = null;
      this.run.currentObjective = null;
      this.run.currentBounty = null;
      return;
    }
    const soloMode = this.run.soloMode ?? "classic";
    if (!this.isClassicSoloMode(soloMode)) {
      const rng = this.rng ?? new Rng(this.run.rngState || this.run.rngSeed || seedFromTime());
      this.run.currentObjective = null;
      this.run.currentBounty = null;
      this.run.level20BountyCompletions = 0;
      this.run.soloStageGoal = options?.preserveExisting
        ? normalizeSoloStageGoal(soloMode, this.run.level, this.levelConfig().tileTypes, this.run.soloStageGoal, rng, this.run.board)
        : createSoloStageGoal(soloMode, this.run.level, this.levelConfig().tileTypes, rng, this.run.board, {
            previousTargetRows: soloMode === "row-rush" ? options?.previousRowRushTargetRows : undefined,
            previousTargetColors: soloMode === "color-hunt" ? options?.previousColorHuntTargetColors : undefined
          });
      return;
    }

    this.run.soloStageGoal = null;
    this.run.levelAdvanceBaseScore = options?.preserveExisting
      ? normalizeLevelAdvanceBaseScore(this.run.levelScore, this.run.levelAdvanceBaseScore)
      : this.run.levelScore;

    if (this.run.level >= MAX_LEVEL) {
      this.run.currentObjective = null;
      if (options?.preserveExisting) {
        const kind = this.run.currentBounty != null && LEVEL20_BOUNTY_POOL.includes(this.run.currentBounty.kind)
          ? this.run.currentBounty.kind
          : LEVEL20_BOUNTY_POOL[0];
        const bounty = level20BountyDefinition(kind, normalizeLevel20BountyCompletions(this.run.level20BountyCompletions));
        this.run.currentBounty = normalizeObjectiveState(bounty.kind, bounty.target, this.run.currentBounty);
      } else {
        this.run.currentBounty = this.rollLevel20Bounty(this.run.currentBounty?.kind ?? null);
      }
      return;
    }

    this.run.currentBounty = null;
    this.run.currentObjective = options?.preserveExisting
      ? normalizeObjectiveStateForLevel(this.run.level, this.run.currentObjective)
      : createObjectiveStateForLevel(this.run.level);
  }

  private initializeLevel20RewardProgress(): void {
    this.nextLevel20RewardScore = LEVEL20_REWARD_SCORE_STEP;
    if (this.run == null || this.run.level !== MAX_LEVEL) {
      return;
    }
    const completedMilestones = Math.max(0, Math.floor(this.run.levelScore / LEVEL20_REWARD_SCORE_STEP));
    this.nextLevel20RewardScore = (completedMilestones + 1) * LEVEL20_REWARD_SCORE_STEP;
  }

  private applyObjectiveReward(reward: ObjectiveReward): void {
    if (this.run == null) {
      return;
    }

    for (const grant of reward.grants) {
      if (grant.type === "score") {
        this.run.levelScore += grant.amount;
        continue;
      }
      if (grant.type === "doomSec") {
        this.run.doomClockSec = Math.min(this.currentDoomClockMaxSec(), this.run.doomClockSec + grant.amount);
        continue;
      }
      if (grant.type === "cash") {
        this.run.cashBalance += grant.amount;
        continue;
      }
      if (grant.type === "keys") {
        this.addRotationKeys(grant.amount);
        continue;
      }
      if (grant.type === "special") {
        for (let i = 0; i < grant.count; i += 1) {
          this.injectRandomSpecialTile(grant.special);
        }
        continue;
      }
      for (let i = 0; i < grant.count; i += 1) {
        this.run.powerupInventory[grant.powerup] += 1;
      }
    }

    this.run.lives = Math.ceil(this.run.doomClockSec / 20);
    this.applyLevel20RewardMilestones();
  }

  private applyObjectiveProgress(telemetry: ObjectiveTelemetry): void {
    if (this.run == null || !this.isSingleplayerSession()) {
      return;
    }
    if (!this.isClassicSoloMode(this.run.soloMode ?? "classic")) {
      return;
    }

    const activeObjective = this.currentActiveObjective();
    if (activeObjective == null) {
      return;
    }

    if (!activeObjective.completed) {
      const gained = objectiveProgressFromTelemetry(activeObjective.kind, telemetry, activeObjective.targetShape);
      if (gained > 0) {
        activeObjective.progress = Math.max(0, Math.min(activeObjective.target, activeObjective.progress + gained));
        activeObjective.completed = activeObjective.progress >= activeObjective.target;
      }
    }

    if (!activeObjective.completed || activeObjective.rewarded) {
      return;
    }

    const reward = objectiveRewardForLevel(
      this.run.level,
      normalizeLevel20BountyCompletions(this.run.level20BountyCompletions)
    );
    activeObjective.rewarded = true;
    void this.audio.playSfx("level_up");

    this.applyObjectiveReward(reward);

    if (this.run.level >= MAX_LEVEL) {
      this.injectLevel20RewardBundle();
      this.run.level20BountyCompletions = normalizeLevel20BountyCompletions(this.run.level20BountyCompletions) + 1;
      this.run.currentBounty = this.rollLevel20Bounty(activeObjective.kind);
      return;
    }
  }

  private refreshSoloContinueAvailability(): void {
    this.soloModeContinueAvailability = loadSoloContinueAvailability();
    this.hasContinue = this.soloModeContinueAvailability[this.selectedSoloMode]
      && this.soloModeLockState(this.selectedSoloMode).unlocked;
  }

  private runSoloMode(): SoloMode {
    return this.run?.soloMode ?? this.selectedSoloMode;
  }

  private isClassicSoloMode(mode: SoloMode): boolean {
    return soloModeUsesClassicProgression(mode);
  }

  private animationDurationMultiplier(mode: SoloMode = this.runSoloMode()): number {
    return this.isSingleplayerSession() ? soloModeAnimationDurationMultiplier(mode) : 1;
  }

  private doomPressureMultiplier(mode: SoloMode = this.runSoloMode()): number {
    return this.isSingleplayerSession() ? soloModeDoomPressureMultiplier(mode) : 1;
  }

  private grooveChargeMultiplier(mode: SoloMode = this.runSoloMode()): number {
    return this.isSingleplayerSession() ? soloModeGrooveChargeMultiplier(mode) : 1;
  }

  private setSelectedSoloMode(mode: SoloMode): void {
    this.selectedSoloMode = mode;
    this.latestRunSoloMode = mode;
    this.hasContinue = this.soloModeContinueAvailability[mode] && this.soloModeLockState(mode).unlocked;
  }

  private setSelectedSoloLeaderboardMode(mode: SoloMode): void {
    if (this.selectedSoloLeaderboardMode === mode) {
      return;
    }
    this.selectedSoloLeaderboardMode = mode;
    if (this.phase === "LEADERBOARD" || this.isPhoneHighscoresVisible()) {
      void this.refreshLeaderboard();
    }
  }

  private currentSoloStageGoal(): SoloStageGoal | null {
    return this.run?.soloStageGoal ?? null;
  }

  private resolveRefillTargetColors(): number[] | undefined {
    const goal = this.currentSoloStageGoal();
    if (goal == null || goal.mode !== "color-hunt") {
      return undefined;
    }
    return [...goal.targetColors];
  }

  private currentSoloGoalProgress(): number {
    const goal = this.currentSoloStageGoal();
    if (goal == null) {
      return 0;
    }
    if (goal.mode === "clearout") {
      if (this.run == null) {
        return 0;
      }
      return Math.max(0, Math.min(goal.target, goal.target - countOccupiedCells(this.run.board)));
    }
    return Math.max(0, Math.min(goal.target, goal.progress));
  }

  private isRowRushRun(mode: SoloMode = this.runSoloMode()): boolean {
    return this.isSingleplayerSession() && mode === "row-rush";
  }

  private currentRowRushGoal(): Extract<SoloStageGoal, { mode: "row-rush" }> | null {
    const goal = this.currentSoloStageGoal();
    return goal?.mode === "row-rush" ? goal : null;
  }

  private createRowRushRoundState(roundStartScore: number = this.run?.totalScore ?? 0): NonNullable<RunState["rowRushRound"]> {
    return {
      roundStartScore: Math.max(0, Math.floor(roundStartScore)),
      lockedRotation: null,
      lastRoundScore: 0,
      lastRoundPerfect: false,
      lastRoundRotation: null
    };
  }

  private ensureRowRushRoundState(): NonNullable<RunState["rowRushRound"]> | null {
    if (this.run == null || !this.isRowRushRun()) {
      return null;
    }
    const existing = this.run.rowRushRound;
    if (existing != null) {
      existing.roundStartScore = Math.max(0, Math.floor(existing.roundStartScore ?? this.run.totalScore));
      existing.lockedRotation = existing.lockedRotation === "RIGHT" ? "RIGHT" : existing.lockedRotation === "LEFT" ? "LEFT" : null;
      existing.lastRoundScore = Math.max(0, Math.floor(existing.lastRoundScore ?? 0));
      existing.lastRoundPerfect = existing.lastRoundPerfect === true;
      existing.lastRoundRotation =
        existing.lastRoundRotation === "RIGHT" ? "RIGHT" : existing.lastRoundRotation === "LEFT" ? "LEFT" : null;
      return existing;
    }
    const created = this.createRowRushRoundState(this.run.totalScore);
    this.run.rowRushRound = created;
    return created;
  }

  private resetRowRushRoundState(roundStartScore: number = this.run?.totalScore ?? 0): void {
    if (this.run == null || !this.isRowRushRun()) {
      return;
    }
    this.run.rowRushRound = this.createRowRushRoundState(roundStartScore);
    this.pendingRotation = null;
  }

  private clearRowRushRoundSummary(): void {
    if (this.run == null || !this.isRowRushRun()) {
      return;
    }
    const round = this.ensureRowRushRoundState();
    if (round == null) {
      return;
    }
    round.lastRoundScore = 0;
    round.lastRoundPerfect = false;
    round.lastRoundRotation = null;
  }

  private normalizeRowRushContinuationState(): void {
    if (this.run == null || !this.isRowRushRun()) {
      return;
    }
    const goal = this.currentRowRushGoal();
    const round = this.ensureRowRushRoundState();
    if (goal == null || round == null) {
      return;
    }
    goal.scoredTargetCellKeys = [...new Set(goal.scoredTargetCellKeys ?? [])].slice(0, goal.target);
    goal.progress = Math.max(goal.progress, goal.scoredTargetCellKeys.length);
    round.roundStartScore = Math.max(0, Math.min(this.run.totalScore, round.roundStartScore));
    this.pendingRotation = round.lockedRotation;
  }

  private rowRushRoundRotationChoice(): RotationDir | null {
    return this.ensureRowRushRoundState()?.lockedRotation ?? null;
  }

  private resolveRowRushRotationChoice(): RotationDir {
    if (this.rng == null) {
      return "LEFT";
    }
    return this.rowRushRoundRotationChoice() ?? (this.rng.nextInt(2) === 0 ? "LEFT" : "RIGHT");
  }

  private transformRowRushScoreEvents(scoreEvents: ResolveScoreEvent[]): ResolveScoreEvent[] {
    if (!this.isRowRushRun() || scoreEvents.length === 0) {
      return scoreEvents;
    }
    const goal = this.currentRowRushGoal();
    if (goal == null) {
      return [];
    }
    const targetRows = new Set(goal.targetRows);
    const transformed: ResolveScoreEvent[] = [];
    for (const event of scoreEvents) {
      if (event.rowRushTargetHits != null) {
        if ((event.rowRushTargetHits ?? 0) <= 0) {
          continue;
        }
        transformed.push(event);
        continue;
      }
      const targetCellKeys = [...new Set((event.scoredCellKeys ?? []).filter((key) => {
        const [rowRaw, colRaw] = key.split(",");
        const row = Number(rowRaw);
        const col = Number(colRaw);
        return Number.isInteger(row)
          && Number.isInteger(col)
          && targetRows.has(row)
          && col >= 0
          && col < (this.run?.board.size ?? 0);
      }))];
      const targetHits = targetCellKeys.length;
      if (targetHits <= 0) {
        continue;
      }
      transformed.push({
        ...event,
        points: event.points * Math.min(10, targetHits),
        rowRushTargetHits: targetHits,
        rowRushTargetCellKeys: targetCellKeys
      });
    }
    return transformed;
  }

  private transformResolveResultForCurrentMode(out: ResolveResult): ResolveResult {
    if (!this.isRowRushRun()) {
      return out;
    }
    const scoreEvents = this.transformRowRushScoreEvents(out.scoreEvents);
    return {
      ...out,
      scoreEvents,
      scoreGained: scoreEvents.reduce((total, event) => total + event.points, 0)
    };
  }

  private applyRowRushCoverageFromScoreEvents(scoreEvents: ResolveScoreEvent[]): void {
    if (!this.isRowRushRun() || this.run == null) {
      return;
    }
    const goal = this.currentRowRushGoal();
    if (goal == null) {
      return;
    }
    const covered = new Set(goal.scoredTargetCellKeys ?? []);
    for (const event of scoreEvents) {
      for (const key of event.rowRushTargetCellKeys ?? []) {
        covered.add(key);
      }
    }
    goal.scoredTargetCellKeys = [...covered].slice(0, goal.target);
    goal.progress = Math.max(goal.progress, goal.scoredTargetCellKeys.length);
  }

  private rowRushPerfectRoundEarned(): boolean {
    const goal = this.currentRowRushGoal();
    return goal != null && goal.target > 0 && goal.progress >= goal.target;
  }

  private flushRowRushPendingComboScore(): void {
    if (!this.isRowRushRun() || this.run == null || this.pendingComboPoints <= 0) {
      this.resetComboState();
      return;
    }
    const scoreGained = this.pendingComboPoints;
    this.resetComboState();
    this.commitScoreToRun(scoreGained);
  }

  private beginSoloStart(mode: SoloMode, options?: { forceTutorial?: boolean }): void {
    if (!this.soloModeLockState(mode).unlocked) {
      this.denyLockedSoloMode(mode);
      return;
    }
    const shouldForceTutorial = options?.forceTutorial === true;
    const tutorialRunPending = soloModeTutorialEligible(mode) && (!hasTutorialBeenSeen() || shouldForceTutorial);
    this.setSelectedSoloMode(mode);
    if (!tutorialRunPending && !staminaService.canPlay()) {
      this.pendingGameplayIntent = { kind: "solo_start", mode };
      if (this.onQuickAdForStamina != null) {
        this.onQuickAdForStamina();
      } else {
        this.fallbackPendingStaminaIntentToGate();
      }
      return;
    }
    this.setPausedState(false);
    this.forceTutorial = shouldForceTutorial;
    this.startNewRun(mode);
  }

  private beginSoloContinue(mode: SoloMode): void {
    if (!this.soloModeLockState(mode).unlocked) {
      this.denyLockedSoloMode(mode);
      return;
    }
    this.setSelectedSoloMode(mode);
    const tutorialContinue = loadRunStateContext(mode)?.tutorialRun === true;
    if (!tutorialContinue && !staminaService.canPlay()) {
      this.pendingGameplayIntent = { kind: "solo_continue", mode };
      if (this.onQuickAdForStamina != null) {
        this.onQuickAdForStamina();
      } else {
        this.fallbackPendingStaminaIntentToGate();
      }
      return;
    }
    this.setPausedState(false);
    if (!this.continueRun(mode)) {
      this.phase = "SOLO_SELECT";
    }
  }

  private applySoloStageProgress(telemetry: SoloStageTelemetry): void {
    if (this.run == null || !this.isSingleplayerSession()) {
      return;
    }
    const mode = this.run.soloMode ?? "classic";
    if (this.isClassicSoloMode(mode)) {
      return;
    }
    const goal = this.currentSoloStageGoal();
    if (goal == null) {
      return;
    }
    if (goal.mode === "row-rush") {
      return;
    }
    if (goal.mode === "color-hunt") {
      const gained = goal.targetColors.reduce(
        (total, color) => total + Math.max(0, telemetry.clearedColors[String(color)] ?? 0),
        0
      );
      goal.progress = Math.max(0, Math.min(goal.target, goal.progress + gained));
      return;
    }
    goal.progress = Math.max(0, Math.min(goal.target, goal.target - countOccupiedCells(this.run.board)));
  }

  private queueObjectiveProgress(telemetry: ObjectiveTelemetry): void {
    if (!hasObjectiveTelemetryValue(telemetry)) {
      return;
    }
    this.pendingObjectiveTelemetry = mergeObjectiveTelemetry(this.pendingObjectiveTelemetry, telemetry);
  }

  private queueSoloStageProgress(telemetry: SoloStageTelemetry): void {
    if (this.isRowRushRun()) {
      return;
    }
    if (!hasSoloStageTelemetryValue(telemetry) && this.runSoloMode() !== "clearout") {
      return;
    }
    this.pendingSoloStageTelemetry = mergeSoloStageTelemetry(this.pendingSoloStageTelemetry, telemetry);
  }

  private flushPendingObjectiveProgress(): void {
    if (this.pendingObjectiveTelemetry != null) {
      const telemetry = this.pendingObjectiveTelemetry;
      this.pendingObjectiveTelemetry = null;
      this.applyObjectiveProgress(telemetry);
    }
    if (this.pendingSoloStageTelemetry != null) {
      const telemetry = this.pendingSoloStageTelemetry;
      this.pendingSoloStageTelemetry = null;
      this.applySoloStageProgress(telemetry);
    }
  }

  private hasPendingResolveWork(): boolean {
    return this.deferredCollapseSteps.length > 0
      || this.pendingScoreBatches.length > 0
      || this.pendingPostResolveChecks;
  }

  private hasActiveVisualAnimation(): boolean {
    return Boolean(this.rotationAnimState?.active || this.collapseAnimState?.active || this.swapAnimState?.active);
  }

  private isAnimating(): boolean {
    return this.hasActiveVisualAnimation();
  }

  private shouldFreezeDoomClock(): boolean {
    return Boolean(
      this.run?.groove.active
      || this.hasActiveVisualAnimation()
      || this.doomFrozenByPowerupAnim
      || this.isDeadBoardSequenceActive()
    );
  }

  private isDeadBoardReshuffleStep(step: CollapseAnimStep | null | undefined): boolean {
    return step?.specialFx === "RESHUFFLE";
  }

  private isDeadBoardSequenceActive(): boolean {
    const activeStep = this.collapseAnimState?.steps[this.collapseAnimState.stepIndex];
    if (this.isDeadBoardReshuffleStep(activeStep)) {
      return true;
    }
    return this.deferredCollapseSteps.some((step) => this.isDeadBoardReshuffleStep(step));
  }

  private shouldPauseRotationCountdown(): boolean {
    return this.hasActiveVisualAnimation() || this.isPowerupBusy();
  }

  private isResolveSequenceBusy(): boolean {
    return this.hasActiveVisualAnimation() || this.isPowerupBusy() || this.hasPendingResolveWork();
  }

  /** Public check for whether the controller is mid-animation or resolving. */
  isBusy(): boolean {
    return this.isResolveSequenceBusy();
  }

  /** Versus sync hook: replace run state and clear transient animation/input buffers. */
  forceSyncRunSnapshot(snapshot: RunState): void {
    const wasGameOver = this.phase === "RUN_GAMEOVER";
    const clampedDoomClockSec = clampDoomClockSecForLevel(snapshot.level, snapshot.doomClockSec);
    const isDeadSnapshot = clampedDoomClockSec <= 0;
    this.run = {
      ...snapshot,
      doomClockSec: clampedDoomClockSec,
      lives: Math.ceil(clampedDoomClockSec / 20),
      nextExtraLifeScore: Infinity,
      nextKeyScore:
        (Math.floor(Math.max(0, snapshot.totalScore) / KEY_EARN_STEP) + 1) * KEY_EARN_STEP,
      rotationKeys: this.clampRotationKeysForMode(snapshot.rotationKeys, snapshot.soloMode ?? "classic"),
      levelAdvanceBaseScore: normalizeLevelAdvanceBaseScore(snapshot.levelScore, snapshot.levelAdvanceBaseScore),
      board: cloneBoard(snapshot.board),
      groove: normalizeGrooveState(snapshot.groove),
      beat: { ...snapshot.beat },
      cashBalance: Math.max(0, Math.floor(snapshot.cashBalance ?? 0)),
      cashTilesClearedTotal: Math.max(0, Math.floor(snapshot.cashTilesClearedTotal ?? 0)),
      powerupInventory: normalizePowerupInventory(snapshot.powerupInventory),
      level20BountyCompletions: normalizeLevel20BountyCompletions(snapshot.level20BountyCompletions)
    };
    this.syncProgressionStateForCurrentLevel({ preserveExisting: true });
    this.initializeLevel20RewardProgress();
    this.grooveReadyCuePending = false;
    this.setPausedState(false);
    this.phase = isDeadSnapshot ? "RUN_GAMEOVER" : "RUN_PLAYABLE";
    this.pendingRotation = this.run.soloMode === "row-rush"
      ? this.run.rowRushRound?.lockedRotation ?? null
      : "LEFT";
    this.warningActive = false;
    this.levelUpTimer = 0;
    this.newColorIndex = null;
    this.message = "";
    this.messageTimer = 0;
    this.displayScore = this.run.totalScore;
    this.currentChainDepth = 0;
    this.idleFramesSinceChainUpdate = 0;
    this.hypeText = "";
    this.hypeTimer = 0;
    this.freezeTimer = 0;
    this.noClearElapsedSec = 0;
    this.hintActive = false;
    this.hintCells = [];
    this.tutorialState = null;
    this.currentRunIsTutorial = false;
    this.soloStaminaSpentForCurrentRun = false;
    this.pendingGameplayIntent = null;
    this.resetTransientAnimations();
    this.resetGrooveTelemetry();
    if (isDeadSnapshot) {
      this.gameOverElapsedSec = wasGameOver ? this.gameOverElapsedSec : 0;
      this.flashColor = "255,80,80";
      if (!wasGameOver) {
        this.pulseFx(0.5, 6.0);
        void this.audio.playSfx("game_over");
      }
      this.resetGrooveStateToIdle();
      return;
    }
    if (this.run.groove.active) {
      this.grooveCyclesRemaining = 1;
      this.grooveAnchorLevel = this.run.level;
      this.grooveFallbackRemainingSec = Math.max(0, this.run.groove.activeRemainingSec);
      this.grooveLastAudioRemainingSec = null;
      this.run.groove.activeRemainingSec = this.grooveFallbackRemainingSec;
    } else {
      this.resetGrooveRuntimeState();
    }
  }

  /** Versus trap hook: fully reset groove and cancel an active groove run immediately. */
  applyGrooveDrainTrap(): void {
    if (this.run == null) {
      return;
    }
    const wasActive = this.run.groove.active;
    this.resetGrooveStateToIdle();
    if (wasActive) {
      this.setMessage("GROOVE DRAINED", STATE_MACHINE_TUNING.continueMessageDurationSec);
    }
    this.syncMusicIntentForRun();
    this.saveIfRunning();
  }

  private startRotationAnimation(rotation: RotationDir, fromBoard: RunState["board"], rotatedBoard: RunState["board"]): void {
    const durationMultiplier = this.animationDurationMultiplier();
    this.rotationAnimState = {
      active: true,
      dir: rotation,
      phase: "grid_spin",
      elapsedSec: 0,
      phaseDurationSec: phaseDuration("grid_spin", durationMultiplier),
      zoom: 1,
      boardAngleRad: 0,
      tileCounterAngleRad: 0,
      fromBoard: cloneBoard(fromBoard),
      rotatedBoard: cloneBoard(rotatedBoard)
    };
  }

  private startCollapseAnimation(steps: CollapseAnimStep[]): void {
    if (steps.length === 0) {
      this.flushPendingScoreBatches();
      this.runPendingPostResolveChecks();
      return;
    }
    const first = steps[0];
    const durationMultiplier = this.animationDurationMultiplier();
    this.collapseAnimState = {
      active: true,
      stepIndex: 0,
      elapsedSec: 0,
      durationSec: collapseDurationForStep(first, durationMultiplier),
      steps: [...steps]
    };
    this.playCollapseStepSfx(first, 0);
    if (first.tier >= 4) {
      this.pulseFx(
        first.tier === 5
          ? STATE_MACHINE_TUNING.collapseAnimation.firstStepTier5Flash
          : STATE_MACHINE_TUNING.collapseAnimation.firstStepTier4Flash,
        first.tier === 5
          ? STATE_MACHINE_TUNING.collapseAnimation.firstStepTier5Shake
          : STATE_MACHINE_TUNING.collapseAnimation.firstStepTier4Shake
      );
    }
    // Color flash to match first cleared tile
    if (first.clearedCells.length > 0 && this.run != null) {
      const cell = first.clearedCells[0];
      const tile = this.run.board.cells[cell.row]?.[cell.col]?.tile;
      if (tile != null) {
        this.flashColor = FX_TILE_RGB[tile.color % FX_TILE_RGB.length];
      }
    }
  }

  private playCollapseStepSfx(step: CollapseAnimStep, stepIndex: number): void {
    if (step.specialFx === "RESHUFFLE") {
      return;
    }
    if (step.sweepImpactAudio != null) {
      const semitoneOffset = step.sweepImpactAudio.sfx === "row_ripper"
        ? step.sweepImpactAudio.impactIndex
        : -step.sweepImpactAudio.impactIndex;
      void this.audio.playSfx(step.sweepImpactAudio.sfx, {
        pitchMultiplier: semitoneMultiplier(semitoneOffset)
      });
      return;
    }

    const pitchMultiplier = stepIndex === 0
      ? 1
      : Math.min(2.0, 1 + stepIndex * STATE_MACHINE_TUNING.collapseAnimation.cascadePitchStep);
    void this.audio.playSfx("cascade_step", { pitchMultiplier });
  }

  private enqueueCollapseSteps(steps: CollapseAnimStep[]): void {
    if (steps.length === 0) {
      return;
    }
    if (this.phase === "RUN_GAMEOVER") {
      return;
    }
    if (this.swapAnimState?.active || this.rotationAnimState?.active) {
      this.deferredCollapseSteps.push(...steps);
      return;
    }
    if (this.collapseAnimState?.active) {
      this.collapseAnimState.steps.push(...steps);
      return;
    }
    this.startCollapseAnimation(steps);
  }

  private flushDeferredCollapse(): void {
    if (
      this.deferredCollapseSteps.length === 0
      || this.swapAnimState?.active
      || this.rotationAnimState?.active
      || this.collapseAnimState?.active
    ) {
      this.runPendingPostResolveChecks();
      return;
    }
    const queued = [...this.deferredCollapseSteps];
    this.deferredCollapseSteps.length = 0;
    this.startCollapseAnimation(queued);
  }

  private advanceRotationAnimation(dt: number): void {
    if (this.rotationAnimState == null) {
      return;
    }

    const anim = this.rotationAnimState;
    const dirSign = anim.dir === "RIGHT" ? 1 : -1;
    const durationMultiplier = this.animationDurationMultiplier();

    anim.elapsedSec += dt;
    const rawPhaseProgress = anim.phaseDurationSec <= 0 ? 1 : anim.elapsedSec / anim.phaseDurationSec;
    const phaseProgress = clamp01(rawPhaseProgress);
    const spinSec = phaseDuration("grid_spin", durationMultiplier);
    const holdSec = phaseDuration("sideways_hold", durationMultiplier);
    const recoverSec = phaseDuration("tile_recover", durationMultiplier);
    const totalRotationSec = spinSec + holdSec + recoverSec;
    let elapsedAcrossPhases = 0;
    if (anim.phase === "grid_spin") {
      elapsedAcrossPhases = Math.min(anim.elapsedSec, spinSec);
    } else if (anim.phase === "sideways_hold") {
      elapsedAcrossPhases = spinSec + Math.min(anim.elapsedSec, holdSec);
    } else {
      elapsedAcrossPhases = spinSec + holdSec + Math.min(anim.elapsedSec, recoverSec);
    }
    const rotationProgress = clamp01(totalRotationSec <= 0 ? 1 : elapsedAcrossPhases / totalRotationSec);
    const zoomSpan = Math.max(0, STATE_MACHINE_TUNING.rotationAnimation.zoomPeakScale - 1);
    anim.zoom = 1 + zoomSpan * Math.sin(Math.PI * rotationProgress);

    if (anim.phase === "grid_spin") {
      anim.boardAngleRad = dirSign * HALF_PI * easeInOutSine(phaseProgress);
      anim.tileCounterAngleRad = 0;
    } else if (anim.phase === "sideways_hold") {
      anim.boardAngleRad = dirSign * HALF_PI;
      anim.tileCounterAngleRad = 0;
    } else {
      const eased = easeInOutSine(phaseProgress);
      anim.boardAngleRad = 0;
      anim.tileCounterAngleRad = dirSign * HALF_PI * (1 - eased);
    }

    if (rawPhaseProgress < 1) {
      return;
    }

    if (anim.phase === "grid_spin") {
      anim.phase = "sideways_hold";
      anim.elapsedSec = 0;
      anim.phaseDurationSec = phaseDuration("sideways_hold", durationMultiplier);
      return;
    }

    if (anim.phase === "sideways_hold") {
      anim.phase = "tile_recover";
      anim.elapsedSec = 0;
      anim.phaseDurationSec = phaseDuration("tile_recover", durationMultiplier);
      anim.boardAngleRad = 0;
      anim.tileCounterAngleRad = dirSign * HALF_PI;
      return;
    }

    this.rotationAnimState = null;
    if (this.pendingClearoutRotationSettle && this.run != null && this.rng != null && this.runSoloMode() === "clearout") {
      this.pendingClearoutRotationSettle = false;
      this.applyResolveOutcome(resolvePostRotationSettle({
        board: this.run.board,
        level: this.levelConfig(),
        rng: this.rng,
        groove: this.run.groove,
        beat: this.run.beat,
        nowSec: this.run.beat.songTimeSec,
        refillEnabled: false,
        refillTargetColors: this.resolveRefillTargetColors()
      }));
    } else {
      this.pendingClearoutRotationSettle = false;
    }
    this.flushDeferredCollapse();
  }

  private advanceCollapseAnimation(dt: number): void {
    if (this.collapseAnimState == null) {
      return;
    }

    const anim = this.collapseAnimState;
    anim.elapsedSec += dt;
    if (anim.elapsedSec < anim.durationSec) {
      return;
    }

    // Carry over dt overshoot so the next step starts mid-progress instead of
    // resetting to 0 — prevents micro-stutters and block teleporting on frame spikes.
    const overshoot = Math.max(0, anim.elapsedSec - anim.durationSec);

    // Per-step juice: flash, shake, vibrate scaled by tier + chain depth
    const completedStep = anim.steps[anim.stepIndex];
    if (completedStep != null && completedStep.specialFx !== "RESHUFFLE") {
      const tier = completedStep.tier;
      const stepDepth = anim.stepIndex;
      // Landing thump with pitch based on how deep in the chain
      void this.audio.playSfx("collapse_land", {
        pitchMultiplier:
          STATE_MACHINE_TUNING.collapseAnimation.collapseLandPitchBase
          + stepDepth * STATE_MACHINE_TUNING.collapseAnimation.collapseLandPitchStep
      });
      // Tier-scaled juice
      if (tier === 5) {
        this.pulseFx(
          STATE_MACHINE_TUNING.collapsePulseByTier.tier5FlashBase + stepDepth * STATE_MACHINE_TUNING.collapsePulseByTier.flashPerDepth,
          STATE_MACHINE_TUNING.collapsePulseByTier.tier5ShakeBase + stepDepth * STATE_MACHINE_TUNING.collapsePulseByTier.shakePerDepth
        );
        this.freezeTimer = Math.max(
          this.freezeTimer,
          STATE_MACHINE_TUNING.chainFreeze.chain3PlusSec * this.animationDurationMultiplier()
        );
      } else if (tier === 4) {
        this.pulseFx(
          STATE_MACHINE_TUNING.collapsePulseByTier.tier4FlashBase + stepDepth * STATE_MACHINE_TUNING.collapsePulseByTier.flashPerDepth,
          STATE_MACHINE_TUNING.collapsePulseByTier.tier4ShakeBase + stepDepth * STATE_MACHINE_TUNING.collapsePulseByTier.shakePerDepth
        );
      } else {
        this.pulseFx(
          STATE_MACHINE_TUNING.collapsePulseByTier.tier3FlashBase + stepDepth * (STATE_MACHINE_TUNING.collapsePulseByTier.flashPerDepth / 1.5),
          STATE_MACHINE_TUNING.collapsePulseByTier.tier3ShakeBase + stepDepth * (STATE_MACHINE_TUNING.collapsePulseByTier.shakePerDepth / 1.5)
        );
      }
    }

    const completedChainIndex = completedStep?.chainIndex ?? 0;

    anim.stepIndex += 1;
    if (anim.stepIndex >= anim.steps.length) {
      this.applyQueuedScoreBatchesThroughChain(completedChainIndex);
      this.flushPendingScoreBatches();
      this.collapseAnimState = null;
      this.flushDeferredCollapse();
      this.runPendingPostResolveChecks();
      return;
    }

    const nextStep = anim.steps[anim.stepIndex];
    this.playCollapseStepSfx(nextStep, anim.stepIndex);

    // Color the flash to match the tiles being cleared
    if (nextStep != null && nextStep.clearedCells.length > 0 && this.run != null) {
      const firstCleared = nextStep.clearedCells[0];
      const tile = this.run.board.cells[firstCleared.row]?.[firstCleared.col]?.tile;
      if (tile != null) {
        this.flashColor = FX_TILE_RGB[tile.color % FX_TILE_RGB.length];
      }
    }

    if (nextStep?.chainIndex !== completedChainIndex) {
      this.applyQueuedScoreBatchesThroughChain(completedChainIndex);
      this.flushPendingObjectiveProgress();
    }

    // Carry overshoot into next step so animation stays smooth across frame spikes
    anim.durationSec = collapseDurationForStep(nextStep, this.animationDurationMultiplier());
    anim.elapsedSec = Math.min(overshoot, anim.durationSec * 0.5); // cap to avoid skipping next step too
  }

  private advanceSwapAnimation(dt: number): void {
    if (this.swapAnimState == null) return;
    this.swapAnimState.elapsedSec += dt;
    if (this.swapAnimState.elapsedSec >= this.swapAnimState.durationSec) {
      this.swapAnimState = null;
      this.flushDeferredCollapse();
    }
  }

  private advanceAnimations(dt: number): void {
    if (this.swapAnimState?.active) {
      this.advanceSwapAnimation(dt);
      return;
    }
    if (this.rotationAnimState?.active) {
      this.advanceRotationAnimation(dt);
      return;
    }
    if (this.collapseAnimState?.active) {
      this.advanceCollapseAnimation(dt);
    }
  }

  private queueResolveScoring(
    scoreEvents: ResolveScoreEvent[],
    options?: {
      commitMode?: PendingScoreBatch["commitMode"];
      chainDepth?: number;
    }
  ): void {
    if (scoreEvents.length === 0) {
      return;
    }

    const commitMode = options?.commitMode ?? "immediate";
    const chainDepth = Math.max(
      0,
      Math.floor(options?.chainDepth ?? scoreEvents.reduce((maxDepth, event) => Math.max(maxDepth, event.chainIndex + 1), 0))
    );
    const grooveMultiplier = this.run?.groove.multiplier ?? 1;
    const queuedBatches: PendingScoreBatch[] = [];
    let currentChainIndex: number | null = null;
    let currentBatch: PendingScoreBatch | null = null;
    for (const event of scoreEvents) {
      if (currentChainIndex == null || event.chainIndex !== currentChainIndex || currentBatch == null) {
        if (currentBatch != null) {
          queuedBatches.push(currentBatch);
        }
        currentChainIndex = event.chainIndex;
        currentBatch = {
          points: event.points,
          chainDepth,
          chainIndex: event.chainIndex,
          scoreEvents: [event],
          commitMode,
          context: {
            grooveMultiplier,
            resolveRawPointsBeforeBatch: 0
          }
        };
      } else {
        currentBatch.points += event.points;
        currentBatch.scoreEvents.push(event);
      }
    }
    if (currentBatch != null) {
      queuedBatches.push(currentBatch);
    }

    let resolveRawPointsBeforeBatch = 0;
    for (const batch of queuedBatches) {
      batch.context.resolveRawPointsBeforeBatch = resolveRawPointsBeforeBatch;
      resolveRawPointsBeforeBatch += batch.points;
      this.pendingScoreBatches.push(batch);
    }
  }

  private chargeGrooveByFraction(charge: number): void {
    if (this.run == null || this.run.groove.active) {
      return;
    }
    if (charge <= 0) {
      return;
    }
    const groove = this.run.groove;
    const wasFull = groove.groove >= 1;
    groove.groove = clamp01(groove.groove + charge * this.grooveChargeMultiplier());
    if (!wasFull && groove.groove >= 1) {
      groove.groove = 1;
      groove.armed = true;
      groove.readyWindowSec = GROOVE_READY_WINDOW_SEC;
      this.grooveReadyCuePending = this.isSingleplayerSession();
    } else if (groove.armed) {
      groove.groove = 1;
    }
  }

  private chargeGrooveFromTimeReward(timeReward: number): void {
    const charge = timeReward / (DOOM_CLOCK_GROOVE_CHARGE_BASELINE_SEC * GROOVE_CHARGE_DIVISOR);
    this.chargeGrooveByFraction(charge);
  }

  private currentDoomClockMaxSec(): number {
    if (this.isRowRushRun()) {
      return ROW_RUSH_ROUND_TIMER_SEC;
    }
    return doomClockCapForLevel(this.run?.level ?? 1);
  }

  private clampDoomClockToCurrentCap(): void {
    if (this.run == null) {
      return;
    }
    this.run.doomClockSec = clampDoomClockSecForLevel(this.run.level, this.run.doomClockSec);
    this.run.lives = Math.ceil(this.run.doomClockSec / 20);
  }

  private commitScoreToRun(scoreGained: number, options?: { applyLevel20Milestones?: boolean }): void {
    if (this.run == null || scoreGained <= 0) {
      return;
    }

    this.run.levelScore += scoreGained;
    this.run.totalScore += scoreGained;
    this.displayScore = this.run.totalScore;
    if (options?.applyLevel20Milestones !== false) {
      this.applyLevel20RewardMilestones();
    }

    const milestoneStep = 1000;
    if (Math.floor(this.run.totalScore / milestoneStep) > Math.floor(this.lastMilestoneScore / milestoneStep)) {
      void this.audio.playSfx("score_milestone");
    }
    this.lastMilestoneScore = this.run.totalScore;

    if (!this.isRowRushRun()) {
      while (this.run.totalScore >= this.run.nextKeyScore) {
        if (this.addRotationKeys(1)) {
          void this.audio.playSfx("key_earned");
        }
        this.run.nextKeyScore += KEY_EARN_STEP;
      }
    }
  }

  private registerDeferredComboScore(scoreGained: number, scoreEvents: ResolveScoreEvent[]): void {
    if (scoreGained <= 0 || scoreEvents.length === 0) {
      return;
    }
    this.pendingComboCount += 1;
    this.comboTimerRemainingSec = COMBO_EXTENSION_WINDOW_SEC;
    this.comboPausedUntilSettle = true;
  }

  private resumeComboWindowAfterSettle(): void {
    if (!this.comboPausedUntilSettle || this.run == null) {
      return;
    }
    if (
      this.phase !== "RUN_PLAYABLE"
      || this.isAnimating()
      || this.isPowerupBusy()
      || this.pendingScoreBatches.length > 0
      || this.pendingPostResolveChecks
    ) {
      return;
    }
    this.comboPausedUntilSettle = false;
  }

  private commitPendingComboScore(options?: { allowLevelUp?: boolean }): void {
    if (this.run == null) {
      this.resetComboState();
      return;
    }
    if (this.pendingComboPoints <= 0) {
      this.resetComboState();
      return;
    }

    const scoreGained = this.pendingComboPoints;
    const allowLevelUp = options?.allowLevelUp !== false;
    this.resetComboState();
    this.commitScoreToRun(scoreGained);

    if (allowLevelUp) {
      const levelUpSteps = this.maybeLevelUp();
      if (levelUpSteps.length > 0 || this.pendingScoreBatches.length > 0) {
        this.pendingPostResolveChecks = true;
      }
      this.enqueueCollapseSteps(levelUpSteps);
    }

    this.saveIfRunning();
  }

  private advanceComboWindow(dt: number): void {
    if (
      this.run == null
      || this.paused
      || this.phase === "RUN_GAMEOVER"
      || this.pendingComboCount <= 0
      || this.comboPausedUntilSettle
      || this.isDeadBoardSequenceActive()
      || dt <= 0
    ) {
      return;
    }
    const nextRemainingSec = this.comboTimerRemainingSec - dt;
    if (nextRemainingSec < 0) {
      this.comboTimerRemainingSec = 0;
      this.commitPendingComboScore();
      return;
    }
    // Keep the streak alive through the exact deadline and expire only once time runs past it.
    this.comboTimerRemainingSec = nextRemainingSec;
  }

  private injectRandomSpecialTile(special: "BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE"): boolean {
    if (this.run == null || this.rng == null) {
      return false;
    }
    const plainCandidates: Position[] = [];
    const fallbackCandidates: Position[] = [];
    const board = this.run.board;
    for (let row = 0; row < board.size; row += 1) {
      for (let col = 0; col < board.size; col += 1) {
        const cell = board.cells[row]?.[col];
        if (cell?.kind !== "TILE" || cell.tile == null) {
          continue;
        }
        fallbackCandidates.push({ row, col });
        if (cell.tile.special === "NONE") {
          plainCandidates.push({ row, col });
        }
      }
    }
    const candidates = plainCandidates.length > 0 ? plainCandidates : fallbackCandidates;
    if (candidates.length === 0) {
      return false;
    }
    const picked = candidates[this.rng.nextInt(candidates.length)];
    if (picked == null) {
      return false;
    }
    const tile = board.cells[picked.row]?.[picked.col]?.tile;
    if (tile == null) {
      return false;
    }
    tile.special = special;
    return true;
  }

  private injectRandomWipeGem(): boolean {
    return this.injectRandomSpecialTile("WIPE");
  }

  private injectRandomPowerupTile(): boolean {
    const special = POWERUP_INJECTION_SPECIALS[this.rng?.nextInt(POWERUP_INJECTION_SPECIALS.length) ?? 0] ?? "BOMB_3";
    return this.injectRandomSpecialTile(special);
  }

  private guaranteedWipeCountForLevel(level: number): number {
    const clampedLevel = Math.max(1, Math.floor(level));
    if (clampedLevel >= 20) {
      return 4;
    }
    if (clampedLevel >= 15) {
      return 3;
    }
    if (clampedLevel >= 10) {
      return 2;
    }
    return 1;
  }

  private injectGuaranteedWipesForLevel(level: number): void {
    const wipesToInject = this.guaranteedWipeCountForLevel(level);
    for (let i = 0; i < wipesToInject; i += 1) {
      this.injectRandomWipeGem();
    }
  }

  private injectLevel20RewardBundle(): void {
    const bundle = this.run?.soloMode === "classic"
      ? CLASSIC_LEVEL20_REWARD_BUNDLE
      : DEFAULT_LEVEL20_REWARD_BUNDLE;
    for (const reward of bundle) {
      for (let i = 0; i < reward.count; i += 1) {
        this.injectRandomSpecialTile(reward.special);
      }
    }
  }

  private applyLevel20RewardMilestones(): void {
    if (this.run == null || this.run.level !== MAX_LEVEL || this.isRowRushRun()) {
      return;
    }
    while (this.run.levelScore >= this.nextLevel20RewardScore) {
      this.injectLevel20RewardBundle();
      this.nextLevel20RewardScore += LEVEL20_REWARD_SCORE_STEP;
    }
  }

  private rolloverColorHuntLevel20Series(): boolean {
    if (
      this.run == null
      || this.rng == null
      || !this.isSingleplayerSession()
      || this.runSoloMode() !== "color-hunt"
      || this.run.level !== MAX_LEVEL
    ) {
      return false;
    }

    const goal = this.currentSoloStageGoal();
    if (goal == null || goal.mode !== "color-hunt" || goal.progress < goal.target) {
      return false;
    }

    const nextSeriesIndex = Math.max(0, Math.floor(goal.seriesIndex ?? 0)) + 1;
    this.commitScoreToRun(COLOR_HUNT_LEVEL20_SERIES_BONUS, { applyLevel20Milestones: false });
    this.run.soloStageGoal = createColorHuntStageGoal(this.run.level, this.levelConfig().tileTypes, this.rng, {
      seriesIndex: nextSeriesIndex,
      previousTargetColors: goal.targetColors
    });
    this.setMessage(`SERIES CLEAR! +${COLOR_HUNT_LEVEL20_SERIES_BONUS.toLocaleString()}`, STATE_MACHINE_TUNING.continueMessageDurationSec);
    this.pulseFx(
      STATE_MACHINE_TUNING.pulseFx.levelUpFlash,
      STATE_MACHINE_TUNING.pulseFx.levelUpShake
    );
    void this.audio.playSfx("level_up");
    return true;
  }

  private maybeInjectPowerupBoost(chainDepth: number): void {
    if (this.run == null) {
      return;
    }
    if (this.run.level < 10 || this.run.level >= 20) {
      return;
    }
    if (chainDepth < 4) {
      return;
    }
    this.injectRandomPowerupTile();
  }

  private maybeInjectWipeFromChainDepth(chainDepth: number): void {
    if (chainDepth < 10) {
      return;
    }
    this.injectRandomWipeGem();
  }

  private applyGrooveIdleDecay(dt: number): void {
    if (this.run == null || dt <= 0) {
      return;
    }
    if (this.phase !== "RUN_PLAYABLE") {
      return;
    }
    const groove = this.run.groove;
    if (groove.active) {
      return;
    }
    if (this.isAnimating() || this.pendingScoreBatches.length > 0 || this.pendingPostResolveChecks) {
      return;
    }
    if (groove.armed) {
      // Keep groove pinned at full during the ready window.
      groove.groove = 1;
      groove.multiplier = 1;
      return;
    }
    groove.groove = Math.max(0, groove.groove - GROOVE_IDLE_DECAY_PER_SEC * dt);
    if (groove.groove < 1) {
      groove.armed = false;
      groove.readyWindowSec = 0;
    }
    groove.multiplier = 1;
  }

  private updateGrooveReadyWindow(dt: number): void {
    if (this.run == null || dt <= 0) {
      return;
    }
    if (this.isDeadBoardSequenceActive()) {
      return;
    }
    if (this.phase !== "RUN_PLAYABLE") {
      return;
    }
    if (this.isAnimating() || this.isPowerupBusy()) {
      return;
    }
    if (this.pendingScoreBatches.length > 0 || this.pendingPostResolveChecks) {
      return;
    }
    const groove = this.run.groove;
    if (groove.active || !groove.armed) {
      return;
    }
    groove.readyWindowSec = Math.max(0, groove.readyWindowSec - dt);
    if (groove.readyWindowSec > 0) {
      return;
    }
    groove.readyWindowSec = 0;
    groove.armed = false;
    this.grooveReadyCuePending = false;
  }

  private applyScoreBatch(
    scoreGained: number,
    chainDepth = 0,
    scoreEvents: ResolveScoreEvent[] = [],
    commitMode: PendingScoreBatch["commitMode"] = "immediate",
    batchContext?: QueuedScoreBatchContext
  ): void {
    if (this.run == null) return;

    const effectiveScoreEvents = this.transformRowRushScoreEvents(scoreEvents);
    const effectiveScoreGained = this.isRowRushRun()
      ? effectiveScoreEvents.reduce((total, event) => total + event.points, 0)
      : scoreGained;
    const finalScoreGained = batchContext == null
      ? effectiveScoreGained
      : resolveBatchPoints(effectiveScoreGained, chainDepth, batchContext);
    const doomClockBeforeBatch = this.run.doomClockSec;
    if (commitMode === "immediate") {
      this.commitScoreToRun(finalScoreGained);
    }

    const popupAnchor = effectiveScoreEvents[0]?.anchor ?? { row: 5, col: 5 };
    if (finalScoreGained > 0) {
      this.lastScorePopup = {
        points: finalScoreGained,
        row: popupAnchor.row,
        col: popupAnchor.col,
        multiplier: batchContext?.grooveMultiplier ?? this.run.groove.multiplier
      };
    }

    if (effectiveScoreEvents.length > 0) {
      this.applyRowRushCoverageFromScoreEvents(effectiveScoreEvents);
    }

    // Doom clock time rewards for matches (skip during groove — groove IS the reward)
    if (effectiveScoreEvents.length > 0) {
      this.noClearElapsedSec = 0;
      this.hintActive = false;
      this.hintCells = [];

      if (!this.run.groove.active && !this.isRowRushRun()) {
        // Count match groups in this batch (each scoreEvent = 1 match group)
        const matchGroupCount = effectiveScoreEvents.length;
        let timeReward = matchGroupCount * DOOM_CLOCK_MATCH_REWARD_SEC;

        // Chain depth bonus
        if (chainDepth > 1) {
          timeReward += (chainDepth - 1) * DOOM_CLOCK_CHAIN_BONUS_SEC;
        }

        const specialDoomBonus = effectiveScoreEvents.reduce((total, event) => total + (event.doomBonusSec ?? 0), 0);
        const specialGrooveBonus = effectiveScoreEvents.reduce((total, event) => total + (event.grooveBonusFrac ?? 0), 0);
        timeReward += specialDoomBonus;
        this.doomClockTimePopup = { amount: timeReward, beatSync: false, anchor: popupAnchor };

        this.run.doomClockSec = Math.min(this.currentDoomClockMaxSec(), this.run.doomClockSec + timeReward);
        this.run.lives = Math.ceil(this.run.doomClockSec / 20);
        this.chargeGrooveFromTimeReward(timeReward);
        this.chargeGrooveByFraction(specialGrooveBonus);
      }
    }

    this.currentChainDepth = chainDepth;
    this.idleFramesSinceChainUpdate = 0;

    const feedbackMode = this.resolveFeedbackMode();
    const scoreSignificance = this.scoreSignificanceForMode(feedbackMode);
    const nowSec = this.run.beat.songTimeSec;

    if (chainDepth >= 4) {
      this.maybeSetCadencedHypeCue("INCREDIBLE!", STATE_MACHINE_TUNING.hypeDurationsSec.incredible, 4, nowSec, feedbackMode);
    } else if (chainDepth >= 3) {
      this.maybeSetCadencedHypeCue("AMAZING!", STATE_MACHINE_TUNING.hypeDurationsSec.amazing, 3, nowSec, feedbackMode);
    } else if (chainDepth >= 2) {
      this.maybeSetCadencedHypeCue("GREAT!", STATE_MACHINE_TUNING.hypeDurationsSec.great, 2, nowSec, feedbackMode);
    } else if (finalScoreGained >= scoreSignificance) {
      this.maybeSetCadencedHypeCue("NICE!", STATE_MACHINE_TUNING.hypeDurationsSec.nice, 1, nowSec, feedbackMode);
    }

    if (this.sessionMode === "versus") {
      const clutchThreshold = feedbackMode === "full"
        ? STATE_MACHINE_TUNING.versusFeedback.clutchSwingScore.full
        : feedbackMode === "reduced"
          ? STATE_MACHINE_TUNING.versusFeedback.clutchSwingScore.reduced
          : STATE_MACHINE_TUNING.versusFeedback.clutchSwingScore.off;
      const isClutch = doomClockBeforeBatch <= STATE_MACHINE_TUNING.versusFeedback.clutchDoomThresholdSec
        && finalScoreGained >= clutchThreshold;
      if (isClutch && nowSec - this.lastClutchCueAtSec >= STATE_MACHINE_TUNING.versusFeedback.clutchCooldownSec) {
        this.lastClutchCueAtSec = nowSec;
        this.maybeSetCadencedHypeCue("CLUTCH SWING!", STATE_MACHINE_TUNING.hypeDurationsSec.clutch, 5, nowSec, feedbackMode, true);
      }

      const payoffThreshold = feedbackMode === "full"
        ? STATE_MACHINE_TUNING.versusFeedback.payoffScore.full
        : feedbackMode === "reduced"
          ? STATE_MACHINE_TUNING.versusFeedback.payoffScore.reduced
          : STATE_MACHINE_TUNING.versusFeedback.payoffScore.off;
      const isPayoff = finalScoreGained >= payoffThreshold && (chainDepth >= 3 || effectiveScoreEvents.length >= 2);
      if (isPayoff && nowSec - this.lastPayoffCueAtSec >= STATE_MACHINE_TUNING.versusFeedback.payoffCooldownSec) {
        this.lastPayoffCueAtSec = nowSec;
        this.maybeSetCadencedHypeCue("PAYOFF!", STATE_MACHINE_TUNING.hypeDurationsSec.payoff, 6, nowSec, feedbackMode, true);
      }
    }

    if (this.isSingleplayerSession()) {
      if (this.grooveReadyCuePending && this.run.groove.armed && !this.run.groove.active) {
        this.grooveReadyCuePending = false;
      } else if (!this.run.groove.armed || this.run.groove.active) {
        this.grooveReadyCuePending = false;
      }
    }

  }

  private applyCashProgress(scoreEvents: ResolveScoreEvent[], bonusCash = 0): void {
    if (this.run == null || (scoreEvents.length === 0 && bonusCash <= 0)) {
      return;
    }
    let cashGain = 0;
    let awardedMatches = 0;
    for (const event of scoreEvents) {
      const gain = cashForScoreEvent(event);
      if (gain <= 0) {
        continue;
      }
      cashGain += gain;
      awardedMatches += 1;
    }
    if (awardedMatches > 0) {
      // Keep this as a monotonic progression counter for persisted run stats.
      this.run.cashTilesClearedTotal += awardedMatches;
    }
    const totalCashGain = cashGain + bonusCash;
    if (totalCashGain <= 0) {
      return;
    }
    this.run.cashBalance += totalCashGain;
    this.cashPopupAmount += totalCashGain;
    this.cashDenyFlashSec = 0;
    void this.audio.playSfx("coin_gain");
  }

  private flushCashPopup(): void {
    if (this.cashPopupAmount <= 0) {
      return;
    }
    this.cashPopupAmount = 0;
  }

  private powerupKindFromAction(action: UiAction): PowerupKind | null {
    if (action === "ARM_POWERUP_HINT") return "HINT";
    if (action === "ARM_POWERUP_ROWRIP") return "ROWRIP";
    if (action === "ARM_POWERUP_COLCUT") return "COLCUT";
    if (action === "ARM_POWERUP_TRANSMUTE") return "TRANSMUTE";
    if (action === "ARM_POWERUP_ZIGZAGBOLT") return "ZIGZAGBOLT";
    return null;
  }

  private clearArmedPowerupState(): void {
    this.armedPowerup = null;
    this.hintFirstPick = null;
  }

  private clearStoredBoardTaps(): void {
    this.lastBoardTap = null;
    this.lastGestureBoardTap = null;
  }

  private clearQueuedSwapBuffer(): void {
    this.queuedSwaps.length = 0;
    this.deferredResolveSwap = null;
  }

  private clearLiveDeferredSwap(): void {
    this.deferredResolveSwap = null;
  }

  private clearBufferedBoardInteractions(options?: { clearQueuedSwaps?: boolean }): void {
    const preserveQueuedSwaps = options?.clearQueuedSwaps === false && this.swapBufferMode === "replay_fifo";
    if (!preserveQueuedSwaps) {
      this.clearQueuedSwapBuffer();
    } else {
      this.clearLiveDeferredSwap();
    }
    this.clearStoredBoardTaps();
  }

  private shouldBufferBusySwap(): boolean {
    if (this.isDeadBoardSequenceActive()) {
      return false;
    }
    if (this.swapBufferMode === "replay_fifo") {
      return true;
    }
    return this.phase === "RUN_PLAYABLE"
      && !this.isPowerupBusy()
      && (
        this.swapAnimState?.active === true
        || this.collapseAnimState?.active === true
        || this.pendingScoreBatches.length > 0
        || this.pendingPostResolveChecks
      );
  }

  private isPowerupBusy(): boolean {
    return this.armedPowerup != null || this.powerupExecuting;
  }

  private isBoardInteractionReady(): boolean {
    return this.phase === "RUN_PLAYABLE"
      && !this.isAnimating()
      && !this.isPowerupBusy()
      && this.pendingScoreBatches.length === 0
      && !this.pendingPostResolveChecks;
  }

  private showPowerupArmMessage(kind: PowerupKind): void {
    const messages: Record<PowerupKind, string> = {
      HINT: "SHOWS MATCH HINTS",
      ROWRIP: "TAP A ROW TO CLEAR",
      COLCUT: "TAP A COLUMN TO CLEAR",
      TRANSMUTE: "TAP A TILE TO UPGRADE",
      ZIGZAGBOLT: "TAP TO ZAP A ZIGZAG PATH"
    };
    this.setMessage(messages[kind], STATE_MACHINE_TUNING.continueMessageDurationSec);
  }

  private applyPowerupDoomBonus(kind: PowerupKind): void {
    if (this.run == null) {
      return;
    }
    if (this.isRowRushRun()) {
      return;
    }
    if (kind !== "ROWRIP" && kind !== "COLCUT" && kind !== "ZIGZAGBOLT") {
      return;
    }
    this.run.doomClockSec = Math.min(
      this.currentDoomClockMaxSec(),
      this.run.doomClockSec + DOOM_CLOCK_POWERUP_SWEEP_BONUS_SEC
    );
    this.run.lives = Math.ceil(this.run.doomClockSec / 20);
    this.doomClockTimePopup = {
      amount: DOOM_CLOCK_POWERUP_SWEEP_BONUS_SEC,
      beatSync: false,
      anchor: { row: 5, col: 5 }
    };
    this.chargeGrooveFromTimeReward(DOOM_CLOCK_POWERUP_SWEEP_BONUS_SEC);
  }

  private tryBuyAndArmPowerup(kind: PowerupKind): void {
    if (this.run == null || this.phase !== "RUN_PLAYABLE") {
      return;
    }
    const inventory = this.run.powerupInventory;
    const owned = inventory[kind];
    if (owned > 0) {
      this.armedPowerup = kind;
      this.hintFirstPick = null;
      this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
      void this.audio.playSfx("powerup_arm");
      this.showPowerupArmMessage(kind);
      return;
    }
    const cost = POWERUP_COSTS[kind];
    if (this.run.cashBalance < cost) {
      this.cashDenyFlashSec = 0.5;
      this.setMessage(`NEED $${cost}`, STATE_MACHINE_TUNING.continueMessageDurationSec);
      void this.audio.playSfx("shop_deny");
      this.clearArmedPowerupState();
      return;
    }
    this.run.cashBalance -= cost;
    inventory[kind] += 1;
    this.armedPowerup = kind;
    this.hintFirstPick = null;
    this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
    void this.audio.playSfx("powerup_arm");
    this.showPowerupArmMessage(kind);
  }

  private tryCastHintPowerup(): void {
    if (this.run == null || this.phase !== "RUN_PLAYABLE") {
      return;
    }
    if (
      this.isAnimating()
      || this.powerupExecuting
      || this.pendingScoreBatches.length > 0
      || this.pendingPostResolveChecks
    ) {
      return;
    }

    this.clearArmedPowerupState();
    const hintCells = findHintCells(this.run.board, { includeSpecialSwaps: false });
    if (hintCells.length <= 0) {
      this.setMessage("NO HINTS", STATE_MACHINE_TUNING.continueMessageDurationSec);
      return;
    }

    const owned = this.run.powerupInventory.HINT;
    if (owned > 0) {
      this.run.powerupInventory.HINT = owned - 1;
    } else {
      const cost = POWERUP_COSTS.HINT;
      if (this.run.cashBalance < cost) {
        this.cashDenyFlashSec = 0.5;
        this.setMessage(`NEED $${cost}`, STATE_MACHINE_TUNING.continueMessageDurationSec);
        void this.audio.playSfx("shop_deny");
        return;
      }
      this.run.cashBalance -= cost;
    }

    this.hintCastCells = hintCells;
    this.hintCastRemainingSec = HINT_CAST_DURATION_SEC;
    this.hintActive = true;
    this.hintCells = [...hintCells];
    this.noClearElapsedSec = 0;

    void this.audio.playSfx("powerup_cast");
    this.onGameEvent?.("POWERUP_BUY", { kind: "HINT" });
    this.onGameEvent?.("POWERUP_USE", { kind: "HINT" });
    this.saveIfRunning();
  }

  private consumePowerup(kind: PowerupKind): boolean {
    if (this.run == null) {
      return false;
    }
    const owned = this.run.powerupInventory[kind];
    if (owned <= 0) {
      return false;
    }
    this.run.powerupInventory[kind] = owned - 1;
    return true;
  }

  private powerupResolveContext(preferredSpawn?: Position) {
    if (this.run == null || this.rng == null) {
      return null;
    }
    return {
      board: this.run.board,
      level: this.levelConfig(),
      rng: this.rng,
      groove: this.run.groove,
      beat: this.run.beat,
      nowSec: this.run.beat.songTimeSec,
      preferredSpawn,
      refillEnabled: this.runSoloMode() !== "clearout",
      refillTargetColors: this.resolveRefillTargetColors()
    };
  }

  private applyResolveOutcome(
    out: ResolveResult,
    sourcePowerup?: PowerupKind,
    comboEligible = false
  ): void {
    const effectiveOut = this.transformResolveResultForCurrentMode(out);
    this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
    const extendComboWindow = comboEligible && effectiveOut.scoreGained > 0 && effectiveOut.scoreEvents.length > 0;
    if (extendComboWindow) {
      this.registerDeferredComboScore(effectiveOut.scoreGained, effectiveOut.scoreEvents);
    }
    this.queueObjectiveProgress(effectiveOut.objectiveTelemetry ?? emptyObjectiveTelemetry());
    this.queueSoloStageProgress(effectiveOut.soloStageTelemetry ?? emptySoloStageTelemetry());
    this.queueResolveScoring(effectiveOut.scoreEvents, { chainDepth: effectiveOut.chainDepth });
    this.applyCashProgress(
      effectiveOut.scoreEvents,
      interruptedLinearPowerupCompletion(effectiveOut, sourcePowerup) ? 0 : completionCashForPowerup(sourcePowerup)
    );
    this.maybeInjectWipeFromChainDepth(effectiveOut.chainDepth);
    this.maybeInjectPowerupBoost(effectiveOut.chainDepth);
    if (!hapticManager.isGestureBoundPlatform()) {
      const bombHaptic = toBombHapticType(effectiveOut.haptics.strongestBomb);
      if (bombHaptic != null) {
        hapticManager.triggerBomb(bombHaptic);
        const bombExplosionStepCount = effectiveOut.visualSteps.reduce((count, step) => (
          step.specialFx === "BOMB_EXPLOSION" ? count + 1 : count
        ), 0);
        if (bombExplosionStepCount > 1) {
          hapticManager.triggerBombChainAftershocks(bombHaptic, bombExplosionStepCount);
        }
      } else if (effectiveOut.haptics.wipeActivated) {
        hapticManager.triggerWipe();
      }
    }
    if (!hapticManager.isGestureBoundPlatform()) {
      if (sourcePowerup === "ROWRIP" && effectiveOut.visualSteps.some((step) => step.specialFx === "POWERUP_ROW_SWEEP")) {
        hapticManager.triggerRowRipSweep();
      }
      if (sourcePowerup === "COLCUT" && effectiveOut.visualSteps.some((step) => step.specialFx === "POWERUP_COL_SWEEP")) {
        hapticManager.triggerColCutSweep();
      }
      if (
        sourcePowerup === "ZIGZAGBOLT"
        && effectiveOut.visualSteps.some((step) => step.specialFx === "POWERUP_ZIGZAG" || step.specialFx === "BOMB_EXPLOSION")
      ) {
        hapticManager.triggerZigzagBurst();
      }
    }
    if (
      sourcePowerup != null
      && (sourcePowerup === "ROWRIP" || sourcePowerup === "COLCUT" || sourcePowerup === "ZIGZAGBOLT")
      && effectiveOut.visualSteps.length > 0
    ) {
      this.doomFrozenByPowerupAnim = true;
    }
    if (effectiveOut.crateBreakCount > 0) {
      void this.audio.playSfx("crate_break");
    }
    this.pendingPostResolveChecks = true;
    this.enqueueCollapseSteps(effectiveOut.visualSteps);
  }

  handleGestureBoardClickImmediate(click: Position): void {
    if (!hapticManager.isGestureBoundPlatform() || this.run == null) {
      return;
    }
    if (
      this.armedPowerup === "ROWRIP"
      || this.armedPowerup === "COLCUT"
      || this.armedPowerup === "ZIGZAGBOLT"
    ) {
      if (
        this.phase !== "RUN_PLAYABLE"
        || this.isAnimating()
        || this.powerupExecuting
        || this.pendingScoreBatches.length > 0
        || this.pendingPostResolveChecks
      ) {
        return;
      }
      const cell = this.run.board.cells[click.row]?.[click.col];
      if (cell == null || cell.kind === "EMPTY") {
        return;
      }
      if (this.run.powerupInventory[this.armedPowerup] <= 0) {
        return;
      }
      if (this.armedPowerup === "ROWRIP") {
        hapticManager.triggerRowRipSweepFromGesture();
      } else if (this.armedPowerup === "COLCUT") {
        hapticManager.triggerColCutSweepFromGesture();
      } else {
        hapticManager.triggerZigzagBurstFromGesture();
      }
      return;
    }

    if (!this.isSpecialTapReady()) {
      this.clearStoredBoardTaps();
      return;
    }

    if (!this.registerBoardTap(click, "gesture")) {
      return;
    }

    const cell = this.run.board.cells[click.row]?.[click.col];
    if (cell?.kind !== "TILE" || cell.tile == null) {
      return;
    }
    if (isBomb(cell.tile.special)) {
      hapticManager.triggerBombFromGesture(toBombHapticTypeFromSpecial(cell.tile.special));
      return;
    }
    if (isWipe(cell.tile.special)) {
      hapticManager.triggerWipeFromGesture();
    }
  }

  handleGestureSwapImmediate(move: MoveAttempt): void {
    if (!hapticManager.isGestureBoundPlatform() || this.run == null) {
      return;
    }
    const board = this.run.board;
    if (!canSwap(board, move.from, move.to)) {
      return;
    }
    const activation = activationFromSwap(board, move.from, move.to);
    const strongestBomb = strongestBombSeedHapticType(activation.bombSeeds);
    if (strongestBomb != null) {
      hapticManager.triggerBombFromGesture(strongestBomb);
      return;
    }
    if (
      this.phase !== "RUN_PLAYABLE"
      || this.isAnimating()
      || this.isPowerupBusy()
      || this.pendingScoreBatches.length > 0
      || this.pendingPostResolveChecks
    ) {
      return;
    }
    const previewBoard = cloneBoard(board);
    swapCells(previewBoard, move.from, move.to);
    const hasMatchAfterSwap = hasAnyMatch(previewBoard);
    const fromTile = board.cells[move.from.row]?.[move.from.col]?.tile;
    const wipeSelectedFirst = activation.wipe != null && fromTile?.special === "WIPE";
    const hasBombActivation = activation.bombSeeds.length > 0;
    const shouldResolveSpecialSwap = wipeSelectedFirst || (hasBombActivation && !hasMatchAfterSwap);
    if (!shouldResolveSpecialSwap) {
      return;
    }
    if (wipeSelectedFirst) {
      hapticManager.triggerWipeFromGesture();
    }
  }

  applySyncedSpecialTap(payload: { row: number; col: number; special: "BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE" }): boolean {
    return this.resolveSpecialTapAt(
      { row: payload.row, col: payload.col },
      { emitSyncEvent: false, expectedSpecial: payload.special }
    );
  }

  private registerBoardTap(click: Position, mode: "gesture" | "idle"): boolean {
    if (this.run == null || !this.isSpecialTapReady()) {
      this.clearStoredBoardTaps();
      return false;
    }
    const nowSec = this.run.beat.songTimeSec;
    const lastTap = mode === "gesture" ? this.lastGestureBoardTap : this.lastBoardTap;
    const isDoubleTap =
      lastTap != null
      && lastTap.row === click.row
      && lastTap.col === click.col
      && nowSec - lastTap.atSec <= DOUBLE_TAP_WINDOW_SEC;
    const nextTap = {
      row: click.row,
      col: click.col,
      atSec: nowSec
    };
    if (mode === "gesture") {
      this.lastGestureBoardTap = isDoubleTap ? null : nextTap;
    } else {
      this.lastBoardTap = isDoubleTap ? null : nextTap;
    }
    return isDoubleTap;
  }

  private isSpecialTapReady(): boolean {
    return this.isBoardInteractionReady();
  }

  private resolveSpecialTapAt(
    click: Position,
    options?: { emitSyncEvent?: boolean; expectedSpecial?: "BOMB_3" | "BOMB_4" | "BOMB_5" | "WIPE" }
  ): boolean {
    if (this.run == null || this.rng == null || !this.isSpecialTapReady()) {
      return false;
    }

    const cell = this.run.board.cells[click.row]?.[click.col];
    if (cell?.kind !== "TILE" || cell.tile == null) {
      return false;
    }
    const special = cell.tile.special;
    if ((!isBomb(special) && !isWipe(special)) || (options?.expectedSpecial != null && special !== options.expectedSpecial)) {
      return false;
    }

    const ctx = this.powerupResolveContext(click);
    if (ctx == null) {
      return false;
    }

    this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
    this.phase = "RUN_RESOLVING";
    const out = isBomb(special)
      ? resolveBombDetonationInPlace(ctx, click)
      : resolveWipeDetonationInPlace(ctx, click);
    this.phase = "RUN_PLAYABLE";

    if (out.totalTilesCleared <= 0 && out.crateBreakCount <= 0 && out.visualSteps.length === 0) {
      return false;
    }

    this.applyResolveOutcome(out);
    this.pulseFx(
      STATE_MACHINE_TUNING.pulseFx.specialSwapFlash,
      STATE_MACHINE_TUNING.pulseFx.specialSwapShake
    );
    this.freezeTimer = STATE_MACHINE_TUNING.chainFreeze.specialSwapSec * this.animationDurationMultiplier();
    void this.audio.playSfx(isBomb(special) ? bombSfxForSpecial(special) : "wipe_sweep");
    if (options?.emitSyncEvent === true) {
      this.onGameEvent?.("SPECIAL_TAP", {
        row: click.row,
        col: click.col,
        special
      });
    }
    this.saveIfRunning();
    return true;
  }

  private executeArmedPowerupOnBoardClick(click: Position): void {
    if (this.run == null || this.rng == null) {
      return;
    }
    if (this.armedPowerup == null || this.phase !== "RUN_PLAYABLE" || this.isAnimating() || this.powerupExecuting) {
      return;
    }
    const kind = this.armedPowerup;
    const ctx = this.powerupResolveContext(click);
    if (ctx == null) {
      return;
    }

    const cell = this.run.board.cells[click.row]?.[click.col];
    if (cell == null || cell.kind === "EMPTY") {
      return;
    }

    if (kind === "ROWRIP") {
      if (!this.consumePowerup(kind)) {
        this.clearArmedPowerupState();
        return;
      }
      this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
      this.applyPowerupDoomBonus(kind);
      this.powerupExecuting = true;
      void this.audio.playSfx("powerup_cast");
      const out = resolvePowerupRowRip(ctx, click.row);
      this.clearArmedPowerupState();
      this.powerupExecuting = false;
      this.applyResolveOutcome(out, kind);
      this.onGameEvent?.("POWERUP_USE", { kind, targetRow: click.row, targetCol: click.col });
      this.saveIfRunning();
      return;
    }

    if (kind === "COLCUT") {
      if (!this.consumePowerup(kind)) {
        this.clearArmedPowerupState();
        return;
      }
      this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
      this.applyPowerupDoomBonus(kind);
      this.powerupExecuting = true;
      void this.audio.playSfx("powerup_cast");
      const out = resolvePowerupColCut(ctx, click.col);
      this.clearArmedPowerupState();
      this.powerupExecuting = false;
      this.applyResolveOutcome(out, kind);
      this.onGameEvent?.("POWERUP_USE", { kind, targetRow: click.row, targetCol: click.col });
      this.saveIfRunning();
      return;
    }

    if (kind === "TRANSMUTE") {
      if (cell.kind !== "TILE" || cell.tile == null) {
        return;
      }
      if (!this.consumePowerup(kind)) {
        this.clearArmedPowerupState();
        return;
      }
      this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
      this.powerupExecuting = true;
      void this.audio.playSfx("transmute");
      const out = resolvePowerupTransmute(ctx, click);
      this.clearArmedPowerupState();
      this.powerupExecuting = false;
      this.applyResolveOutcome(out);
      this.onGameEvent?.("POWERUP_USE", { kind, targetRow: click.row, targetCol: click.col });
      this.saveIfRunning();
      return;
    }

    if (kind === "ZIGZAGBOLT") {
      if (!this.consumePowerup(kind)) {
        this.clearArmedPowerupState();
        return;
      }
      this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
      this.applyPowerupDoomBonus(kind);
      this.powerupExecuting = true;
      void this.audio.playSfx("powerup_cast");
      const out = resolvePowerupZigzag(ctx, click);
      this.clearArmedPowerupState();
      this.powerupExecuting = false;
      this.applyResolveOutcome(out, kind);
      this.onGameEvent?.("POWERUP_USE", { kind, targetRow: click.row, targetCol: click.col });
      this.saveIfRunning();
    }
  }

  private handleIdleBoardClick(click: Position): void {
    if (this.run == null || this.rng == null) {
      return;
    }
    if (!this.isSpecialTapReady()) {
      this.clearStoredBoardTaps();
      return;
    }
    if (!this.registerBoardTap(click, "idle")) {
      return;
    }
    this.resolveSpecialTapAt(click, { emitSyncEvent: true });
  }

  private applyNextQueuedScoreBatch(): void {
    const next = this.pendingScoreBatches.shift();
    if (next == null) {
      return;
    }
    this.applyScoreBatch(next.points, next.chainDepth, next.scoreEvents, next.commitMode, next.context);
  }

  private applyQueuedScoreBatchesThroughChain(chainIndex: number): void {
    while ((this.pendingScoreBatches[0]?.chainIndex ?? Number.POSITIVE_INFINITY) <= chainIndex) {
      this.applyNextQueuedScoreBatch();
    }
  }

  private flushPendingScoreBatches(): void {
    while (this.pendingScoreBatches.length > 0) {
      this.applyNextQueuedScoreBatch();
    }
  }

  private drainPendingScoreBatches(): void {
    this.flushPendingScoreBatches();
  }

  private collectQueuedSwaps(input: GameInput): void {
    while (true) {
      const move = input.consumeSwap();
      if (move == null) {
        break;
      }
      const bufferedMove = {
        from: { row: move.from.row, col: move.from.col },
        to: { row: move.to.row, col: move.to.col }
      };
      if (this.isBoardInteractionReady() || this.swapBufferMode === "replay_fifo") {
        this.queuedSwaps.push(bufferedMove);
        continue;
      }
      if (this.shouldBufferBusySwap()) {
        this.deferredResolveSwap = bufferedMove;
      }
    }
  }

  private applyQueuedSwapIfReady(): void {
    if (!this.isBoardInteractionReady()) {
      return;
    }
    const nextSwap = this.deferredResolveSwap ?? this.queuedSwaps.shift() ?? null;
    this.deferredResolveSwap = null;
    if (nextSwap == null) {
      return;
    }
    this.applySwap(nextSwap);
    if (this.tutorialState != null) {
      notifyTutorialSwap(this.tutorialState);
      if (this.onboardingState?.active) {
        const obStep = getCurrentStep(this.onboardingState);
        if (obStep?.tutorialStep === "SWAP") {
          advanceOnboardingStep(this.onboardingState);
        }
      }
    }
  }

  /** Force game state so the current tutorial step is achievable. */
  private prepTutorialStep(): void {
    if (this.tutorialState == null || this.run == null) return;
    const step = this.tutorialState.step;
    if (step === "GROOVE") {
      // Arm groove so the player can tap the meter
      if (!this.run.groove.armed && !this.run.groove.active) {
        this.run.groove.groove = 1;
        this.run.groove.armed = true;
        this.run.groove.readyWindowSec = 999;
        this.grooveReadyCuePending = this.isSingleplayerSession();
      }
    } else if (step === "ROTATE") {
      // Ensure at least 1 rotation key
      if (this.run.rotationKeys <= 0) {
        this.run.rotationKeys = 1;
      }
    } else if (step === "RADIAL") {
      // Give a powerup so the radial menu shows items
      if (this.run.powerupInventory.HINT <= 0 && this.run.cashBalance < POWERUP_COSTS.HINT) {
        this.run.powerupInventory.HINT = 1;
      }
    }
  }

  private resetChainDepthIfIdle(): void {
    if (this.currentChainDepth === 0) {
      this.idleFramesSinceChainUpdate = 0;
      return;
    }
    if (this.isAnimating()) {
      this.idleFramesSinceChainUpdate = 0;
      return;
    }
    if (this.pendingScoreBatches.length > 0 || this.pendingPostResolveChecks) {
      this.idleFramesSinceChainUpdate = 0;
      return;
    }
    this.idleFramesSinceChainUpdate += 1;
    if (this.idleFramesSinceChainUpdate < 2) {
      return;
    }
    this.currentChainDepth = 0;
    this.idleFramesSinceChainUpdate = 0;
  }

  private runPendingPostResolveChecks(): void {
    if (!this.pendingPostResolveChecks || this.run == null || this.rng == null) {
      return;
    }
    if (this.isAnimating() || this.pendingScoreBatches.length > 0) {
      return;
    }

    this.pendingPostResolveChecks = false;
    this.flushPendingObjectiveProgress();
    const skipNoMoveCheck = this.skipNoMoveCheckOnce || this.skipNoMoveCheckAfterReshuffleOnce;
    const noMoveSteps = skipNoMoveCheck ? [] : this.postSettleNoMoveCheck();
    this.skipNoMoveCheckOnce = false;
    this.skipNoMoveCheckAfterReshuffleOnce = false;
    if (this.phase === "RUN_GAMEOVER" || this.run.doomClockSec <= 0) {
      return;
    }
    if (noMoveSteps.length > 0) {
      this.pendingPostResolveChecks = true;
      this.enqueueCollapseSteps(noMoveSteps);
      this.saveIfRunning();
      return;
    }

    const levelUpSteps = this.maybeLevelUp();
    if (levelUpSteps.length > 0 || this.pendingScoreBatches.length > 0) {
      this.pendingPostResolveChecks = true;
    }
    this.enqueueCollapseSteps(levelUpSteps);
    this.saveIfRunning();
  }

  private enterGameOver(): void {
    if (this.run == null) {
      return;
    }
    this.commitPendingComboScore({ allowLevelUp: false });
    const soloMode = this.runSoloMode();
    const finalScore = this.run.totalScore;
    const finalLevel = this.run.level;
    const displayName = this.authState.profile?.displayName ?? "PLAYER";
    this.run.doomClockSec = Math.max(0, this.run.doomClockSec);
    this.run.lives = 0;
    this.phase = "RUN_GAMEOVER";
    this.setPausedState(false);
    this.gameOverElapsedSec = 0;
    this.gameOverStep = 0;
    this.gameOverStepElapsed = 0;
    this.leaderboardExpanded = false;
    this.levelUpTriggered = false;
    this.newlyUnlockedModes = [];
    this.currentChainDepth = 0;
    this.idleFramesSinceChainUpdate = 0;
    this.clearQueuedSwapBuffer();
    this.latestRunName = displayName;
    this.latestRunScore = finalScore;
    this.latestRunLevel = finalLevel;
    this.latestRunSoloMode = soloMode;
    this.latestRunRank = null;
    this.latestRunRankResolved = false;
    this.scoreSubmitFailed = false;
    this.flashColor = "255,80,80";
    this.pulseFx(0.5, 6.0);
    this.sfxAndHaptic("game_over", "game_over");
    this.resetGrooveStateToIdle();
    if (this.allowMusicIntentChanges) {
      this.audio.setMusicIntent(finalLevel, "VERSE");
    }
    if (
      this.isSingleplayerSession()
      && !this.currentRunIsTutorial
      && !this.soloStaminaSpentForCurrentRun
    ) {
      this.soloStaminaSpentForCurrentRun = true;
      void staminaService.consumeRound();
    }
    if (this.isSingleplayerSession()) {
      clearRunState(soloMode);
      this.refreshSoloContinueAvailability();
      this.prepareLeaderboardDisplay(soloMode);
      const syncLeaderboard = async (): Promise<void> => {
        await this.refreshLeaderboard(100, soloMode);
        this.latestRunRank = await fetchScoreRank(finalScore, soloMode);
        this.latestRunRankResolved = true;
        if (this.latestRunRank === 1) {
          void this.audio.playSfx("new_highscore");
        }
      };
      const userId = this.authState.user?.id ?? "guest-local";
      if (this.authState.user?.id != null) {
        if (networkManager.isOnline()) {
          void clearRunStateCloud(userId, soloMode);
        }
      }
      if (networkManager.isOnline()) {
        void submitScore(userId, displayName, finalScore, finalLevel, soloMode)
          .then((result) => {
            if (result === "error") {
              this.scoreSubmitFailed = true;
            }
          })
          .finally(() => {
            void syncLeaderboard();
          });
      } else {
        // Still refresh cached leaderboard so the game over screen renders
        void syncLeaderboard();
      }
    }
    this.resetTransientAnimations();
  }

  private postSettleNoMoveCheck(): CollapseAnimStep[] {
    if (this.run == null || this.rng == null) return [];
    const levelCfg = this.levelConfig();
    if (this.runSoloMode() === "clearout" && countOccupiedCells(this.run.board) === 0) {
      return [];
    }
    if (this.runSoloMode() === "clearout") {
      return [];
    }
    if (hasValidMove(this.run.board)) {
      return [];
    }
    const durationOverrideSec = DEAD_BOARD_RESHUFFLE_STEP_SEC / this.animationDurationMultiplier();
    const plannedReshuffle = planDeadBoardReshuffle(this.run.board, levelCfg, this.rng, {
      durationOverrideSec
    });
    this.clearBufferedBoardInteractions();
    this.skipNoMoveCheckAfterReshuffleOnce = !plannedReshuffle.playable;
    this.run.board = plannedReshuffle.board;
    return plannedReshuffle.steps;
  }

  private maybeLevelUp(): CollapseAnimStep[] {
    if (this.run == null || this.rng == null) return [];
    if (this.reseedEndlessClearoutLevel20()) {
      return [];
    }
    if (this.rolloverColorHuntLevel20Series()) {
      return [];
    }
    const steps: CollapseAnimStep[] = [];

    while (this.run.level < MAX_LEVEL) {
      const currentCfg = this.levelConfig();
      const soloMode = this.run.soloMode ?? "classic";
      const soloGoal = this.currentSoloStageGoal();
      const previousRowRushTargetRows = soloGoal?.mode === "row-rush" ? [...soloGoal.targetRows] : undefined;
      const previousColorHuntTargetColors = soloGoal?.mode === "color-hunt" ? [...soloGoal.targetColors] : undefined;
      const classicSingleplayer = this.isSingleplayerSession() && this.isClassicSoloMode(soloMode);
      const classicProgressSatisfied = this.currentClassicLevelScoreProgress() >= currentCfg.scoreTarget;
      const soloProgressSatisfied = !this.isClassicSoloMode(soloMode) && soloGoal != null && (
        soloGoal.mode === "clearout"
          ? countOccupiedCells(this.run.board) === 0
          : soloGoal.mode === "row-rush"
            ? false
          : soloGoal.progress >= soloGoal.target
      );
      const shouldAdvance = this.isSingleplayerSession() && !this.isClassicSoloMode(soloMode)
        ? soloProgressSatisfied
        : classicProgressSatisfied;
      if (!shouldAdvance) {
        break;
      }

      const missedObjectiveBonus = classicSingleplayer
        && this.run.level < MAX_LEVEL
        && this.run.currentObjective?.completed !== true;
      const prevTileTypes = currentCfg.tileTypes;
      if (!(this.isSingleplayerSession() && !this.isClassicSoloMode(soloMode))) {
        this.run.levelScore -= currentCfg.scoreTarget;
      }
      this.run.level += 1;
      this.clampRunRotationKeysForCurrentMode();
      this.clampDoomClockToCurrentCap();
      if (this.run.level === MAX_LEVEL) {
        this.applyLevel20RewardMilestones();
      }
      this.syncMusicIntentForRun();
      const nextCfg = this.levelConfig();
      setRotationInterval(this.run.beat, nextCfg.rotationEveryBeats);
      let settle: ResolveResult | null = null;
      if (this.isSingleplayerSession() && soloMode === "clearout") {
        this.run.board = this.createClearoutBoard(nextCfg);
        this.syncProgressionStateForCurrentLevel();
      } else {
        this.syncProgressionStateForCurrentLevel({
          previousRowRushTargetRows,
          previousColorHuntTargetColors
        });
        injectObstaclesForLevel(this.run.board, nextCfg, this.rng);
        if (!hasValidMove(this.run.board) || hasAnyMatch(this.run.board)) {
          guaranteedReshuffle(this.run.board, nextCfg, this.rng);
        }
        if (hasAnyMatch(this.run.board)) {
          settle = resolveCascades({
            board: this.run.board,
            level: nextCfg,
            rng: this.rng,
            groove: this.run.groove,
            beat: this.run.beat,
            nowSec: this.run.beat.songTimeSec,
            refillEnabled: this.runSoloMode() !== "clearout",
            refillTargetColors: this.resolveRefillTargetColors()
          });
        }
      }
      if (settle != null) {
        this.queueResolveScoring(settle.scoreEvents, { chainDepth: settle.chainDepth });
        this.applyCashProgress(settle.scoreEvents);
        this.maybeInjectWipeFromChainDepth(settle.chainDepth);
        this.maybeInjectPowerupBoost(settle.chainDepth);
        if (settle.crateBreakCount > 0) {
          void this.audio.playSfx("crate_break");
        }
      }
      this.injectGuaranteedWipesForLevel(this.run.level);
      if (this.run.level >= 10 && this.run.level <= 20) {
        this.injectRandomPowerupTile();
      }
      if (settle != null) {
        steps.push(...settle.visualSteps);
      }

      // Detect new tile color introduction
      const hasNewColor = nextCfg.tileTypes > prevTileTypes;
      this.newColorIndex = hasNewColor ? nextCfg.tileTypes - 1 : null;

      this.phase = "RUN_LEVELUP";
      this.levelUpTimer = hasNewColor
        ? STATE_MACHINE_TUNING.levelUpNewColorDurationSec
        : STATE_MACHINE_TUNING.levelUpPhaseDurationSec;
      if (missedObjectiveBonus) {
        this.setMessage("BONUS MISSED", STATE_MACHINE_TUNING.continueMessageDurationSec);
      }
      this.pulseFx(
        STATE_MACHINE_TUNING.pulseFx.levelUpFlash,
        STATE_MACHINE_TUNING.pulseFx.levelUpShake
      );
      this.sfxAndHaptic("level_up", "level_complete");
      if (hasNewColor) {
        setTimeout(() => void this.audio.playSfx("new_color"), 300);
      }
      if (classicSingleplayer) {
        break;
      }
    }

    return steps;
  }

  private prepareRowRushNextRound(previousTargetRows: number[] | undefined): CollapseAnimStep[] {
    if (this.run == null || this.rng == null || !this.isRowRushRun()) {
      return [];
    }
    const nextCfg = this.levelConfig();
    this.syncProgressionStateForCurrentLevel({ previousRowRushTargetRows: previousTargetRows });
    injectObstaclesForLevel(this.run.board, nextCfg, this.rng);
    if (!hasValidMove(this.run.board) || hasAnyMatch(this.run.board)) {
      guaranteedReshuffle(this.run.board, nextCfg, this.rng);
    }
    let settle: ResolveResult | null = null;
    if (hasAnyMatch(this.run.board)) {
      settle = resolveCascades({
        board: this.run.board,
        level: nextCfg,
        rng: this.rng,
        groove: this.run.groove,
        beat: this.run.beat,
        nowSec: this.run.beat.songTimeSec,
        refillEnabled: true,
        refillTargetColors: this.resolveRefillTargetColors()
      });
    }
    this.injectGuaranteedWipesForLevel(this.run.level);
    if (this.run.level >= 10 && this.run.level <= 20) {
      this.injectRandomPowerupTile();
    }
    return settle?.visualSteps ?? [];
  }

  private enterRowRushLevelInterstitial(
    roundScore: number,
    perfectRound: boolean,
    rotation: RotationDir,
    followUpSteps: CollapseAnimStep[]
  ): void {
    if (this.run == null || !this.isRowRushRun()) {
      return;
    }
    const roundState = this.ensureRowRushRoundState();
    if (roundState != null) {
      roundState.lastRoundScore = Math.max(0, Math.floor(roundScore));
      roundState.lastRoundPerfect = perfectRound;
      roundState.lastRoundRotation = rotation;
    }
    this.phase = "RUN_LEVELUP";
    this.levelUpTimer = ROW_RUSH_INTERSTITIAL_SEC;
    this.newColorIndex = null;
    this.executeRotation("row-rush-round-end", rotation);
    if (followUpSteps.length > 0) {
      this.enqueueCollapseSteps(followUpSteps);
    }
    if (perfectRound) {
      this.setMessage(`PERFECT ROUND! +${ROW_RUSH_PERFECT_BONUS.toLocaleString()}`, STATE_MACHINE_TUNING.continueMessageDurationSec);
    }
    this.pulseFx(
      STATE_MACHINE_TUNING.pulseFx.levelUpFlash,
      STATE_MACHINE_TUNING.pulseFx.levelUpShake
    );
    this.sfxAndHaptic("level_up", "level_complete");
  }

  private tryAdvanceRowRushRound(): void {
    if (
      this.run == null
      || this.rng == null
      || !this.isRowRushRun()
      || !this.rowRushRoundAdvancePending
    ) {
      return;
    }
    if (
      this.isAnimating()
      || this.isPowerupBusy()
      || this.pendingScoreBatches.length > 0
      || this.pendingPostResolveChecks
    ) {
      return;
    }

    this.rowRushRoundAdvancePending = false;
    this.flushRowRushPendingComboScore();

    const goal = this.currentRowRushGoal();
    const roundState = this.ensureRowRushRoundState();
    if (goal == null || roundState == null) {
      console.warn("[RowRush] tryAdvanceRowRushRound: goal or roundState is null — forcing advance to prevent stuck round");
      this.resetRowRushRoundState(this.run.totalScore);
      this.enterRowRushLevelInterstitial(0, false, "LEFT", []);
      this.saveIfRunning();
      return;
    }

    const perfectRound = this.rowRushPerfectRoundEarned();
    if (perfectRound) {
      this.commitScoreToRun(ROW_RUSH_PERFECT_BONUS, { applyLevel20Milestones: false });
    }
    const roundScore = Math.max(0, this.run.totalScore - roundState.roundStartScore);
    const rotation = this.resolveRowRushRotationChoice();
    const previousTargetRows = [...goal.targetRows];
    let followUpSteps: CollapseAnimStep[] = [];
    this.rowRushPendingGameOver = this.run.level >= MAX_LEVEL;

    if (!this.rowRushPendingGameOver) {
      this.run.level += 1;
      this.clampRunRotationKeysForCurrentMode();
      this.clampDoomClockToCurrentCap();
      this.syncMusicIntentForRun();
      const nextCfg = this.levelConfig();
      setRotationInterval(this.run.beat, nextCfg.rotationEveryBeats);
      followUpSteps = this.prepareRowRushNextRound(previousTargetRows);
      this.resetRowRushRoundState(this.run.totalScore);
      const nextRound = this.ensureRowRushRoundState();
      if (nextRound != null) {
        nextRound.lastRoundScore = roundScore;
        nextRound.lastRoundPerfect = perfectRound;
        nextRound.lastRoundRotation = rotation;
      }
      this.run.doomClockSec = ROW_RUSH_ROUND_TIMER_SEC;
      this.run.lives = Math.ceil(this.run.doomClockSec / 20);
    } else {
      roundState.lockedRotation = null;
    }

    this.enterRowRushLevelInterstitial(roundScore, perfectRound, rotation, followUpSteps);
    this.saveIfRunning();
  }

  private applySwap(move: { from: { row: number; col: number }; to: { row: number; col: number } }): void {
    if (this.run == null || this.rng == null) return;
    const board = this.run.board;

    // Capture tiles before any mutation for swap animation
    const fromTile = board.cells[move.from.row]?.[move.from.col]?.tile;
    const toTile = board.cells[move.to.row]?.[move.to.col]?.tile;

    if (!canSwap(board, move.from, move.to)) {
      // Invalid swap: animate rejection if both cells have tiles
      if (fromTile != null && toTile != null) {
        this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
        this.swapAnimState = {
          active: true,
          elapsedSec: 0,
          durationSec: STATE_MACHINE_TUNING.swapAnimation.rejectedSec * this.animationDurationMultiplier(),
          fromCell: move.from,
          toCell: move.to,
          fromTile: { ...fromTile },
          toTile: { ...toTile },
          rejected: true
        };
        void this.audio.playSfx("swap_reject");
      }
      return;
    }

    const activation = activationFromSwap(board, move.from, move.to);
    swapCells(board, move.from, move.to);
    this.onGameEvent?.("SWAP", {
      fromRow: move.from.row, fromCol: move.from.col,
      toRow: move.to.row, toCol: move.to.col
    });
    const hasMatchAfterSwap = hasAnyMatch(board);
    const wipeSelectedFirst = activation.wipe != null && fromTile?.special === "WIPE";
    const hasBombActivation = activation.bombSeeds.length > 0;
    const hasUsableSpecialActivation = wipeSelectedFirst || hasBombActivation;
    const shouldResolveSpecialSwap = hasUsableSpecialActivation && (wipeSelectedFirst || !hasMatchAfterSwap);

    if (shouldResolveSpecialSwap) {
      // Valid special swap — animate then resolve
      this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
      void this.audio.playSfx("swap_valid");
      if (fromTile != null && toTile != null) {
        this.swapAnimState = {
          active: true,
          elapsedSec: 0,
          durationSec: STATE_MACHINE_TUNING.swapAnimation.validSec * this.animationDurationMultiplier(),
          fromCell: move.from,
          toCell: move.to,
          fromTile: { ...fromTile },
          toTile: { ...toTile },
          rejected: false
        };
      }
      this.phase = "RUN_RESOLVING";
      const swappedSpecialTile = board.cells[move.to.row]?.[move.to.col]?.tile;
      if (swappedSpecialTile) {
        this.flashColor = FX_TILE_RGB[swappedSpecialTile.color % FX_TILE_RGB.length];
      }
      const out = resolveSwapByPriorityStages(
        {
          board,
          level: this.levelConfig(),
          rng: this.rng,
          groove: this.run.groove,
          beat: this.run.beat,
          nowSec: this.run.beat.songTimeSec,
          preferredSpawn: move.to,
          refillEnabled: this.runSoloMode() !== "clearout",
          refillTargetColors: this.resolveRefillTargetColors()
        },
        activation
      );
      this.phase = "RUN_PLAYABLE";
      this.pulseFx(
        STATE_MACHINE_TUNING.pulseFx.specialSwapFlash,
        STATE_MACHINE_TUNING.pulseFx.specialSwapShake
      );
      this.freezeTimer = STATE_MACHINE_TUNING.chainFreeze.specialSwapSec * this.animationDurationMultiplier();
      const bombSfx = strongestBombSfxForSeeds(activation.bombSeeds);
      void this.audio.playSfx(wipeSelectedFirst ? "wipe_sweep" : (bombSfx ?? "bomb_explode"));
      this.applyResolveOutcome(out);
      this.saveIfRunning();
      return;
    }

    if (!hasMatchAfterSwap) {
      // No match — undo swap, animate rejection
      swapCells(board, move.from, move.to);
      if (fromTile != null && toTile != null) {
        this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
        this.swapAnimState = {
          active: true,
          elapsedSec: 0,
          durationSec: STATE_MACHINE_TUNING.swapAnimation.rejectedSec * this.animationDurationMultiplier(),
          fromCell: move.from,
          toCell: move.to,
          fromTile: { ...fromTile },
          toTile: { ...toTile },
          rejected: true
        };
        void this.audio.playSfx("swap_reject");
      }
      return;
    }

    // Valid match swap — animate then resolve
    this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
    void this.audio.playSfx("swap_valid");
    if (fromTile != null && toTile != null) {
      this.swapAnimState = {
        active: true,
        elapsedSec: 0,
        durationSec: STATE_MACHINE_TUNING.swapAnimation.validSec * this.animationDurationMultiplier(),
        fromCell: move.from,
        toCell: move.to,
        fromTile: { ...fromTile },
        toTile: { ...toTile },
        rejected: false
      };
    }
    this.phase = "RUN_RESOLVING";
    const swappedMatchTile = board.cells[move.to.row]?.[move.to.col]?.tile;
    if (swappedMatchTile) {
      this.flashColor = FX_TILE_RGB[swappedMatchTile.color % FX_TILE_RGB.length];
    }
    const baseOut = resolveCascades({
      board,
      level: this.levelConfig(),
      rng: this.rng,
      groove: this.run.groove,
      beat: this.run.beat,
      nowSec: this.run.beat.songTimeSec,
      preferredSpawn: move.to,
      refillEnabled: this.runSoloMode() !== "clearout",
      refillTargetColors: this.resolveRefillTargetColors()
    });
    let out = baseOut;
    let hadDeferredBomb = false;
    let deferredBombSeeds: Array<{ special: BombSpecial }> = [];
    if (activation.bombSeeds.length > 0) {
      const activeBombSeeds: Array<{ center: Position; special: "BOMB_3" | "BOMB_4" | "BOMB_5" }> = [];
      for (const seed of activation.bombSeeds) {
        const cell = board.cells[seed.center.row]?.[seed.center.col];
        if (cell?.kind !== "TILE" || cell.tile == null || !isBomb(cell.tile.special)) {
          continue;
        }
        activeBombSeeds.push({
          center: { row: seed.center.row, col: seed.center.col },
          special: cell.tile.special
        });
      }
      if (activeBombSeeds.length > 0) {
        hadDeferredBomb = true;
        deferredBombSeeds = activeBombSeeds.map((seed) => ({ special: seed.special }));
        const deferredBombOut = resolveSwapByPriorityStages(
          {
            board,
            level: this.levelConfig(),
            rng: this.rng,
            groove: this.run.groove,
            beat: this.run.beat,
            nowSec: this.run.beat.songTimeSec,
            preferredSpawn: move.to,
            refillEnabled: this.runSoloMode() !== "clearout",
            refillTargetColors: this.resolveRefillTargetColors()
          },
          {
            triggered: true,
            wipe: null,
            bombSeeds: activeBombSeeds
          }
        );
        out = {
          ...out,
          groupsCleared: out.groupsCleared + deferredBombOut.groupsCleared,
          totalTilesCleared: out.totalTilesCleared + deferredBombOut.totalTilesCleared,
          coinEligibleClears: out.coinEligibleClears + deferredBombOut.coinEligibleClears,
          chainDepth: out.chainDepth + deferredBombOut.chainDepth,
          scoreGained: out.scoreGained + deferredBombOut.scoreGained,
          earnedKey: out.earnedKey || deferredBombOut.earnedKey,
          crateBreakCount: out.crateBreakCount + deferredBombOut.crateBreakCount,
          scoreEvents: [...out.scoreEvents, ...deferredBombOut.scoreEvents],
          visualSteps: [...out.visualSteps, ...deferredBombOut.visualSteps],
          objectiveTelemetry: mergeObjectiveTelemetry(out.objectiveTelemetry, deferredBombOut.objectiveTelemetry),
          soloStageTelemetry: mergeSoloStageTelemetry(out.soloStageTelemetry, deferredBombOut.soloStageTelemetry),
          haptics: {
            strongestBomb: strongerBomb(out.haptics.strongestBomb, deferredBombOut.haptics.strongestBomb),
            wipeActivated: out.haptics.wipeActivated || deferredBombOut.haptics.wipeActivated
          }
        };
      }
    }
    this.phase = "RUN_PLAYABLE";
    if (out.chainDepth > 1) {
      void this.audio.playSfx("cascade_chain", { pitchMultiplier: 1 + (out.chainDepth - 1) * 0.15 });
    } else {
      void this.audio.playSfx("match_clear");
    }
    if (hadDeferredBomb) {
      const bombSfx = strongestBombSfxForSeeds(deferredBombSeeds);
      void this.audio.playSfx(bombSfx ?? "bomb_explode");
      this.pulseFx(
        STATE_MACHINE_TUNING.pulseFx.specialSwapFlash,
        STATE_MACHINE_TUNING.pulseFx.specialSwapShake
      );
      this.freezeTimer = Math.max(
        this.freezeTimer,
        STATE_MACHINE_TUNING.chainFreeze.specialSwapSec * this.animationDurationMultiplier()
      );
    }
    if (out.chainDepth >= 3) {
      this.freezeTimer = Math.max(
        this.freezeTimer,
        STATE_MACHINE_TUNING.chainFreeze.chain3PlusSec * this.animationDurationMultiplier()
      );
    }
    this.applyResolveOutcome(out, undefined, true);
    this.saveIfRunning();
  }

  private executeRotation(
    source: "timer" | "manual" | "row-rush-round-end" = "timer",
    manualDirection: RotationDir = "LEFT"
  ): void {
    if (this.run == null || this.rng == null) return;
    this.noClearElapsedSec = 0;

    if (source === "manual" && this.isRowRushRun()) {
      const rowRushRound = this.ensureRowRushRoundState();
      if (rowRushRound == null || rowRushRound.lockedRotation != null) {
        return;
      }
      rowRushRound.lockedRotation = manualDirection;
      this.pendingRotation = manualDirection;
      this.saveIfRunning();
      return;
    }

    const board = this.run.board;
    const preBoard = cloneBoard(board);
    const rotation: RotationDir = source === "timer" ? "LEFT" : manualDirection;
    if (source === "manual") {
      if (this.run.groove.active && this.run.groove.freeRotationsRemaining > 0) {
        this.run.groove.freeRotationsRemaining -= 1;
      } else {
        if (this.run.rotationKeys <= 0) {
          // Message hidden — reject SFX is sufficient feedback
          void this.audio.playSfx("swap_reject");
          return;
        }
        this.run.rotationKeys -= 1;
      }
    }
    this.pendingRotation = rotation;

    this.clearBufferedBoardInteractions({ clearQueuedSwaps: false });
    rotateBoard(board, rotation);
    if (source === "manual") {
      this.onGameEvent?.("ROTATION", { dir: rotation });
    }
    const rotatedBoard = cloneBoard(board);

    this.pulseFx(
      STATE_MACHINE_TUNING.pulseFx.rotationFlash,
      STATE_MACHINE_TUNING.pulseFx.rotationShake
    );
    if (source === "manual") {
      void this.audio.playSfx(rotation === "RIGHT" ? "right_rotate" : "left_rotate");
    } else {
      void this.audio.playSfx("rotation");
    }
    // Rotation is a pure transform now; do not run post-resolve checks that can reshuffle.
    this.pendingPostResolveChecks = false;
    this.pendingClearoutRotationSettle = this.isSingleplayerSession() && this.runSoloMode() === "clearout";
    this.skipNoMoveCheckOnce = false;
    this.skipNoMoveCheckAfterReshuffleOnce = false;

    if (this.phase === "RUN_GAMEOVER") {
      this.pendingClearoutRotationSettle = false;
      return;
    }

    if (source === "manual") {
      const cycleSec = this.run.beat.rotationEveryBeats * this.run.beat.beatDurationSec;
      this.run.beat.timeToNextRotationSec = cycleSec;
      this.warningActive = false;
    }

    this.startRotationAnimation(rotation, preBoard, rotatedBoard);
    this.saveIfRunning();
  }

  private activateGrooveMode(): boolean {
    if (this.run == null) return false;
    if (this.run.groove.active) {
      return false;
    }
    if (!this.run.groove.armed) return false;
    if (this.run.groove.readyWindowSec <= 0) return false;
    this.run.groove.armed = false;
    this.run.groove.active = true;
    this.run.groove.readyWindowSec = 0;
    this.grooveReadyCuePending = false;
    this.grooveCyclesRemaining = 1;
    this.grooveAnchorLevel = this.run.level;
    this.grooveFallbackRemainingSec = GROOVE_MODE_DURATION_SEC;
    this.grooveLastAudioRemainingSec = null;
    this.run.groove.activeRemainingSec = GROOVE_MODE_DURATION_SEC;
    this.run.groove.groove = 1;
    this.run.groove.multiplier = GROOVE_MODE_MULTIPLIER;
    this.run.groove.freeRotationsRemaining = this.isRowRushRun() ? 0 : 5;
    this.syncMusicIntentForRun();
    this.syncGrooveRemainingFromMusicBoundary();
    this.hypeText = `GROOVE x${GROOVE_MODE_MULTIPLIER}!`;
    this.hypeTimer = STATE_MACHINE_TUNING.hypeDurationsSec.groove;
    this.setMessage(
      `GROOVE MODE ACTIVE x${GROOVE_MODE_MULTIPLIER}`,
      STATE_MACHINE_TUNING.continueMessageDurationSec
    );
    this.pulseFx(
      STATE_MACHINE_TUNING.pulseFx.specialSwapFlash,
      STATE_MACHINE_TUNING.pulseFx.specialSwapShake
    );
    void this.audio.playSfx("groove_milestone");
    this.saveIfRunning();
    return true;
  }

  private syncMusicIntentForRun(): void {
    if (this.run == null) {
      return;
    }
    if (!this.allowMusicIntentChanges) {
      return;
    }
    if (this.run.groove.active) {
      this.audio.setMusicIntent(this.grooveAnchorLevel ?? this.run.level, "HOOK");
      return;
    }
    this.audio.setMusicIntent(this.run.level, "VERSE");
  }

  private resolveAudioAlignedGrooveRemainingSec(): number | null {
    if (this.run == null || !this.run.groove.active) {
      return null;
    }
    const grooveLevel = this.grooveAnchorLevel ?? this.run.level;
    const secondsUntilHookComplete = this.audio.getSecondsUntilHookLoopComplete(grooveLevel);
    if (secondsUntilHookComplete == null) {
      return null;
    }
    const hookLoopSec = this.audio.getHookLoopDurationSec(grooveLevel);
    const extraCycles = Math.max(0, this.grooveCyclesRemaining - 1);
    const extraSec = hookLoopSec != null ? extraCycles * hookLoopSec : 0;
    return Math.max(0, secondsUntilHookComplete + extraSec);
  }

  private syncGrooveRemainingFromMusicBoundary(): void {
    if (this.run == null || !this.run.groove.active) {
      return;
    }
    const audioRemainingSec = this.resolveAudioAlignedGrooveRemainingSec();
    this.grooveLastAudioRemainingSec = audioRemainingSec;
    if (audioRemainingSec != null) {
      this.grooveFallbackRemainingSec = Math.max(this.grooveFallbackRemainingSec, audioRemainingSec);
    }
    const fallbackRemainingSec = Math.max(0, this.grooveFallbackRemainingSec);
    let remainingSec = audioRemainingSec ?? fallbackRemainingSec;
    // Keep a tiny positive countdown while groove is still active to avoid showing stale 0s.
    if (this.grooveCyclesRemaining > 0 && fallbackRemainingSec > 0 && remainingSec <= 0) {
      remainingSec = Math.min(0.05, fallbackRemainingSec);
    }
    this.run.groove.activeRemainingSec = remainingSec;
  }

  private endGrooveMode(reason: "audio" | "fallback"): void {
    if (this.run == null) {
      return;
    }
    if (reason === "audio") {
      this.grooveEndByAudioCount += 1;
    } else {
      this.grooveEndByFallbackCount += 1;
    }
    this.resetGrooveStateToIdle();
    this.setMessage("GROOVE MODE ENDED", STATE_MACHINE_TUNING.continueMessageDurationSec);
    this.syncMusicIntentForRun();
    this.saveIfRunning();
  }

  private updateGrooveMode(dt: number): void {
    if (this.run == null) {
      return;
    }
    if (this.isDeadBoardSequenceActive()) {
      return;
    }
    if (!this.run.groove.active) {
      return;
    }
    if (dt > 0) {
      this.grooveFallbackRemainingSec = Math.max(0, this.grooveFallbackRemainingSec - dt);
    }
    const musicEvents = this.audio.consumeMusicEvents();
    const grooveLevel = this.grooveAnchorLevel ?? this.run.level;
    let completedHookCycles = 0;
    for (const event of musicEvents) {
      if (event.kind === "HOOK_LOOP_COMPLETED" && event.level === grooveLevel) {
        completedHookCycles += 1;
      }
    }
    if (completedHookCycles > 0) {
      this.grooveCyclesRemaining = Math.max(0, this.grooveCyclesRemaining - completedHookCycles);
    }
    this.run.groove.groove = 1;
    this.run.groove.multiplier = GROOVE_MODE_MULTIPLIER;
    this.syncGrooveRemainingFromMusicBoundary();
    const endedByAudio = this.grooveCyclesRemaining <= 0;
    const endedByFallback = this.grooveFallbackRemainingSec <= 0;
    if (endedByAudio || endedByFallback) {
      this.endGrooveMode(endedByAudio ? "audio" : "fallback");
    }
  }

  private validateGrooveInvariants(): void {
    if (!GROOVE_DEBUG_ENABLED || this.run == null || !this.run.groove.active) {
      return;
    }
    if (
      this.run.groove.multiplier !== GROOVE_MODE_MULTIPLIER
      || this.run.groove.groove !== 1
      || this.run.groove.activeRemainingSec <= 0
    ) {
      console.warn("[groove] invariant violation", {
        multiplier: this.run.groove.multiplier,
        groove: this.run.groove.groove,
        activeRemainingSec: this.run.groove.activeRemainingSec,
        fallbackRemainingSec: this.grooveFallbackRemainingSec,
        audioRemainingSec: this.grooveLastAudioRemainingSec,
        cyclesRemaining: this.grooveCyclesRemaining
      });
    }
  }

  private updatePowerupExecution(dt: number): void {
    if (this.run == null) {
      return;
    }
    if (this.cashDenyFlashSec > 0) {
      this.cashDenyFlashSec = Math.max(0, this.cashDenyFlashSec - dt);
    }
    if (this.powerupExecution == null) {
      return;
    }
    this.powerupExecution.remainingSec = Math.max(0, this.powerupExecution.remainingSec - dt);
    if (this.powerupExecution.remainingSec > 0) {
      return;
    }
    this.powerupExecution = null;
    this.powerupExecuting = false;
    this.saveIfRunning();
  }

  private handleAction(action: UiAction): void {
    if (this.handleCoreGuards(action)) return;
    if (this.handleNavigationAction(action)) return;
    if (this.handleProfileAction(action)) return;
    if (this.handlePauseAndSettingsAction(action)) return;
    if (this.handleGameplayAndDailyAction(action)) return;
    if (this.handleSocialAction(action)) return;
    this.handleMenuAndStartAction(action);
  }

  // ---------------------------------------------------------------------------
  // handleAction sub-methods — split for readability. Each returns true when
  // the action was consumed (original code had `return;`).
  // ---------------------------------------------------------------------------

  private handleCoreGuards(action: UiAction): boolean {
    if (action === "TOGGLE_PAUSE") {
      if (this.paused) {
        this.setPausedState(false);
      } else if (this.pauseEligible()) {
        this.setPausedState(true);
      }
      return true;
    }
    const armKind = this.powerupKindFromAction(action);
    if (armKind != null) {
      if (armKind === "HINT") {
        this.tryCastHintPowerup();
        return true;
      }
      this.tryBuyAndArmPowerup(armKind);
      if (this.armedPowerup === armKind) {
        this.onGameEvent?.("POWERUP_BUY", { kind: armKind });
      }
      this.saveIfRunning();
      return true;
    }
    if (action === "CANCEL_POWERUP") {
      if (this.paused) {
        this.setPausedState(false);
        return true;
      }
      this.clearArmedPowerupState();
      this.saveIfRunning();
      return true;
    }
    if (action === "SKIP_TUTORIAL") {
      if (this.tutorialState != null) {
        this.tutorialState = null;
        markTutorialSeen();
      }
      return true;
    }
    // -- Onboarding actions -----------------------------------------------
    if (action === "ONBOARDING_TAP_ADVANCE" && this.onboardingState?.active) {
      const step = getCurrentStep(this.onboardingState);
      if (!step || step.type === "tutorial-step") return true;
      void this.audio.playSfx("button_click");
      advanceOnboardingStep(this.onboardingState);
      if (!this.onboardingState.active && this.onboardingState.section === "world-tour") {
        markOnboardingSeen();
        this.onboardingState = null;
        this.phase = "TITLE";
        this.onOnboardingTourComplete?.();
      }
      return true;
    }
    if (action === "ONBOARDING_SKIP") {
      this.onboardingState = null;
      this.phase = "TITLE";
      return true;
    }
    if (action === "RAFSBY_WORLD_TOUR") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onboardingState = createOnboardingState("world-tour");
      this.phase = "ONBOARDING_CALL";
      this.phoneFriendId = null;
      return true;
    }
    if (action === "RAFSBY_HOW_TO_PLAY") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onboardingState = createOnboardingState("how-to-play");
      this.phoneFriendId = null;
      this.phase = "TITLE";
      // Player sees title screen and can tap Solo to start the tutorial
      return true;
    }
    if (action === "DEBUG_RESET_ONBOARDING") {
      resetOnboardingSeen();
      this.setMessage("ONBOARDING RESET", 2);
      return true;
    }
    if (action === "ACTIVATE_GROOVE") {
      const activated = this.activateGrooveMode();
      if (activated) {
        this.onGameEvent?.("GROOVE_ACTIVATE", {});
      }
      if (this.tutorialState != null) {
        notifyTutorialAction(this.tutorialState, "GROOVE");
        if (this.onboardingState?.active) {
          const obStep = getCurrentStep(this.onboardingState);
          if (obStep?.tutorialStep === "GROOVE") {
            advanceOnboardingStep(this.onboardingState);
          }
        }
      }
      return true;
    }
    return false;
  }

  private handleNavigationAction(action: UiAction): boolean {
    if (action === "TOGGLE_MUSIC") {
      this.audio.toggleEnabled();
      return true;
    }
    if (action === "TOGGLE_SFX") {
      if (this.audio.isSfxEnabled()) {
        void this.audio.playSfx("toggle_click");
        this.audio.setSfxEnabled(false);
      } else {
        this.audio.setSfxEnabled(true);
        void this.audio.playSfx("toggle_click");
      }
      return true;
    }
    if (action === "TOGGLE_HAPTICS") {
      void this.audio.playSfx("toggle_click");
      hapticManager.setEnabled(!hapticManager.isEnabled());
      return true;
    }
    if (action === "TOGGLE_SETTINGS") {
      if (this.phase === "SETTINGS") {
        this.handleAction("CLOSE_SETTINGS");
      } else {
        this.handleAction("OPEN_SETTINGS");
      }
      return true;
    }
    if (
      action === "OPEN_SETTINGS"
      && this.phase !== "AUTH_GATE"
      && this.phase !== "USERNAME_PICK"
      && this.phase !== "ONBOARDING_CALL"
      && this.phase !== "SETTINGS"
    ) {
      this.sfxAndHaptic("button_click", "button_tap");
      // From title screen, open the phone on the settings tab instead
      if (this.phase === "TITLE") {
        this.phoneLastSeenCount = this.phoneTotalCount;
        this.phoneTab = "settings";
        this.settingsPage = 0;
        this.phase = "PHONE";
        return true;
      }
      const versusRunPhase =
        this.sessionMode === "versus"
        && (
          this.phase === "RUN_PLAYABLE"
          || this.phase === "RUN_RESOLVING"
          || this.phase === "RUN_LEVELUP"
          || this.phase === "VERSUS_MATCH"
        );
      if (versusRunPhase) {
        this.onVersusAction?.("OPEN_SETTINGS");
        return true;
      }
      this.preSettingsPhase = this.phase;
      // Pause only for single-player gameplay phases.
      if (this.pauseEligible()) {
        this.setPausedState(true);
        this.phase = "PAUSE_MENU";
        return true;
      }
      this.phase = "SETTINGS";
      this.settingsPage = 0;
      return true;
    }
    if (action === "OPEN_WORLD_MAP" && (this.phase === "TITLE" || this.phase === "PHONE")) {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "WORLD_MAP";
      return true;
    }
    if (action === "OPEN_PHONE" && this.phase === "TITLE") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phoneLastSeenCount = this.phoneTotalCount;
      this.phoneTab = "messages";
      this.settingsPage = 0;
      this.phase = "PHONE";
      return true;
    }
    if (action === "PHONE_BACK" && this.phase === "PHONE") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "TITLE";
      return true;
    }
    // Notification tab switching
    if (this.phase === "PHONE") {
      const tabActions: Record<string, "messages" | "friends" | "highscores" | "settings" | "wallet"> = {
        PHONE_TAB_MESSAGES: "messages",
        PHONE_TAB_FRIENDS: "friends",
        PHONE_TAB_HIGHSCORES: "highscores",
        PHONE_TAB_SETTINGS: "settings",
        PHONE_TAB_WALLET: "wallet",
      };
      const tab = tabActions[action];
      if (tab && tab !== this.phoneTab) {
        this.sfxAndHaptic("button_click", "button_tap");
        this.phoneTab = tab;
        this.phoneFriendId = null;
        this.phoneAddFriendMode = false;
        this.phoneEditUsernameMode = false;
        this.phoneReferMode = false;
        return true;
      }
      if (action === "PHONE_TAB_TRAVEL") {
        this.handleAction("OPEN_WORLD_MAP");
        return true;
      }
      // Message detail view actions
      if (action === "OPEN_MESSAGE" && this.phoneTab === "messages") {
        this.sfxAndHaptic("button_click", "button_tap");
        // phoneMessageId is set by main.ts after consuming tapped index
        this.onVersusAction?.(action);
        return true;
      }
      if (action === "MESSAGE_BACK" && this.phoneMessageId != null) {
        this.sfxAndHaptic("button_click", "button_tap");
        this.phoneMessageId = null;
        return true;
      }
      if (action === "MESSAGE_ACTION" && this.phoneMessageId != null) {
        this.sfxAndHaptic("button_click", "button_tap");
        this.onMessageAction?.("primary", this.phoneMessageId);
        return true;
      }
      if (action === "MESSAGE_ACTION_SECONDARY" && this.phoneMessageId != null) {
        this.sfxAndHaptic("button_click", "button_tap");
        this.onMessageAction?.("secondary", this.phoneMessageId);
        return true;
      }
      // Friend detail view actions
      if (action === "OPEN_FRIEND_DETAIL" && this.phoneTab === "friends") {
        this.sfxAndHaptic("button_click", "button_tap");
        // phoneFriendId is set by main.ts after consuming tapped index
        this.onVersusAction?.(action);
        return true;
      }
      if (action === "FRIEND_DETAIL_BACK" && this.phoneFriendId != null) {
        this.sfxAndHaptic("button_click", "button_tap");
        this.phoneFriendId = null;
        return true;
      }
    }
    // Message alert tap (global — works outside PHONE phase too)
    if (action === "TAP_MESSAGE_ALERT" && this.incomingMessageAlert != null) {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      const alertMsgId = this.incomingMessageAlert.messageId;
      this.incomingMessageAlert = null;
      this.phoneShakeTimer = 0;
      this.phoneLastSeenCount = this.phoneTotalCount;
      this.phoneTab = "messages";
      this.phoneMessageId = alertMsgId;
      this.settingsPage = 0;
      this.phase = "PHONE";
      return true;
    }
    if (action === "DISMISS_MESSAGE_ALERT" && this.incomingMessageAlert != null) {
      this.incomingMessageAlert = null;
      return true;
    }
    if (action === "WORLD_MAP_BACK" && this.phase === "WORLD_MAP") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "TITLE";
      return true;
    }
    if (
      action === "OPEN_SHOP"
      && this.phase !== "AUTH_GATE"
      && this.phase !== "USERNAME_PICK"
      && this.phase !== "ONBOARDING_CALL"
    ) {
      if (!this.featureLockState("shop").unlocked) {
        this.denyLockedFeature("shop");
        return true;
      }
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.dailyShopSkins = getDailyShopSkins(Date.now(), getCachedOwnedIds());
      this.shopViewIndex = 0;
      this.phase = "SHOP";
      return true;
    }
    if (action === "SHOP_PREV" && this.phase === "SHOP" && this.dailyShopSkins.length > 0) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.shopViewIndex = (this.shopViewIndex - 1 + this.dailyShopSkins.length) % this.dailyShopSkins.length;
      return true;
    }
    if (action === "SHOP_NEXT" && this.phase === "SHOP" && this.dailyShopSkins.length > 0) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.shopViewIndex = (this.shopViewIndex + 1) % this.dailyShopSkins.length;
      return true;
    }
    if (
      action === "OPEN_GEM_SHOP"
      && (
        this.phase === "TITLE"
        || this.phase === "SETTINGS"
        || this.phase === "PROFILE_SETTINGS"
        || this.phase === "SHOP"
      )
    ) {
      if (!this.featureLockState("gemShop").unlocked) {
        this.denyLockedFeature("gemShop");
        return true;
      }
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "GEM_SHOP";
      return true;
    }
    if (action === "OPEN_TOKEN_SHOP" && this.phase === "GEM_SHOP") {
      if (!this.featureLockState("tokenShop").unlocked) {
        this.denyLockedFeature("tokenShop");
        return true;
      }
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "TOKEN_SHOP";
      return true;
    }
    if (action === "TOKEN_SHOP_BACK" && this.phase === "TOKEN_SHOP") {
      const ps = tokenShopService.getState().purchaseState;
      if (ps === "signing" || ps === "verifying") return true;
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "GEM_SHOP";
      return true;
    }
    return false;
  }

  private handleProfileAction(action: UiAction): boolean {
    if (
      action === "OPEN_PROFILE"
      && this.phase !== "AUTH_GATE"
      && this.phase !== "USERNAME_PICK"
      && this.phase !== "ONBOARDING_CALL"
    ) {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.profilePage = 0;
      this.profileFilter = "all";
      this.profileViewMode = "browse";
      this.profileBrowseIndex = 0;
      this.profileVinylFlipped = false;
      this.profileVinylFlipMs = 0;
      this.phase = "PROFILE";
      // Fetch personal bests for vinyl B-side
      const userId = this.authState.user?.id;
      if (userId != null && userId !== "guest-local") {
        void fetchPersonalBests(userId).then((bests) => {
          this.personalBests = bests;
        });
      }
      return true;
    }
    const isPhoneWallet = this.phase === "PHONE" && this.phoneTab === "wallet";
    const isPhoneSettings = (this.phase === "PHONE" && this.phoneTab === "settings") || (this.phase === "PAUSE_MENU" && this.pauseShowSettings);
    if (action === "PROFILE_PAGE_PREV" && (this.phase === "PROFILE" || isPhoneWallet)) {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      if (this.profilePage > 0) this.profilePage--;
      return true;
    }
    if (action === "PROFILE_PAGE_NEXT" && (this.phase === "PROFILE" || isPhoneWallet)) {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      const ownedIds = getCachedOwnedIds();
      const visibleItemCount = computeProfileVisibleItemCount(
        this.profileFilter,
        ownedIds.length,
        this.profileNftAvatarCount
      );
      const totalPages = computeProfileTotalPages(visibleItemCount);
      if (this.profilePage < totalPages - 1) this.profilePage++;
      return true;
    }
    if (action === "GAMEOVER_NEXT" && this.phase === "RUN_GAMEOVER") {
      const maxStep = 2 + this.newlyUnlockedModes.length;
      if (this.gameOverStep < maxStep) {
        this.gameOverStep++;
        this.gameOverStepElapsed = 0;
        return true;
      }
    }
    if (action === "VERSUS_RESULT_NEXT" && this.phase === "VERSUS_RESULT") {
      const maxStep = this.versusAdHeartRestored || !staminaService.canPlay() ? 3 : 2;
      if (this.versusResultStep < maxStep) {
        this.versusResultStep++;
        this.versusResultStepElapsed = 0;
        // Entering the ad step → fire ad automatically, advance when closed
        if (this.versusResultStep === 2 && !staminaService.canPlay() && this.onQuickAdForStamina != null) {
          this.onQuickAdForStamina();
        }
        return true;
      }
    }
    if ((action === "VERSUS_EXIT" || action === "VERSUS_REMATCH") && this.phase === "VERSUS_RESULT") {
      void this.audio.playSfx("button_click");
      this.onVersusAction?.(action);
      return true;
    }
    if (action === "SCORE_RETRY_SUBMIT" && this.phase === "RUN_GAMEOVER" && this.scoreSubmitFailed) {
      this.scoreSubmitFailed = false;
      const userId = this.authState.user?.id ?? "guest-local";
      const displayName = this.latestRunName;
      const score = this.latestRunScore;
      const level = this.latestRunLevel;
      const mode = this.latestRunSoloMode;
      void submitScore(userId, displayName, score, level, mode)
        .then((result) => {
          if (result === "error") {
            this.scoreSubmitFailed = true;
          }
        });
      return true;
    }
    if (action === "LEADERBOARD_TOGGLE" && this.phase === "RUN_GAMEOVER") {
      this.toggleLeaderboardExpanded();
      return true;
    }
    if (action === "MAIN_MENU" && this.phase === "SHOP") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "TITLE";
      return true;
    }
    if (action === "MAIN_MENU" && (this.phase === "GEM_SHOP" || this.phase === "TOKEN_SHOP")) {
      if (this.phase === "TOKEN_SHOP") {
        const ps = tokenShopService.getState().purchaseState;
        if (ps === "signing" || ps === "verifying") return true;
      }
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.dailyShopSkins = getDailyShopSkins(Date.now(), getCachedOwnedIds());
      this.phase = "SHOP";
      return true;
    }
    if (action === "MAIN_MENU" && this.phase === "PROFILE") {
      if (this.profileViewMode === "grid") {
        this.sfxAndHaptic("button_click", "button_tap");
        this.profileViewMode = "browse";
        return true;
      }
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "TITLE";
      return true;
    }
    if (action === "OPEN_PROFILE_SETTINGS" && this.phase === "PROFILE") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "PROFILE_SETTINGS";
      return true;
    }
    if (action === "PROFILE_FILTER_ALL" && (this.phase === "PROFILE" || isPhoneWallet)) {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.profileFilter = "all";
      this.profilePage = 0;
      this.profileBrowseIndex = 0;
      return true;
    }
    if (action === "PROFILE_FILTER_WALLET" && (this.phase === "PROFILE" || isPhoneWallet)) {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.profileFilter = "wallet";
      this.profilePage = 0;
      this.profileBrowseIndex = 0;
      return true;
    }
    if (action === "PROFILE_BROWSE_PREV" && this.phase === "PROFILE") {
      this.sfxAndHaptic("button_click", "button_tap");
      const total = computeProfileVisibleItemCount(this.profileFilter, getCachedOwnedIds().length, this.profileNftAvatarCount);
      if (total > 0) this.profileBrowseIndex = (this.profileBrowseIndex - 1 + total) % total;
      return true;
    }
    if (action === "PROFILE_BROWSE_NEXT" && this.phase === "PROFILE") {
      this.sfxAndHaptic("button_click", "button_tap");
      const total = computeProfileVisibleItemCount(this.profileFilter, getCachedOwnedIds().length, this.profileNftAvatarCount);
      if (total > 0) this.profileBrowseIndex = (this.profileBrowseIndex + 1) % total;
      return true;
    }
    if (action === "PROFILE_VINYL_FLIP" && this.phase === "PROFILE") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.profileVinylFlipped = !this.profileVinylFlipped;
      this.profileVinylFlipMs = performance.now();
      return true;
    }
    if ((action === "PROFILE_VIEW_ALL" || action === "PROFILE_EDIT_SKIN") && this.phase === "PROFILE") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.profileViewMode = "grid";
      return true;
    }
    if (action === "PROFILE_VIEW_BROWSE" && this.phase === "PROFILE") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.profileViewMode = "browse";
      return true;
    }
    if (action === "MAIN_MENU" && this.phase === "PROFILE_SETTINGS") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "PROFILE";
      return true;
    }
    if (action === "TOGGLE_APPEAR_OFFLINE" && (this.phase === "PROFILE_SETTINGS" || isPhoneWallet || isPhoneSettings)) {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.appearOffline = !this.appearOffline;
      try { localStorage.setItem("bitbeast.appearOffline", this.appearOffline ? "1" : "0"); } catch { /* ignore */ }
      return true;
    }
    if (action === "TOGGLE_PUSH_NOTIFICATIONS" && (this.phase === "PROFILE_SETTINGS" || isPhoneWallet || isPhoneSettings)) {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.onTogglePushNotifications?.();
      return true;
    }
    if (action === "CHANGE_USERNAME" && this.phase === "PROFILE_SETTINGS") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.phase = "USERNAME_PICK";
      return true;
    }
    if (action === "CHANGE_USERNAME" && isPhoneSettings) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.phoneEditUsernameMode = true;
      return true;
    }
    // Wallet actions: WALLET_CONNECT, WALLET_UNLINK, NFT_SYNC (on profile settings)
    // AVATAR_SELECT_NFT (on profile page with wallet filter)
    if (action === "WALLET_CONNECT" && (this.phase === "PROFILE_SETTINGS" || isPhoneWallet)) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onWalletAction?.("connect");
      return true;
    }
    if (action === "WALLET_UNLINK" && (this.phase === "PROFILE_SETTINGS" || isPhoneWallet)) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onWalletAction?.("unlink");
      return true;
    }
    if (action === "NFT_SYNC" && (this.phase === "PROFILE_SETTINGS" || isPhoneWallet)) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onWalletAction?.("sync");
      return true;
    }
    if (action === "AVATAR_SELECT_NFT" && (this.phase === "PROFILE" || isPhoneWallet)) {
      this.sfxAndHaptic("skin_equip", "button_tap");
      this.onWalletAction?.("avatarSelect");
      return true;
    }
    if (action === "SOL_WALLET_CONNECT" && (this.phase === "PROFILE_SETTINGS" || this.phase === "TOKEN_SHOP" || isPhoneWallet)) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onWalletAction?.("solConnect");
      return true;
    }
    if (action === "SOL_WALLET_DISCONNECT" && (this.phase === "PROFILE_SETTINGS" || isPhoneWallet)) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onWalletAction?.("solDisconnect");
      return true;
    }
    return false;
  }

  private handlePauseAndSettingsAction(action: UiAction): boolean {
    if (action === "CLOSE_SETTINGS" && this.phase === "SETTINGS") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      const returnPhase = this.preSettingsPhase ?? "TITLE";
      this.preSettingsPhase = null;
      this.phase = returnPhase;
      // Unpause if returning to gameplay (single-player or versus)
      if (returnPhase === "RUN_PLAYABLE" || returnPhase === "RUN_RESOLVING" || returnPhase === "RUN_LEVELUP" || returnPhase === "VERSUS_MATCH") {
        this.setPausedState(false);
      }
      return true;
    }
    // --- Pause menu (phone-style) ---
    if (action === "PAUSE_RESUME" && this.phase === "PAUSE_MENU") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      if (this.pauseShowSettings) {
        this.pauseShowSettings = false;
        return true;
      }
      const returnPhase = this.preSettingsPhase ?? "TITLE";
      this.preSettingsPhase = null;
      this.pauseShowSettings = false;
      this.phase = returnPhase;
      this.setPausedState(false);
      return true;
    }
    if (action === "PAUSE_SETTINGS" && this.phase === "PAUSE_MENU") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.pauseShowSettings = true;
      this.settingsPage = 0;
      return true;
    }
    if (action === "PAUSE_SETTINGS_BACK" && this.phase === "PAUSE_MENU") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.pauseShowSettings = false;
      return true;
    }
    if (action === "PAUSE_RESTART" && this.phase === "PAUSE_MENU") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.preSettingsPhase = null;
      this.pauseShowSettings = false;
      this.setPausedState(false);
      this.startNewRun(this.runSoloMode());
      return true;
    }
    if (action === "PAUSE_QUIT" && this.phase === "PAUSE_MENU") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.preSettingsPhase = null;
      this.pauseShowSettings = false;
      this.setPausedState(false);
      this.tutorialState = null;
      this.currentRunIsTutorial = false;
      this.clearGameplayVisualState();
      this.run = null;
      this.phase = "TITLE";
      return true;
    }
    if (action === "MAIN_MENU" && this.phase === "SETTINGS") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.preSettingsPhase = null;
      this.setPausedState(false);
      this.tutorialState = null;
      this.currentRunIsTutorial = false;
      this.clearGameplayVisualState();
      if (this.sessionMode === "versus") {
        // Forfeit the versus match and go to main menu
        this.onVersusAction?.("VERSUS_FORFEIT");
      } else {
        this.run = null;
        this.phase = "TITLE";
      }
      return true;
    }
    const isPhoneSettings = (this.phase === "PHONE" && this.phoneTab === "settings") || (this.phase === "PAUSE_MENU" && this.pauseShowSettings);
    if (action === "LOGOUT" && (this.phase === "SETTINGS" || isPhoneSettings)) {
      this.sfxAndHaptic("button_click", "button_tap");
      void this.authManager.signOut();
      return true;
    }
    if (this.phase === "SETTINGS" || isPhoneSettings) {
      if (action === "SETTINGS_PAGE_NEXT") {
        const totalPages = getSettingsPageCount({
          surface: isPhoneSettings ? "phone" : "main",
          canUseDebugSettings: this.debugAccess.canUseDebugSettings,
        });
        if (this.settingsPage < totalPages - 1) {
          this.settingsPage++;
          this.sfxAndHaptic("menu_whoosh", "button_tap");
        }
      } else if (action === "SETTINGS_PAGE_PREV") {
        if (this.settingsPage > 0) {
          this.settingsPage--;
          this.sfxAndHaptic("menu_whoosh", "button_tap");
        }
      } else if (action === "SETTINGS_BG_STYLE_NEXT") {
        this.applyManualPerformanceChange({
          backgroundStyleMode: cycleOption(BG_STYLE_OPTIONS, this.performanceSettings.backgroundStyleMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_BG_BLOOM_NEXT") {
        this.applyManualPerformanceChange({
          backgroundBloomMode: cycleOption(BG_BLOOM_OPTIONS, this.performanceSettings.backgroundBloomMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_BG_GLOW_NEXT") {
        this.applyManualPerformanceChange({
          backgroundGlowMode: cycleOption(BG_GLOW_OPTIONS, this.performanceSettings.backgroundGlowMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_BG_SATURATION_NEXT") {
        this.applyManualPerformanceChange({
          backgroundSaturationMode: cycleOption(BG_SATURATION_OPTIONS, this.performanceSettings.backgroundSaturationMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_BG_MIX_NEXT") {
        this.applyManualPerformanceChange({
          backgroundMixMode: cycleOption(BG_MIX_OPTIONS, this.performanceSettings.backgroundMixMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_BG_MOTION_NEXT") {
        this.applyManualPerformanceChange({
          backgroundMotionMode: cycleOption(BG_MOTION_OPTIONS, this.performanceSettings.backgroundMotionMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_PRESET_NEXT") {
        const currentPreset = this.performanceSettings.preset === "CUSTOM"
          ? "BALANCED"
          : this.performanceSettings.preset;
        const nextPreset = cycleOption(PERFORMANCE_PRESET_ORDER, currentPreset);
        this.setPerformancePreset(nextPreset);
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_SIDEFX_NEXT") {
        this.applyManualPerformanceChange({
          sideFxMode: cycleOption(SIDE_FX_OPTIONS, this.performanceSettings.sideFxMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_SIDEFX_DENSITY_NEXT") {
        this.applyManualPerformanceChange({
          sideFxDensityMode: cycleOption(SIDE_FX_DENSITY_OPTIONS, this.performanceSettings.sideFxDensityMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_PARTICLES_NEXT") {
        this.applyManualPerformanceChange({
          particleMode: cycleOption(PARTICLE_OPTIONS, this.performanceSettings.particleMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_OPPONENT_NEXT") {
        this.applyManualPerformanceChange({
          opponentRenderMode: cycleOption(OPPONENT_RENDER_OPTIONS, this.performanceSettings.opponentRenderMode)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (action === "SETTINGS_FPSCAP_NEXT") {
        this.applyManualPerformanceChange({
          fpsCap: cycleOption(FPS_CAP_OPTIONS, this.performanceSettings.fpsCap)
        });
        this.sfxAndHaptic("toggle_click", "button_tap");
      } else if (this.debugAccess.canUseDebugSettings) {
        if (action === "SETTINGS_DEBUG_ENABLED_TOGGLE") {
          this.applyDebugSettings({ ...this.debugSettings, enabled: !this.debugSettings.enabled }, "settings_ui");
          this.sfxAndHaptic("toggle_click", "button_tap");
        } else if (action === "SETTINGS_DEBUG_OVERLAY_GAMEPLAY_TOGGLE") {
          this.applyDebugSettings({ ...this.debugSettings, overlayGameplay: !this.debugSettings.overlayGameplay }, "settings_ui");
          this.sfxAndHaptic("toggle_click", "button_tap");
        } else if (action === "SETTINGS_DEBUG_OVERLAY_BOT_TOGGLE") {
          this.applyDebugSettings({ ...this.debugSettings, overlayBot: !this.debugSettings.overlayBot }, "settings_ui");
          this.sfxAndHaptic("toggle_click", "button_tap");
        } else if (action === "SETTINGS_DEBUG_VERBOSITY_NEXT") {
          this.applyDebugSettings({
            ...this.debugSettings,
            verbosity: cycleOption(DEBUG_VERBOSITY_OPTIONS, this.debugSettings.verbosity)
          }, "settings_ui");
          this.sfxAndHaptic("toggle_click", "button_tap");
        } else if (action === "SETTINGS_DEBUG_DIAGNOSTICS_NEXT") {
          this.applyDebugSettings({
            ...this.debugSettings,
            diagnostics: cycleOption(DEBUG_DIAGNOSTICS_OPTIONS, this.debugSettings.diagnostics)
          }, "settings_ui");
          this.sfxAndHaptic("toggle_click", "button_tap");
        } else if (action === "SETTINGS_DEBUG_PERFSAFE_TOGGLE") {
          this.applyDebugSettings({ ...this.debugSettings, perfSafeMode: !this.debugSettings.perfSafeMode }, "settings_ui");
          this.sfxAndHaptic("toggle_click", "button_tap");
        }
      }
      return true;
    }
    return false;
  }

  private handleGameplayAndDailyAction(action: UiAction): boolean {
    if (this.paused) {
      return true;
    }
    if (this.powerupExecuting) {
      return true;
    }
    if (action === "ROTATE_LEFT_NOW" || action === "ROTATE_RIGHT_NOW") {
      if (this.phase !== "RUN_PLAYABLE" || this.run == null || this.isAnimating() || this.isPowerupBusy()) {
        return true;
      }
      const dir: RotationDir = action === "ROTATE_RIGHT_NOW" ? "RIGHT" : "LEFT";
      if (this.isRowRushRun()) {
        const rowRushRound = this.ensureRowRushRoundState();
        if (rowRushRound != null && rowRushRound.lockedRotation == null) {
          rowRushRound.lockedRotation = dir;
          this.pendingRotation = dir;
          this.saveIfRunning();
        }
      } else {
        this.executeRotation("manual", dir);
      }
      if (this.tutorialState != null) {
        notifyTutorialAction(this.tutorialState, "ROTATE");
        if (this.onboardingState?.active) {
          const obStep = getCurrentStep(this.onboardingState);
          if (obStep?.tutorialStep === "ROTATE") {
            advanceOnboardingStep(this.onboardingState);
          }
        }
      }
      this.saveIfRunning();
      return true;
    }
    if ((action === "CLAIM_USERNAME" || action === "FOCUS_USERNAME_INPUT") && this.phase === "USERNAME_PICK") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onVersusAction?.(action);
      return true;
    }
    if ((action === "CLAIM_USERNAME" || action === "FOCUS_USERNAME_INPUT") && this.phase === "PHONE" && this.phoneEditUsernameMode) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onVersusAction?.(action);
      return true;
    }
    if (action === "PHONE_EDIT_USERNAME_BACK" && this.phase === "PHONE" && this.phoneEditUsernameMode) {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phoneEditUsernameMode = false;
      return true;
    }
    if (action === "OPEN_DAILY_LOGIN_BONUS" && this.phase === "TITLE") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.dailyLoginModalOpen = false;
      this.phase = "DAILY_REWARDS";
      this.onVersusAction?.(action);
      return true;
    }
    if (action === "CLOSE_DAILY_LOGIN_BONUS" && this.phase === "TITLE") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.dailyLoginModalOpen = false;
      this.onVersusAction?.(action);
      return true;
    }
    if (action === "CLAIM_DAILY_LOGIN_BONUS" && (this.phase === "TITLE" || this.phase === "DAILY_REWARDS")) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.dailyRewardAnimPhase = "entrance";
      this.dailyRewardAnimStartMs = performance.now();
      this.phase = "DAILY_REWARDS_OPEN";
      this.onVersusAction?.(action);
      return true;
    }
    if (action === "MAIN_MENU" && this.phase === "DAILY_REWARDS") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = "TITLE";
      return true;
    }
    if (action === "DAILY_REWARD_CRATE_TAP" && this.phase === "DAILY_REWARDS_OPEN") {
      if (this.dailyRewardAnimPhase === "waiting") {
        this.sfxAndHaptic("button_click", "button_tap");
        this.dailyRewardAnimPhase = "shaking";
        this.dailyRewardAnimStartMs = performance.now();
      }
      return true;
    }
    if (action === "DAILY_REWARD_CONTINUE" && this.phase === "DAILY_REWARDS_OPEN") {
      if (this.dailyRewardAnimPhase === "done") {
        this.sfxAndHaptic("button_click", "button_tap");
        this.phase = "TITLE";
        this.gemBurstPending = true;
      }
      return true;
    }
    if (action === "OPEN_AUTH_GATE" && this.phase === "TITLE") {
      this.sfxAndHaptic("button_click", "button_tap");
      if (this.authState.user?.id === "guest-local" && this.authManager.hasBackend()) {
        this.dailyLoginModalOpen = false;
        this.phase = "AUTH_GATE";
      }
      return true;
    }
    return false;
  }

  private handleSocialAction(action: UiAction): boolean {
    if (action === "OPEN_FRIENDS" && this.phase !== "AUTH_GATE" && this.phase !== "USERNAME_PICK") {
      if (!this.featureLockState("friends").unlocked) {
        this.denyLockedFeature("friends");
        return true;
      }
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      // Notify external handler (refreshes friend data)
      this.onVersusAction?.(action);
      // Open the phone on the friends tab
      this.phoneLastSeenCount = this.phoneTotalCount;
      this.phoneTab = "friends";
      this.settingsPage = 0;
      this.phase = "PHONE";
      return true;
    }
    if (action === "OPEN_ADD_FRIEND" && (this.phase === "FRIENDS" || this.phase === "PHONE")) {
      if (!this.featureLockState("addFriend").unlocked) {
        this.denyLockedFeature("addFriend");
        return true;
      }
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      if (this.phase === "PHONE") {
        this.phoneAddFriendMode = true;
        this.phoneFriendId = null;
        return true;
      }
      this.preAddFriendPhase = this.phase;
      this.phase = "ADD_FRIEND";
      return true;
    }
    if (action === "PHONE_ADD_FRIEND_BACK" && this.phase === "PHONE" && this.phoneAddFriendMode) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.phoneAddFriendMode = false;
      return true;
    }
    if (action === "PHONE_REFER_FRIEND" && this.phase === "PHONE") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.phoneReferMode = true;
      this.phoneFriendId = null;
      this.phoneAddFriendMode = false;
      return true;
    }
    if (action === "PHONE_REFER_BACK" && this.phase === "PHONE" && this.phoneReferMode) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.phoneReferMode = false;
      return true;
    }
    if (action === "ADD_FRIEND_BACK" && this.phase === "ADD_FRIEND") {
      this.sfxAndHaptic("menu_whoosh", "button_tap");
      this.phase = this.preAddFriendPhase ?? "FRIENDS";
      this.preAddFriendPhase = null;
      return true;
    }
    if (
      action === "ADD_FRIEND"
      || action === "FRIEND_BATTLE"
      || action === "FRIEND_ACCEPT"
      || action === "FRIEND_DECLINE"
      || action === "FRIEND_REMOVE"
      || action === "FRIEND_REMOVE_CONFIRM"
      || action === "FRIEND_REMOVE_CANCEL"
      || action === "FRIENDS_PAGE_PREV"
      || action === "FRIENDS_PAGE_NEXT"
      || action === "PHONE_REFER_SHARE"
      || action === "PHONE_REFER_COPY"
    ) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onVersusAction?.(action);
      return true;
    }
    // Route shop actions to external handler
    if (
      (action === "SHOP_BUY" || action === "SHOP_EQUIP" || action === "SHOP_EXCHANGE" || action === "SHOP_BUY_GEMS" || action === "SHOP_BUY_SOL" || action === "SHOP_BUY_IAP" || action === "SHOP_RESTORE_IAP" || action === "SHOP_IAP_CONFIRM" || action === "SHOP_IAP_CANCEL" || action === "TOKEN_SHOP_SELECT" || action === "TOKEN_SHOP_CONFIRM" || action === "TOKEN_SHOP_CANCEL" || action === "TOKEN_SHOP_DISMISS" || action === "TOKEN_SHOP_RECHECK") &&
      (this.phase === "SHOP" || this.phase === "GEM_SHOP" || this.phase === "TOKEN_SHOP")
    ) {
      const lockedFeature = this.lockedFeatureForShopAction(action);
      if (lockedFeature != null && !this.featureLockState(lockedFeature).unlocked) {
        this.denyLockedFeature(lockedFeature);
        return true;
      }
      this.sfxAndHaptic("button_click", "button_tap");
      this.onShopAction?.(action);
      return true;
    }
    // Route profile actions to external handler
    const isPhoneWallet = this.phase === "PHONE" && this.phoneTab === "wallet";
    if (action === "PROFILE_EQUIP" && (this.phase === "PROFILE" || isPhoneWallet)) {
      this.sfxAndHaptic("skin_equip", "button_tap");
      this.onProfileAction?.(action);
      return true;
    }
    // Block lobby entry if locked
    if (action === "VERSUS_MODE" && !this.featureLockState("lobby").unlocked) {
      this.denyLockedFeature("lobby");
      return true;
    }
    // Route versus-specific actions to external handler
    if (
      action === "VERSUS_MODE" ||
      action === "VERSUS_READY" ||
      action === "VERSUS_LEAVE" ||
      action === "VERSUS_QUICKMATCH" ||
      action === "VERSUS_CHAT" ||
      action === "VERSUS_CREATE_LOBBY" ||
      action === "VERSUS_JOIN_LOBBY" ||
      action === "LOBBY_TOGGLE_JOIN" ||
      action === "VERSUS_TAP_LOBBY_ROW" ||
      action === "VERSUS_REFRESH_LOBBIES" ||
      action === "VERSUS_FORFEIT" ||
      action === "TRAP_LOCKDOWN" ||
      action === "TRAP_FOG" ||
      action === "TRAP_COIN_TAX" ||
      action === "TRAP_GROOVE_DRAIN" ||
      action === "TRAP_DOOM_SURGE"
    ) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onVersusAction?.(action);
      return true;
    }
    // MAIN_MENU from versus phases delegates to external handler
    if (action === "MAIN_MENU" && (
      this.phase === "VERSUS_LOBBY" ||
      this.phase === "VERSUS_MATCH" ||
      this.phase === "VERSUS_RESULT" ||
      this.phase === "FRIENDS"
    )) {
      this.sfxAndHaptic("button_click", "button_tap");
      this.clearGameplayVisualState();
      this.onVersusAction?.(action);
      return true;
    }
    if (action === "MAIN_MENU") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.setPausedState(false);
      this.clearGameplayVisualState();
      this.run = null;
      this.pendingGameplayIntent = null;
      this.tutorialState = null;
      this.currentRunIsTutorial = false;
      this.soloStaminaSpentForCurrentRun = false;
      this.phase = "TITLE";
      return true;
    }
    return false;
  }

  private handleMenuAndStartAction(action: UiAction): boolean {
    if (action === "EDIT_PROFILE") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.profileVinylFlipped = false;
      this.profileVinylFlipMs = 0;
      this.phase = "PROFILE";
      const editUserId = this.authState.user?.id;
      if (editUserId != null && editUserId !== "guest-local") {
        void fetchPersonalBests(editUserId).then((bests) => {
          this.personalBests = bests;
        });
      }
      return true;
    }
    if (action === "VIEW_LEADERBOARD") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.phoneLastSeenCount = this.phoneTotalCount;
      this.phoneTab = "highscores";
      this.settingsPage = 0;
      this.phase = "PHONE";
      return true;
    }
    const leaderboardTabsAvailable = this.phase === "LEADERBOARD" || this.isPhoneHighscoresVisible();
    if (action === "LEADERBOARD_TAB_SOLO" && leaderboardTabsAvailable) {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.setLeaderboardTab("solo");
      return true;
    }
    if (action === "LEADERBOARD_TAB_VERSUS" && leaderboardTabsAvailable) {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.setLeaderboardTab("versus");
      return true;
    }
    if (action === "SOLO_LEADERBOARD_CLASSIC" && leaderboardTabsAvailable && this.leaderboardTab === "solo") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.setSelectedSoloLeaderboardMode("classic");
      return true;
    }
    if (action === "SOLO_LEADERBOARD_ROW_RUSH" && leaderboardTabsAvailable && this.leaderboardTab === "solo") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.setSelectedSoloLeaderboardMode("row-rush");
      return true;
    }
    if (action === "SOLO_LEADERBOARD_CLEAROUT" && leaderboardTabsAvailable && this.leaderboardTab === "solo") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.setSelectedSoloLeaderboardMode("clearout");
      return true;
    }
    if (action === "SOLO_LEADERBOARD_COLOR_HUNT" && leaderboardTabsAvailable && this.leaderboardTab === "solo") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.setSelectedSoloLeaderboardMode("color-hunt");
      return true;
    }
    if (action === "SOLO_LEADERBOARD_TURBOMAXXING" && leaderboardTabsAvailable && this.leaderboardTab === "solo") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.setSelectedSoloLeaderboardMode("turbomaxxing");
      return true;
    }
    if (action === "SOLO_LEADERBOARD_CYCLE_NEXT" && leaderboardTabsAvailable && this.leaderboardTab === "solo") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      const idx = SOLO_MODES.indexOf(this.selectedSoloLeaderboardMode);
      const next = SOLO_MODES[(idx + 1) % SOLO_MODES.length]!;
      this.setSelectedSoloLeaderboardMode(next);
      return true;
    }
    if (action === "OPEN_SOLO_SELECTOR") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.focusSoloSelectorMode(SOLO_SELECT_MODES[0]!);
      this.phase = "SOLO_SELECT";
      return true;
    }
    if (action === "CLOSE_SOLO_SELECTOR" || action === "SOLO_SELECT_BACK") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.phase = "TITLE";
      return true;
    }
    if (action === "SOLO_CAROUSEL_PREV") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.soloCarouselIndex = (this.soloCarouselIndex + SOLO_SELECT_MODES.length - 1) % SOLO_SELECT_MODES.length;
      this.setSelectedSoloMode(SOLO_SELECT_MODES[this.soloCarouselIndex]!);
      this.refreshSoloContinueAvailability();
      return true;
    }
    if (action === "SOLO_CAROUSEL_NEXT") {
      this.sfxAndHaptic("toggle_click", "button_tap");
      this.soloCarouselIndex = (this.soloCarouselIndex + 1) % SOLO_SELECT_MODES.length;
      this.setSelectedSoloMode(SOLO_SELECT_MODES[this.soloCarouselIndex]!);
      this.refreshSoloContinueAvailability();
      return true;
    }
    if (action === "START") {
      this.sfxAndHaptic("button_click", "button_tap");
      if (this.phase === "TITLE" || this.phase === "ONBOARDING_CALL") {
        this.refreshSoloContinueAvailability();
        this.phase = "SOLO_SELECT";
        return true;
      }
      this.beginSoloStart(this.selectedSoloMode);
      return true;
    }
    if (action === "START_SOLO_CLASSIC") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloStart("classic");
      return true;
    }
    if (action === "START_SOLO_ROW_RUSH") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloStart("row-rush");
      return true;
    }
    if (action === "START_SOLO_CLEAROUT") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloStart("clearout");
      return true;
    }
    if (action === "START_SOLO_COLOR_HUNT") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloStart("color-hunt");
      return true;
    }
    if (action === "START_SOLO_TURBOMAXXING") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloStart("turbomaxxing");
      return true;
    }
    if (action === "START_TUTORIAL") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloStart("classic", { forceTutorial: true });
      return true;
    }
    if (action === "CONTINUE") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloContinue(this.selectedSoloMode);
      return true;
    }
    if (action === "CONTINUE_SOLO_CLASSIC") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloContinue("classic");
      return true;
    }
    if (action === "CONTINUE_SOLO_ROW_RUSH") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloContinue("row-rush");
      return true;
    }
    if (action === "CONTINUE_SOLO_CLEAROUT") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloContinue("clearout");
      return true;
    }
    if (action === "CONTINUE_SOLO_COLOR_HUNT") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloContinue("color-hunt");
      return true;
    }
    if (action === "CONTINUE_SOLO_TURBOMAXXING") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.beginSoloContinue("turbomaxxing");
      return true;
    }
    if (action === "STAMINA_HEART_TAP") {
      this.sfxAndHaptic("button_click", "button_tap");
      // Show AD_GATE as info screen — no gameplay intent so closing returns to title
      this.pendingGameplayIntent = null;
      this.phase = "AD_GATE";
      return true;
    }
    if (action === "AD_GATE_WATCH_AD") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onAdGateWatchAd?.();
      return true;
    }
    if (action === "AD_GATE_CONNECT_WALLET") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onAdGateConnectWallet?.();
      return true;
    }
    if (action === "SHARE_REFERRAL") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onShareReferral?.();
      return true;
    }
    if (action === "INSTALL_APP") {
      this.sfxAndHaptic("button_click", "button_tap");
      this.onInstallApp?.();
      return true;
    }
    if (action === "LOGIN_GOOGLE") {
      void this.authManager.signInWithGoogle();
    }
    if (action === "LOGIN_DISCORD") {
      void this.authManager.signInWithDiscord();
    }
    if (action === "LOGOUT") {
      void this.authManager.signOut();
    }
    return false;
  }

  update(dt: number, input: GameInput): void {
    const actions = input.consumeActions();
    for (const action of actions) {
      this.handleAction(action);
    }

    // -- Daily reward animation phase advancement -----------------------
    if (this.phase === "DAILY_REWARDS_OPEN") {
      const elapsed = performance.now() - this.dailyRewardAnimStartMs;
      if (this.dailyRewardAnimPhase === "entrance" && elapsed >= 500) {
        this.dailyRewardAnimPhase = "waiting";
        this.dailyRewardAnimStartMs = performance.now();
      } else if (this.dailyRewardAnimPhase === "shaking" && elapsed >= 800) {
        this.dailyRewardAnimPhase = "burst";
        this.dailyRewardAnimStartMs = performance.now();
        void this.audio.playSfx("daily_treasure");
      } else if (this.dailyRewardAnimPhase === "burst" && elapsed >= 300) {
        this.dailyRewardAnimPhase = "reveal";
        this.dailyRewardAnimStartMs = performance.now();
      } else if (this.dailyRewardAnimPhase === "reveal" && elapsed >= 800) {
        this.dailyRewardAnimPhase = "done";
        this.dailyRewardAnimStartMs = performance.now();
      }
    }

    // -- Onboarding: update call shake timer ----------------------------
    if (this.onboardingState?.active) {
      updateCallShake(this.onboardingState, dt);
    }

    // -- Tutorial: detect radial hold reaching threshold ----------------
    if (this.tutorialState != null && this.tutorialState.step === "RADIAL") {
      const radialState = input.getRadialMenuState();
      if (radialState != null && radialState.holdReached) {
        notifyTutorialRadialHold(this.tutorialState);
        if (this.onboardingState?.active) {
          const obStep = getCurrentStep(this.onboardingState);
          if (obStep?.tutorialStep === "RADIAL") {
            advanceOnboardingStep(this.onboardingState);
          }
        }
      }
    }

    // -- Radial menu release ? dispatch selected action -----------------
    const radialRelease = input.consumeRadialRelease();
    if (radialRelease != null) {
      const { kind, pointerX, pointerY, hubX, hubY } = radialRelease;
      if (kind === "powerup") {
        const inv = this.run?.powerupInventory;
        const cash = Math.max(0, Math.floor(this.run?.cashBalance ?? 0));
        const items = createPowerupRadialItems(inv, cash);
        const idx = resolveHighlightedIndex(hubX, hubY, pointerX, pointerY, items);
        if (idx != null && items[idx].enabled) {
          this.handleAction(items[idx].action);
        }
      } else {
        // For traps, build items with permissive enabled state — the external
        // onVersusAction handler validates cost/cooldown when dispatched.
        const items = createTrapRadialItems(Infinity, 0, true);
        const idx = resolveHighlightedIndex(hubX, hubY, pointerX, pointerY, items);
        if (idx != null) {
          this.handleAction(items[idx].action);
        }
      }
    }

    if (this.paused) {
      this.clearBufferedBoardInteractions();
      while (input.consumeSwap() != null) {
        // discard buffered swaps while paused
      }
      while (input.consumeBoardClick() != null) {
        // discard buffered clicks while paused
      }
      this.dragState = null;
      this.hoverCell = null;
      this.hoveredPowerup = null;
      this.wasDragging = false;
      return;
    }

    if (this.messageTimer > 0) {
      this.messageTimer = Math.max(0, this.messageTimer - dt);
      if (this.messageTimer === 0) {
        this.message = "";
      }
    }
    this.flashAlpha = Math.max(0, this.flashAlpha - dt * STATE_MACHINE_TUNING.flashDecayPerSec);
    this.shakeMagnitude *= Math.pow(0.002, dt);
    if (this.shakeMagnitude < 0.01) this.shakeMagnitude = 0;

    if (this.hypeTimer > 0) {
      this.hypeTimer = Math.max(0, this.hypeTimer - dt);
      if (this.hypeTimer === 0) this.hypeText = "";
    }

    if (this.phoneShakeTimer > 0) {
      this.phoneShakeTimer -= dt;
    }

    this.dragState = input.getDragState();
    this.hoverCell = input.getHoverCell();
    this.hoveredPowerup = input.getHoveredPowerup();
    if (this.dragState != null && !this.wasDragging) {
      void this.audio.playSfx("tile_pickup");
    }
    this.wasDragging = this.dragState != null;

    if (this.authErrorTimer > 0) {
      this.authErrorTimer = Math.max(0, this.authErrorTimer - dt);
      if (this.authErrorTimer === 0) {
        this.authError = "";
      }
    }
    if (this.phase === "AUTH_GATE") {
      return;
    }
    if (
      this.phase === "TITLE"
      || this.phase === "SETTINGS"
      || this.phase === "RUN_GAMEOVER"
      || this.phase === "PROFILE"
      || this.phase === "PROFILE_SETTINGS"
      || this.phase === "LEADERBOARD"
      || this.phase === "SHOP"
      || this.phase === "GEM_SHOP"
      || this.phase === "TOKEN_SHOP"
    ) {
      if (this.phase === "RUN_GAMEOVER") {
        const prevT = this.gameOverElapsedSec;
        this.gameOverElapsedSec += dt;
        this.gameOverStepElapsed += dt;
        const t = this.gameOverElapsedSec;
        // Score tick SFX during count-up (t=0.3 to ~0.9), throttled to ~10/sec
        if (t >= 0.3 && t <= 1.0 && this.latestRunScore > 0) {
          const tickInterval = 0.1;
          if (Math.floor(t / tickInterval) > Math.floor(prevT / tickInterval)) {
            const scoreProgress = Math.min(1, (t - 0.3) / 0.6);
            if (scoreProgress < 1) {
              void this.audio.playSfx("score_tick");
            }
          }
        }
        // Coin collect SFX when count-up finishes
        if (prevT < 1.0 && t >= 1.0) {
          void this.audio.playSfx("coin_gain");
        }
      }
      return;
    }
    if (this.phase === "USERNAME_PICK" || this.phase === "FRIENDS" || this.phase === "ADD_FRIEND") {
      return;
    }
    // Versus phases are managed externally by VersusController / main.ts
    if (this.phase === "VERSUS_RESULT") {
      this.versusResultStepElapsed += dt;
      return;
    }
    if (
      this.phase === "VERSUS_LOBBY" ||
      this.phase === "VERSUS_COUNTDOWN" ||
      this.phase === "VERSUS_MATCH"
    ) {
      return;
    }
    // Cloud save debounce
    if (this.cloudSaveTimer > 0) {
      this.cloudSaveTimer -= dt;
      if (this.cloudSaveTimer <= 0) {
        this.cloudSaveTimer = 0;
        this.flushCloudSave();
      }
    }
    if (this.run == null) {
      return;
    }
    this.collectQueuedSwaps(input);
    if (this.armedPowerup != null) {
      this.clearStoredBoardTaps();
      while (true) {
        const click = input.consumeBoardClick();
        if (click == null) {
          break;
        }
        this.executeArmedPowerupOnBoardClick(click);
      }
    } else {
      while (true) {
        const click = input.consumeBoardClick();
        if (click == null) {
          break;
        }
        this.handleIdleBoardClick(click);
      }
    }
    this.updatePowerupExecution(dt);

    if (this.pendingRotation == null && !this.isRowRushRun()) {
      this.pendingRotation = "LEFT";
    }

    if (this.freezeTimer > 0) {
      this.freezeTimer = Math.max(0, this.freezeTimer - dt);
    }

    if (this.tutorialState != null) {
      updateTutorial(this.tutorialState, dt);
      if (isTutorialComplete(this.tutorialState)) {
        this.tutorialState = null;
        markTutorialSeen();
      } else {
        this.prepTutorialStep();
      }
    }

    this.updateGrooveMode(dt);
    this.validateGrooveInvariants();
    this.applyGrooveIdleDecay(dt);
    this.updateGrooveReadyWindow(dt);

    // Keep hit-stop FX from stalling visual timelines; stalling here can leave
    // the next collapse step rendered half-started and make tiles appear to teleport.
    const deadBoardSequenceActive = this.isDeadBoardSequenceActive();
    const doomClockFrozen = deadBoardSequenceActive || (this.isRowRushRun() ? false : this.shouldFreezeDoomClock());
    if (this.isRowRushRun()) {
      const rowRushTimerActive = this.phase === "RUN_PLAYABLE" || this.phase === "RUN_RESOLVING";
      if (rowRushTimerActive && !deadBoardSequenceActive) {
        this.run.doomClockSec = Math.max(0, this.run.doomClockSec - dt);
        if (this.run.doomClockSec <= 0) {
          this.run.doomClockSec = 0;
          this.rowRushRoundAdvancePending = true;
        }
      }
    } else if (!doomClockFrozen) {
      this.run.doomClockSec -= DOOM_CLOCK_DRAIN_PER_SEC * this.doomPressureMultiplier() * dt;
      if (this.run.doomClockSec <= 0) {
        this.run.doomClockSec = 0;
        this.enterGameOver();
        return;
      }
    }
    this.run.lives = Math.ceil(this.run.doomClockSec / 20);

    const pauseRotationCountdown = deadBoardSequenceActive || this.shouldPauseRotationCountdown();
    this.advanceAnimations(dt);
    const beatTick = advanceBeat(this.run.beat, dt, {
      pauseRotationCountdown
    });

    const animatingNow = this.isAnimating();
    const wasWarning = this.warningActive;
    this.warningActive = !this.isRowRushRun() && !animatingNow && beatTick.warningNow;
    if (this.warningActive && !wasWarning) {
      void this.audio.playSfx("warning_alert");
    }

    if (this.phase === "RUN_LEVELUP") {
      this.levelUpTimer -= dt;
      if (this.levelUpTimer <= 0) {
        if (this.rowRushPendingGameOver && this.isRowRushRun()) {
          this.rowRushPendingGameOver = false;
          this.enterGameOver();
          return;
        }
        this.phase = "RUN_PLAYABLE";
        this.newColorIndex = null;
        if (this.isRowRushRun()) {
          this.pendingRotation = null;
        }
      }
    }

    if (
      beatTick.rotationDue
      && this.phase !== "RUN_LEVELUP"
      && !animatingNow
      && !this.isPowerupBusy()
      && !this.isRowRushRun()
    ) {
      this.executeRotation("timer");
    }

    if (!this.isAnimating()) {
      this.drainPendingScoreBatches();
      this.runPendingPostResolveChecks();
      this.resumeComboWindowAfterSettle();
      this.tryAdvanceRowRushRound();
      this.applyQueuedSwapIfReady();
      this.resetChainDepthIfIdle();
    }
    this.advanceComboWindow(deadBoardSequenceActive ? 0 : dt);
    if (
      this.doomFrozenByPowerupAnim
      && !this.hasActiveVisualAnimation()
      && !this.hasPendingResolveWork()
      && !this.isPowerupBusy()
    ) {
      this.doomFrozenByPowerupAnim = false;
    }

    if (this.hintCastRemainingSec > 0) {
      this.hintCastRemainingSec = Math.max(0, this.hintCastRemainingSec - dt);
      if (this.hintCastRemainingSec <= 0) {
        this.hintCastCells = [];
      }
    }

    const animatingForHints = this.isAnimating();
    const hintEligible =
      this.phase === "RUN_PLAYABLE" &&
      !animatingForHints &&
      this.tutorialState == null &&
      this.run.level <= 7;
    let idleHintCells: Position[] = [];
    if (hintEligible) {
      this.noClearElapsedSec += dt;
      if (this.noClearElapsedSec >= 4) {
        idleHintCells = findHintCells(this.run.board);
      }
    } else if (this.phase !== "RUN_PLAYABLE" || this.tutorialState != null || this.run.level > 7) {
      this.noClearElapsedSec = 0;
    }

    const activeHintCastCells = this.hintCastRemainingSec > 0 ? this.hintCastCells : [];
    const mergedHintCells = mergeUniquePositions(idleHintCells, activeHintCastCells);
    this.hintActive = mergedHintCells.length > 0;
    this.hintCells = mergedHintCells;
  }

  getPhase(): GamePhase {
    return this.phase;
  }

  getViewModel() {
    const stamina = staminaService.getState();
    const staminaNextRecoverySec = stamina.nextRecoveryAt == null
      ? null
      : Math.max(0, Math.ceil((stamina.nextRecoveryAt - Date.now()) / 1000));
    const levelCfg = this.levelConfig();
    const popup = this.lastScorePopup;
    this.lastScorePopup = null;
    const cashPopupAmount = this.cashPopupAmount;
    this.flushCashPopup();
    const doomClockPopup = this.doomClockTimePopup;
    this.doomClockTimePopup = null;
    const comboCount = this.pendingComboCount;
    const comboWindowRemainingSec = comboCount > 0 ? this.comboTimerRemainingSec : 0;
    const doomClockFrozen = this.isDeadBoardSequenceActive() || (this.isRowRushRun() ? false : this.shouldFreezeDoomClock());
    const musicVisual: MusicVisualFrame | null = this.audio.getMusicVisualFrame();
    const soloMode = this.runSoloMode();
    const soloGoal = this.currentSoloStageGoal();
    const goalProgress = this.currentSoloGoalProgress();
    const goalTarget = soloGoal?.target ?? null;
    const classicScoreProgress = this.run == null ? 0 : this.currentClassicLevelScoreProgress();
    const _activeObj = this.currentActiveObjective();
    const shapeNames: Record<string, string> = { SQUARE: "SQUARE", L: "L-SHAPE", T: "T-SHAPE" };
    const goalLabel = this.isClassicSoloMode(soloMode)
      ? (_activeObj == null ? null
        : _activeObj.kind === "SHAPE_MATCH" && _activeObj.targetShape
          ? `MATCH ${shapeNames[_activeObj.targetShape] ?? _activeObj.targetShape}`
          : objectiveLabel(_activeObj.kind))
      : soloMode === "row-rush"
        ? "PERFECT ROUND"
        : soloMode === "color-hunt"
          ? "CLEAR \x01 BLOCKS"
          : "CLEAR ALL BLOCKS";
    const scoreRemaining = this.run == null
      ? levelCfg.scoreTarget
      : this.isClassicSoloMode(soloMode)
        ? (this.run.level >= MAX_LEVEL ? 0 : Math.max(0, levelCfg.scoreTarget - classicScoreProgress))
        : soloGoal?.mode === "clearout"
          ? countOccupiedCells(this.run.board)
          : Math.max(0, (soloGoal?.target ?? 0) - goalProgress);
    const pointsToNextLevel = scoreRemaining;
    const activeObjective = this.currentActiveObjective();
    const activeObjectiveReward =
      this.run == null || activeObjective == null || !this.isClassicSoloMode(soloMode)
        ? null
        : objectiveRewardForLevel(
            this.run.level,
            normalizeLevel20BountyCompletions(this.run.level20BountyCompletions)
          );
    const debugViewModel = this.debugAccess.canUseDebugSettings && this.debugSettings.enabled
      ? buildGameplayDebugSnapshot({
        phase: this.phase,
        run: this.run,
        displayScore: this.displayScore,
        currentChainDepth: this.currentChainDepth,
        warningActive: this.warningActive,
        pendingRotation: this.pendingRotation,
        queuedSwapsCount: this.queuedSwaps.length + (this.deferredResolveSwap != null ? 1 : 0),
        pendingScoreBatchesCount: this.pendingScoreBatches.length,
        deferredCollapseStepsCount: this.deferredCollapseSteps.length,
        pendingPostResolveChecks: this.pendingPostResolveChecks,
        rotationAnim: this.rotationAnimState,
        collapseAnim: this.collapseAnimState,
        swapAnim: this.swapAnimState,
        powerupBusy: this.isPowerupBusy(),
        doomFrozenByPowerupAnim: this.doomFrozenByPowerupAnim || this.isDeadBoardSequenceActive(),
        botDiagnostics: this.botDecisionDiagnostics
      })
      : null;
    const rowRushRoundSummary = this.phase === "RUN_LEVELUP" && soloMode === "row-rush" && this.run?.rowRushRound != null
      ? {
          round: this.run.level >= MAX_LEVEL && this.rowRushPendingGameOver
            ? MAX_LEVEL
            : Math.max(1, this.run.level - 1),
          score: this.run.rowRushRound.lastRoundScore,
          perfectBonusAwarded: this.run.rowRushRound.lastRoundPerfect,
          rotation: this.run.rowRushRound.lastRoundRotation ?? "LEFT"
        }
      : null;
    return {
      phase: this.phase,
      paused: this.paused,
      preSettingsPhase: this.preSettingsPhase,
      run: this.run,
      soloMode,
      soloModeLabel: soloModeLabel(soloMode),
      latestRunSoloMode: this.latestRunSoloMode,
      latestRunSoloModeLabel: soloModeLabel(this.latestRunSoloMode),
      selectedSoloMode: this.selectedSoloMode,
      soloCarouselIndex: this.soloCarouselIndex,
      soloModeContinueAvailability: { ...this.soloModeContinueAvailability },
      soloModeLocks: {
        classic: this.soloModeLockState("classic"),
        "row-rush": this.soloModeLockState("row-rush"),
        clearout: this.soloModeLockState("clearout"),
        "color-hunt": this.soloModeLockState("color-hunt"),
        turbomaxxing: this.soloModeLockState("turbomaxxing"),
      },
      activeObjective: activeObjective == null ? null : { ...activeObjective },
      activeObjectiveLabel: activeObjective == null ? null : objectiveLabel(activeObjective.kind),
      activeObjectiveReward,
      goalLabel,
      goalProgress: this.isClassicSoloMode(soloMode)
        ? activeObjective?.progress ?? 0
        : goalProgress,
      goalTarget: this.isClassicSoloMode(soloMode)
        ? activeObjective?.target ?? null
        : goalTarget,
      goalRemaining: this.isClassicSoloMode(soloMode)
        ? Math.max(0, (activeObjective?.target ?? 0) - (activeObjective?.progress ?? 0))
        : scoreRemaining,
      goalRewardSummary: activeObjectiveReward == null ? null : objectiveRewardSummary(activeObjectiveReward),
      goalTargetRows: soloGoal?.mode === "row-rush" ? [...soloGoal.targetRows] : [],
      goalTargetColors: soloGoal?.mode === "color-hunt" ? [...soloGoal.targetColors] : [],
      levelTarget: this.isClassicSoloMode(soloMode)
        ? levelCfg.scoreTarget
        : goalTarget ?? levelCfg.scoreTarget,
      pointsToNextLevel,
      hasContinue: this.hasContinue,
      warningActive: this.warningActive,
      pendingRotation: this.pendingRotation,
      rowRushRoundSummary,
      message: this.message,
      leaderboard: this.leaderboard,
      leaderboardMode: this.leaderboardMode,
      leaderboardDisplayMode: this.leaderboardDisplayMode(),
      leaderboardLoading: this.leaderboardLoading,
      leaderboardTab: this.leaderboardTab,
      selectedSoloLeaderboardMode: this.selectedSoloLeaderboardMode,
      versusLeaderboard: this.versusLeaderboard,
      versusRankContext: this.versusRankContext,
      versusLeaderboardLoading: this.versusLeaderboardLoading,
      authState: this.authState,
      musicEnabled: this.audio.isEnabled(),
      sfxEnabled: this.audio.isSfxEnabled(),
      hapticsEnabled: hapticManager.isEnabled(),
      musicVolume: this.audio.getMusicVolume(),
      sfxVolume: this.audio.getSfxVolume(),
      performance: clonePerformanceSettings(this.performanceSettings),
      settingsPage: this.settingsPage,
      pauseShowSettings: this.pauseShowSettings,
      canUseDebugSettings: this.debugAccess.canUseDebugSettings,
      debugSettings: { ...this.debugSettings },
      flashAlpha: this.flashAlpha,
      flashColor: this.flashColor,
      shakeMagnitude: this.shakeMagnitude,
      rotationAnim: this.rotationAnimState,
      collapseAnim: this.collapseAnimState,
      swapAnim: this.swapAnimState,
      tutorialState: this.tutorialState,
      lastScorePopup: popup,
      currentChainDepth: this.currentChainDepth,
      comboCount,
      comboWindowRemainingSec,
      comboWindowDurationSec: COMBO_EXTENSION_WINDOW_SEC,
      hypeText: this.hypeText,
      hypeTimer: this.hypeTimer,
      levelUpTimer: this.levelUpTimer,
      levelUpDurationSec: this.newColorIndex != null
        ? STATE_MACHINE_TUNING.levelUpNewColorDurationSec
        : this.isRowRushRun() ? ROW_RUSH_INTERSTITIAL_SEC : STATE_MACHINE_TUNING.levelUpPhaseDurationSec,
      newColorIndex: this.newColorIndex,
      displayScore: this.displayScore,
      dragState: this.dragState,
      hoverCell: this.hoverCell,
      hoveredPowerup: this.hoveredPowerup,
      hintActive: this.hintActive,
      hintCells: this.hintCells,
      gameOverElapsedSec: this.gameOverElapsedSec,
      gameOverStep: this.gameOverStep,
      gameOverStepElapsed: this.gameOverStepElapsed,
      leaderboardExpanded: this.leaderboardExpanded,
      levelUpTriggered: this.levelUpTriggered,
      newLevel: this.newLevel,
      xpBeforeRun: this.xpBeforeRun,
      newlyUnlockedModes: [...this.newlyUnlockedModes],
      doomClockSec: this.run?.doomClockSec ?? 0,
      doomClockMaxSec: this.currentDoomClockMaxSec(),
      doomClockFrozen,
      doomClockTimePopup: doomClockPopup,
      cashPopupAmount,
      cashDenyFlashSec: this.cashDenyFlashSec,
      armedPowerup: this.armedPowerup,
      hintFirstPick: this.hintFirstPick,
      powerupExecuting: this.powerupExecuting,
      powerupExecutionRemainingSec: this.powerupExecution?.remainingSec ?? 0,
      latestRunName: this.latestRunName,
      latestRunScore: this.latestRunScore,
      latestRunLevel: this.latestRunLevel,
      latestRunRank: this.latestRunRank,
      latestRunRankResolved: this.latestRunRankResolved,
      isNewHighScore: this.latestRunRankResolved && this.latestRunRank === 1,
      scoreSubmitFailed: this.scoreSubmitFailed,
      musicVisual,
      debugViewModel,
      authError: this.authError,
      versusLobby: null as VersusLobbyRenderData | null,
      versusCountdown: null as VersusCountdownData | null,
      versusMatch: null as VersusMatchRenderData | null,
      versusOptionsOpen: false,
      versusResult: null as VersusResultData | null,
      versusResultStep: this.versusResultStep,
      versusResultStepElapsed: this.versusResultStepElapsed,
      versusAdHeartRestored: this.versusAdHeartRestored,
      versusRatingAfter: null as number | null,
      rematchLocalWants: false,
      rematchRemoteWants: false,
      rematchRemoteLeft: false,
      // Economy
      coins: this.walletCoins,
      gems: this.walletGems,
      gemBurstPending: this.gemBurstPending,
      gemBurstPackIdx: this.gemBurstPackIdx,
      coinsEarnedThisRun: this.coinsEarnedThisRun,
      equippedCharacter: this.equippedChar,
      shopSkins: this.dailyShopSkins,
      shopViewIndex: this.shopViewIndex,
      ownedSkins: (() => {
        const seen = new Set<string>();
        return getCachedOwnedIds()
          .map((id) => getCharacterById(id))
          .filter((c): c is CharacterDef => {
            if (c == null || seen.has(c.id)) return false;
            seen.add(c.id);
            return true;
          });
      })(),
      profilePage: this.profilePage,
      profileFilter: this.profileFilter,
      profileViewMode: this.profileViewMode,
      profileBrowseIndex: this.profileBrowseIndex,
      profileVinylFlipped: this.profileVinylFlipped,
      profileVinylFlipMs: this.profileVinylFlipMs,
      personalBests: this.personalBests,
      unreadPhoneCount: this.overrideUnreadPhoneCount ?? Math.max(0, this.phoneTotalCount - this.phoneLastSeenCount),
      phoneTab: this.phoneTab,
      phoneMessageId: this.phoneMessageId,
      incomingMessageAlert: this.incomingMessageAlert,
      phoneShakeTimer: this.phoneShakeTimer,
      inboxMessages: [] as Array<import("../social/messageService").InboxMessage>,
      skinBgColor: getSkinBgColor(this.equippedChar.imagePath.replace("/assets/characters/", "").replace(".png", "")),
      onboardingSkin: this.onboardingSkin,
      onboardingState: this.onboardingState,
      onboardingStep: this.onboardingState ? getCurrentStep(this.onboardingState) : null,
      // Social & levels
      friendsOnlineCount: this.friendsOnlineCount,
      username: this.authState.profile?.username ?? null,
      level: this.authState.profile?.level ?? 1,
      prestigeStars: prestigeStarsForLevel(this.authState.profile?.level ?? 1),
      xpProgress: (() => {
        if (!this.hasPersistentProgressAccount()) {
          return 0;
        }
        const xp = this.authState.profile?.totalXp ?? 0;
        const info = levelFromTotalXp(xp);
        return info.xpNeeded > 0 ? info.xpIntoLevel / info.xpNeeded : 0;
      })(),
      xpText: (() => {
        if (!this.hasPersistentProgressAccount()) {
          return "SIGN IN TO EARN XP";
        }
        const xp = this.authState.profile?.totalXp ?? 0;
        const info = levelFromTotalXp(xp);
        return `${info.xpIntoLevel} / ${info.xpNeeded}`;
      })(),
      featureLocks: {
        friends: this.featureLockState("friends"),
        addFriend: this.featureLockState("addFriend"),
        shop: this.featureLockState("shop"),
        lobby: this.featureLockState("lobby"),
        gemShop: this.featureLockState("gemShop"),
        tokenShop: this.featureLockState("tokenShop"),
        customLobbies: this.featureLockState("customLobbies"),
      },
      dailyLoginStatus: null as DailyLoginBonusStatus | null,
      dailyLoginLoading: false,
      dailyLoginClaimPending: false,
      dailyLoginModalOpen: this.dailyLoginModalOpen,
      dailyRewardAnimPhase: this.dailyRewardAnimPhase,
      dailyRewardAnimStartMs: this.dailyRewardAnimStartMs,
      dailyRewardClaimedReward: this.dailyRewardClaimedReward,
      versusError: this.versusError,
      versusBusy: this.versusBusy,
      versusQuickmatchActive: false,
      versusActionPhase: "idle" as const,
      versusActionName: null,
      versusStatusLine: "",
      // Friends (overlaid from main.ts)
      friendsList: [] as Array<{ odId: string; username: string; level: number; online: boolean; equippedSkin: string; isNpc?: boolean }>,
      phoneFriendId: this.phoneFriendId,
      phoneAddFriendMode: this.phoneAddFriendMode,
      phoneEditUsernameMode: this.phoneEditUsernameMode,
      phoneReferMode: this.phoneReferMode,
      pendingRequests: [] as Array<{ username: string; level: number }>,
      sentRequests: [] as Array<{ username: string; level: number }>,
      friendsError: "",
      friendRemoveConfirm: null as { username: string; prompt: string } | null,
      friendRemoveBusy: false,
      friendsPage: 0,
      friendsTotalCount: 0,
      appearOffline: this.appearOffline,
      openLobbies: [] as Array<{ code: string; players: number }>,
      lobbyLastRefreshMs: 0,
      lobbyBrowserError: "",
      lobbyJoinInputOpen: false,
      // OFFL-03: default true; main.ts overrides with networkManager.isOnline() each frame
      isOnline: true,
      // Loading state: main.ts overrides each frame
      loadingAction: null as string | null,
      // IAP gem packs (main.ts overrides each frame)
      iapPacks: [] as IapPackRenderData[],
      iapAvailable: false,
      // IAP purchase state (main.ts overrides each frame)
      iapPurchaseState: { phase: "idle", packIndex: -1, gems: 0, priceString: "", label: "", message: "", messageTimer: 0 } as IapPurchaseState,
      // Radial menu: populated by main.ts each frame from input state
      radialMenu: null,
      // ETH wallet / NFT avatar state (main.ts overrides each frame from walletService)
      walletAddress: null,
      walletVerified: false,
      isBitbeastOwner: false,
      walletSyncStatus: null,
      walletLastSync: null,
      walletConnecting: false,
      nftSyncing: false,
      unlockedAvatarTokenIds: [] as number[],
      // NFT avatar resolver output (main.ts overrides each frame from avatarResolver)
      nftAvatars: [] as AvatarDisplay[],
      nftSyncAge: "Never",
      nftCacheStale: false,
      delegatedVaults: [] as string[],
      // Solana wallet state (main.ts overrides each frame from solanaService)
      solWalletAddress: null as string | null,
      solWalletConnected: false,
      solTokenBalances: [] as { mint: string; symbol: string; balance: number; usdValue: number | null }[],
      solBalanceLoading: false,
      // Token shop state (main.ts overrides each frame from tokenShopService)
      tokenShopItems: [] as Array<{ id: string; displayName: string; usdPrice: number; tokenPrice: number | null; gemAmount: number | null; itemType: string; tokenSymbol: string | null }>,
      tokenPurchaseState: "idle" as string,
      tokenPurchaseError: null as string | null,
      tokenSelectedItemName: null as string | null,
      tokenSelectedAmount: null as string | null,
      hasWhitelistedTokens: false,
      activeTokenSymbol: null as string | null,
      tokenVerifyAttemptCount: 0,
      // Referral system (main.ts overrides each frame)
      referralStats: null as { count: number; max: number } | null,
      // PWA install prompt (main.ts overrides each frame)
      showInstallApp: false,
      pushEnabled: false,
      pushSupported: false,
      staminaCurrent: stamina.current,
      staminaMax: stamina.max,
      staminaNextRecoverySec,
      staminaPremiumActive: stamina.premiumActive,
      staminaPremiumSource: stamina.premiumSource as PremiumSource | null,
      staminaPlayable: stamina.premiumActive || stamina.current > 0,
    };
  }

  // -- Economy helpers ---------------------------------------------------

  setDailyRewardClaimed(coins: number, gems: number): void {
    this.dailyRewardClaimedReward = { coins, gems };
  }

  setWalletBalance(coins: number, gems: number): void {
    this.walletCoins = coins;
    this.walletGems = gems;
  }

  /** Show a transient message overlay (e.g. purchase feedback). */
  showMessage(text: string, durationSec = 2): void {
    this.setMessage(text, durationSec);
  }

  dispatchUiActionForTesting(action: UiAction): void {
    this.handleAction(action);
  }

  setDailyLoginModalOpen(open: boolean): void {
    // Suppress during onboarding flow — don't show daily reward popup
    if (open && (this.phase === "USERNAME_PICK" || this.phase === "ONBOARDING_CALL")) {
      return;
    }
    this.dailyLoginModalOpen = open;
  }

  setProfileNftAvatarCount(count: number): void {
    this.profileNftAvatarCount = Number.isFinite(count)
      ? Math.max(0, Math.floor(count))
      : 0;
  }

  refreshShopSkins(): void {
    this.dailyShopSkins = getDailyShopSkins(Date.now(), getCachedOwnedIds());
    if (this.dailyShopSkins.length === 0) {
      this.shopViewIndex = 0;
      return;
    }
    this.shopViewIndex = Math.min(this.shopViewIndex, this.dailyShopSkins.length - 1);
  }

  triggerGemBurst(packIdx = -1): void {
    this.gemBurstPending = true;
    this.gemBurstPackIdx = packIdx;
  }

  consumeGemBurst(): void {
    this.gemBurstPending = false;
  }

  /** Called when transitioning to RUN_GAMEOVER. Returns coins earned. */
  calculateRunCoins(): number {
    const coins = Math.floor(this.latestRunScore / 100);
    this.coinsEarnedThisRun = coins;
    return coins;
  }

  setLevelUpTriggered(triggered: boolean, newLevel: number): void {
    this.levelUpTriggered = triggered;
    this.newLevel = newLevel;
  }

  setNewlyUnlockedModes(modes: SoloMode[]): void {
    this.newlyUnlockedModes = modes;
  }

  setIsBitbeastOwner(value: boolean): void {
    this._isBitbeastOwner = value;
  }

  setXpBeforeRun(xpProgress: number): void {
    this.xpBeforeRun = xpProgress;
  }

  toggleLeaderboardExpanded(): void {
    this.leaderboardExpanded = !this.leaderboardExpanded;
  }

  /** Update wallet after async credit completes. */
  onCoinsEarned(newBalance: { coins: number; gems: number }): void {
    this.walletCoins = newBalance.coins;
    this.walletGems = newBalance.gems;
  }

  resetCoinsEarnedThisRun(): void {
    this.coinsEarnedThisRun = 0;
  }

  resetVersusResultStep(): void {
    this.versusResultStep = 0;
    this.versusResultStepElapsed = 0;
    this.versusAdHeartRestored = false;
  }

  setEquippedCharacter(char: { icon: string; color: string; name: string; imagePath: string; imageSmallPath: string }): void {
    this.equippedChar = char;
  }

  setFriendsOnlineCount(count: number): void {
    this.friendsOnlineCount = count;
  }

  setVersusError(error: string): void {
    this.versusError = error;
  }

  setVersusBusy(busy: boolean): void {
    this.versusBusy = busy;
  }

  /** Pre-load a random starter skin so it's visible during USERNAME_PICK phone UI */
  prepareOnboardingSkin(): void {
    if (this.onboardingSkin == null) {
      const randomIndex = Math.floor(Math.random() * CHARACTER_CATALOG.length);
      this.onboardingSkin = CHARACTER_CATALOG[randomIndex] ?? CHARACTER_CATALOG[0] ?? null;
    }
  }

  enterOnboarding(): void {
    this.prepareOnboardingSkin();
    if (this.onboardingSkin != null) {
      this.onOnboardingConfirm?.(this.onboardingSkin.id);
    }
    this.onboardingState = createOnboardingState("world-tour");
    this.phase = "ONBOARDING_CALL";
  }
}
